/**
 * The public API, described in one place.
 *
 * Written by hand rather than generated from the route definitions, and that
 * is deliberate: a generated reference tells a reader the shape of a request
 * but not which of two similar endpoints they want, what happens when they
 * retry, or why a field they expect is absent. Those are the things people are
 * actually looking for.
 *
 * Endpoint paths, parameter names and code samples stay in English in every
 * language. They are what gets typed.
 */

export type Lang = 'en' | 'bg' | 'de' | 'fr' | 'ru' | 'uk' | 'it' | 'es';

export interface Param {
  name: string;
  type: string;
  required: boolean;
  /** Translated description, keyed by language. */
  description: Record<Lang, string>;
}

export interface Endpoint {
  id: string;
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  group: string;
  title: Record<Lang, string>;
  summary: Record<Lang, string>;
  /** The thing that is not obvious from the signature. */
  note?: Record<Lang, string>;
  params?: Param[];
  request?: string;
  response: string;
  /**
   * The error codes this endpoint can return, as keys into ERROR_CODES.
   *
   * A list of codes rather than a list of descriptions, because a code means
   * the same thing wherever it appears and describing it once is the only way
   * the eight translations can be kept in step. What is endpoint-specific is
   * *which* codes can arrive, and that is what this list carries.
   *
   * Codes every endpoint can return - unauthorized, rate_limited,
   * internal_error - are omitted here and covered once in the errors table at
   * the foot of the page.
   */
  errors?: string[];
}

const t = (
  en: string,
  bg: string,
  de: string,
  fr: string,
  ru: string,
  uk: string,
  it: string,
  es: string,
): Record<Lang, string> => ({ en, bg, de, fr, ru, uk, it, es });

// ---------------------------------------------------------------------------

export const INTRO: Record<Lang, { heading: string; body: string[] }> = {
  en: {
    heading: 'WyoCSPay API',
    body: [
      'A REST API over HTTPS. Requests and responses are JSON. Amounts are always integers in the currency’s smallest unit - 1000 is $10.00, and never a decimal.',
      'Authenticate with your secret key as a bearer token. Keys are shown once when issued; if you lose one, revoke it and issue another.',
      'Every write accepts an Idempotency-Key header. Send the same key with the same body and you get the same result rather than a second charge - use it on anything you might retry.',
    ],
  },
  bg: {
    heading: 'WyoCSPay API',
    body: [
      'REST API през HTTPS. Заявките и отговорите са JSON. Сумите винаги са цели числа в най-малката единица на валутата - 1000 е $10.00, никога с десетична запетая.',
      'Удостоверявате се с тайния си ключ като bearer token. Ключът се показва само веднъж при издаване; ако го загубите, отнемете го и издайте нов.',
      'Всяка заявка за запис приема заглавие Idempotency-Key. Изпратите ли същия ключ със същото тяло, получавате същия резултат вместо второ плащане - ползвайте го навсякъде, където може да опитате пак.',
    ],
  },
  de: {
    heading: 'WyoCSPay API',
    body: [
      'Eine REST-API über HTTPS. Anfragen und Antworten sind JSON. Beträge sind immer ganze Zahlen in der kleinsten Einheit der Währung - 1000 sind 10,00 $, niemals eine Dezimalzahl.',
      'Authentifizierung mit Ihrem geheimen Schlüssel als Bearer-Token. Schlüssel werden nur einmal angezeigt; geht einer verloren, widerrufen Sie ihn und stellen einen neuen aus.',
      'Jeder schreibende Aufruf akzeptiert einen Idempotency-Key-Header. Derselbe Schlüssel mit demselben Body liefert dasselbe Ergebnis statt einer zweiten Belastung.',
    ],
  },
  fr: {
    heading: 'API WyoCSPay',
    body: [
      'Une API REST sur HTTPS. Requêtes et réponses en JSON. Les montants sont toujours des entiers dans la plus petite unité de la devise - 1000 vaut 10,00 $, jamais un décimal.',
      "Authentifiez-vous avec votre clé secrète en bearer token. Une clé n'est affichée qu'une fois ; si vous la perdez, révoquez-la et créez-en une autre.",
      "Chaque écriture accepte un en-tête Idempotency-Key. La même clé avec le même corps renvoie le même résultat au lieu d'un second débit.",
    ],
  },
  ru: {
    heading: 'WyoCSPay API',
    body: [
      'REST API поверх HTTPS. Запросы и ответы в JSON. Суммы всегда целые числа в наименьшей единице валюты - 1000 это $10.00, никогда не дробь.',
      'Аутентификация секретным ключом в качестве bearer-токена. Ключ показывается один раз при выдаче; при утере отзовите его и выпустите новый.',
      'Каждый записывающий запрос принимает заголовок Idempotency-Key. Тот же ключ с тем же телом вернёт тот же результат вместо второго списания.',
    ],
  },
  uk: {
    heading: 'WyoCSPay API',
    body: [
      'REST API поверх HTTPS. Запити та відповіді у JSON. Суми завжди цілі числа у найменшій одиниці валюти - 1000 це $10.00, ніколи не дріб.',
      'Автентифікація секретним ключем як bearer-токеном. Ключ показується один раз; якщо втратили, відкличте його та випустіть новий.',
      'Кожен запис приймає заголовок Idempotency-Key. Той самий ключ із тим самим тілом поверне той самий результат замість другого списання.',
    ],
  },
  it: {
    heading: 'API WyoCSPay',
    body: [
      "Un'API REST su HTTPS. Richieste e risposte in JSON. Gli importi sono sempre interi nell'unità più piccola della valuta - 1000 è $10.00, mai un decimale.",
      "Autenticati con la tua chiave segreta come bearer token. Una chiave viene mostrata una sola volta; se la perdi, revocala ed emettine un'altra.",
      "Ogni scrittura accetta l'header Idempotency-Key. La stessa chiave con lo stesso corpo restituisce lo stesso risultato invece di un secondo addebito.",
    ],
  },
  es: {
    heading: 'API de WyoCSPay',
    body: [
      'Una API REST sobre HTTPS. Peticiones y respuestas en JSON. Los importes son siempre enteros en la unidad más pequeña de la moneda - 1000 son $10.00, nunca un decimal.',
      'Autentíquese con su clave secreta como bearer token. La clave se muestra una sola vez; si la pierde, revóquela y emita otra.',
      'Cada escritura acepta una cabecera Idempotency-Key. La misma clave con el mismo cuerpo devuelve el mismo resultado en lugar de un segundo cargo.',
    ],
  },
};

export const GROUPS: Record<string, Record<Lang, string>> = {
  payments: t('Payments', 'Плащания', 'Zahlungen', 'Paiements', 'Платежи', 'Платежі', 'Pagamenti', 'Pagos'),
  links: t('Payment links', 'Линкове за плащане', 'Zahlungslinks', 'Liens de paiement', 'Ссылки на оплату', 'Посилання на оплату', 'Link di pagamento', 'Enlaces de pago'),
  refunds: t('Refunds', 'Рефунди', 'Rückerstattungen', 'Remboursements', 'Возвраты', 'Повернення', 'Rimborsi', 'Reembolsos'),
  disputes: t('Disputes', 'Спорове', 'Zahlungsstreitfälle', 'Litiges', 'Спорные операции', 'Спірні операції', 'Contestazioni', 'Disputas'),
  balance: t('Balance and payouts', 'Баланс и изплащания', 'Guthaben und Auszahlungen', 'Solde et virements', 'Баланс и выплаты', 'Баланс і виплати', 'Saldo e bonifici', 'Saldo y transferencias'),
  customers: t('Customers', 'Клиенти', 'Kunden', 'Clients', 'Клиенты', 'Клієнти', 'Clienti', 'Clientes'),
  webhooks: t('Webhooks', 'Webhooks', 'Webhooks', 'Webhooks', 'Webhooks', 'Webhooks', 'Webhooks', 'Webhooks'),
  capabilities: t('Capabilities', 'Възможности', 'Funktionsfreigaben', 'Capacités', 'Возможности', 'Можливості', 'Capacità', 'Capacidades'),
  support: t('Support tickets', 'Тикети за поддръжка', 'Support-Tickets', 'Tickets de support', 'Тикеты поддержки', 'Тикети підтримки', 'Ticket di assistenza', 'Tickets de soporte'),
  branding: t('Branding', 'Брандиране', 'Branding', 'Image de marque', 'Брендинг', 'Брендинг', 'Brand', 'Marca'),
  account: t('Account', 'Профил', 'Konto', 'Compte', 'Аккаунт', 'Обліковий запис', 'Account', 'Cuenta'),
};

/**
 * What each error code means, described once.
 *
 * An endpoint lists the codes it can return; this is where they are explained.
 * Keeping the description in one place is what makes it possible for eight
 * translations of forty-odd endpoints to say the same thing about the same
 * code, and it means a merchant who has met `not_onboarded` on one endpoint
 * already knows what it means on the next.
 *
 * The four codes every endpoint can return - `unauthorized`, `forbidden`,
 * `rate_limited` and `internal_error` - are in the table at the foot of the
 * page rather than repeated on each entry.
 */
export const ERROR_CODES: Record<string, { status: number; text: Record<Lang, string> }> = {
  invalid_request: {
    status: 400,
    text: t(
      'A field is missing or the wrong shape. The response names the field and what was wrong with it.',
      'Липсва поле или е с грешен вид. Отговорът посочва полето и какво не е наред с него.',
      'Ein Feld fehlt oder hat die falsche Form. Die Antwort nennt das Feld und den Fehler.',
      "Un champ manque ou n'a pas la bonne forme. La réponse nomme le champ et le problème.",
      'Поле отсутствует или имеет неверный вид. Ответ называет поле и в чём ошибка.',
      'Поле відсутнє або має невірний вигляд. Відповідь називає поле та помилку.',
      'Un campo manca o ha la forma sbagliata. La risposta indica il campo e il problema.',
      'Falta un campo o tiene la forma equivocada. La respuesta indica el campo y el problema.',
    ),
  },
  not_found: {
    status: 404,
    text: t(
      'No such object. An object belonging to another merchant produces this same response, byte for byte, so this cannot be used to find out which identifiers exist.',
      'Няма такъв обект. Обект на друг търговец връща същия отговор, буква по буква, така че това не може да се използва за откриване кои идентификатори съществуват.',
      'Kein solches Objekt. Ein Objekt eines anderen Händlers liefert exakt dieselbe Antwort, sodass sich damit nicht herausfinden lässt, welche Kennungen existieren.',
      "Objet inexistant. Un objet appartenant à un autre marchand donne exactement la même réponse, ce qui interdit de découvrir quels identifiants existent.",
      'Такого объекта нет. Объект другого продавца даёт точно такой же ответ, поэтому так нельзя выяснить, какие идентификаторы существуют.',
      'Такого об’єкта немає. Об’єкт іншого продавця дає точно таку саму відповідь, тож так не можна дізнатися, які ідентифікатори існують.',
      "Oggetto inesistente. Un oggetto di un altro esercente produce esattamente la stessa risposta, quindi non si può scoprire quali identificatori esistono.",
      'No existe el objeto. Un objeto de otro comercio produce exactamente la misma respuesta, así que no sirve para descubrir qué identificadores existen.',
    ),
  },
  invalid_cursor: {
    status: 400,
    text: t(
      'starting_after does not name an object you own. Pass the id of the last item from the previous page.',
      'starting_after не сочи ваш обект. Подайте id на последния елемент от предишната страница.',
      'starting_after benennt kein Objekt, das Ihnen gehört. Übergeben Sie die id des letzten Eintrags der vorigen Seite.',
      "starting_after ne désigne pas un objet vous appartenant. Passez l'id du dernier élément de la page précédente.",
      'starting_after не указывает на ваш объект. Передайте id последнего элемента предыдущей страницы.',
      'starting_after не вказує на ваш об’єкт. Передайте id останнього елемента попередньої сторінки.',
      "starting_after non indica un tuo oggetto. Passa l'id dell'ultimo elemento della pagina precedente.",
      'starting_after no designa un objeto suyo. Pase el id del último elemento de la página anterior.',
    ),
  },
  invalid_amount: {
    status: 400,
    text: t(
      'The amount is not a positive whole number of minor units, or it is too small for the currency.',
      'Сумата не е цяло положително число в най-малката единица или е твърде малка за валутата.',
      'Der Betrag ist keine positive ganze Zahl in der kleinsten Einheit oder für die Währung zu klein.',
      "Le montant n'est pas un entier positif en unités mineures, ou est trop petit pour la devise.",
      'Сумма не является целым положительным числом в минимальных единицах либо слишком мала для валюты.',
      'Сума не є цілим додатним числом у мінімальних одиницях або занадто мала для валюти.',
      "L'importo non è un intero positivo in unità minori, o è troppo piccolo per la valuta.",
      'El importe no es un entero positivo en unidades menores, o es demasiado pequeño para la moneda.',
    ),
  },
  idempotency_conflict: {
    status: 409,
    text: t(
      'This Idempotency-Key has already been used, with a different body or on a different endpoint. Use a fresh key for each distinct request.',
      'Този Idempotency-Key вече е използван - с различно тяло или на друг адрес. Ползвайте нов ключ за всяка отделна заявка.',
      'Dieser Idempotency-Key wurde bereits verwendet, mit anderem Body oder an einem anderen Endpunkt. Nutzen Sie pro Anfrage einen neuen Schlüssel.',
      "Cette clé d'idempotence a déjà servi, avec un corps différent ou sur un autre endpoint. Utilisez une clé neuve par requête distincte.",
      'Этот Idempotency-Key уже использован - с другим телом или на другом эндпоинте. Используйте новый ключ для каждого отдельного запроса.',
      'Цей Idempotency-Key вже використано - з іншим тілом або на іншому ендпоінті. Використовуйте новий ключ для кожного окремого запиту.',
      "Questa Idempotency-Key è già stata usata, con un corpo diverso o su un altro endpoint. Usa una chiave nuova per ogni richiesta distinta.",
      'Esta clave de idempotencia ya se usó, con otro cuerpo o en otro endpoint. Use una clave nueva por cada petición distinta.',
    ),
  },
  idempotency_in_progress: {
    status: 409,
    text: t(
      'An earlier request with this key has not finished. Wait a moment and retry with the same key - do not start a new one.',
      'По-ранна заявка с този ключ още не е приключила. Изчакайте малко и опитайте пак със същия ключ - не започвайте нова.',
      'Eine frühere Anfrage mit diesem Schlüssel läuft noch. Kurz warten und mit demselben Schlüssel erneut senden.',
      "Une requête antérieure avec cette clé n'est pas terminée. Patientez et réessayez avec la même clé.",
      'Более ранний запрос с этим ключом ещё не завершён. Подождите и повторите с тем же ключом.',
      'Раніший запит із цим ключем ще не завершено. Зачекайте та повторіть із тим самим ключем.',
      'Una richiesta precedente con questa chiave non è ancora terminata. Attendi e riprova con la stessa chiave.',
      'Una petición anterior con esta clave no ha terminado. Espere y reintente con la misma clave.',
    ),
  },
  idempotency_key_required: {
    status: 400,
    text: t(
      'This endpoint moves money and refuses to run without an Idempotency-Key, because a retried request would otherwise be a second transfer.',
      'Този адрес движи пари и отказва без Idempotency-Key, защото иначе повторна заявка би била втори превод.',
      'Dieser Endpunkt bewegt Geld und verweigert ohne Idempotency-Key, weil ein Wiederholungsversuch sonst eine zweite Überweisung wäre.',
      "Cet endpoint déplace de l'argent et refuse sans Idempotency-Key : sinon une nouvelle tentative serait un second virement.",
      'Этот эндпоинт двигает деньги и без Idempotency-Key откажет: иначе повтор запроса стал бы вторым переводом.',
      'Цей ендпоінт рухає гроші і без Idempotency-Key відмовить: інакше повтор запиту став би другим переказом.',
      "Questo endpoint muove denaro e rifiuta senza Idempotency-Key: altrimenti un nuovo tentativo sarebbe un secondo bonifico.",
      'Este endpoint mueve dinero y rechaza sin Idempotency-Key: de lo contrario un reintento sería una segunda transferencia.',
    ),
  },
  not_onboarded: {
    status: 422,
    text: t(
      'Stripe has not finished verifying this account, so there is no connected account to read from yet. Finish onboarding in the panel.',
      'Stripe още не е завършил верификацията на профила, така че още няма свързан акаунт за четене. Довършете регистрацията в панела.',
      'Stripe hat dieses Konto noch nicht verifiziert, es gibt also noch kein verbundenes Konto zum Auslesen. Onboarding im Panel abschließen.',
      "Stripe n'a pas terminé la vérification du compte : il n'y a pas encore de compte connecté à lire. Terminez l'inscription dans le panneau.",
      'Stripe ещё не завершил проверку аккаунта, читать пока не из чего. Завершите регистрацию в панели.',
      'Stripe ще не завершив перевірку облікового запису, читати поки нема з чого. Завершіть реєстрацію в панелі.',
      "Stripe non ha finito di verificare l'account, quindi non c'è ancora un account connesso da leggere. Completa l'onboarding nel pannello.",
      'Stripe no ha terminado de verificar la cuenta, así que aún no hay cuenta conectada que leer. Complete el alta en el panel.',
    ),
  },
  not_settled: {
    status: 422,
    text: t(
      'This payment was never charged, so there is nothing to refund. Only a payment that reached SUCCEEDED has money to return.',
      'Това плащане никога не е било таксувано, така че няма какво да се върне. Само плащане, стигнало до SUCCEEDED, има пари за връщане.',
      'Diese Zahlung wurde nie belastet, es gibt also nichts zu erstatten. Nur eine Zahlung im Status SUCCEEDED hat Geld zurückzugeben.',
      "Ce paiement n'a jamais été débité : il n'y a rien à rembourser. Seul un paiement passé à SUCCEEDED a de l'argent à rendre.",
      'Этот платёж никогда не списывался, возвращать нечего. Деньги есть только у платежа, дошедшего до SUCCEEDED.',
      'Цей платіж ніколи не списувався, повертати нічого. Гроші є лише у платежу, що дійшов до SUCCEEDED.',
      'Questo pagamento non è mai stato addebitato, quindi non c’è nulla da rimborsare.',
      'Este pago nunca se cobró, así que no hay nada que reembolsar.',
    ),
  },
  not_refundable: {
    status: 422,
    text: t(
      'The payment is in a state that cannot be refunded. The message names the state.',
      'Плащането е в състояние, което не може да се рефундира. Съобщението посочва състоянието.',
      'Die Zahlung ist in einem Status, der nicht erstattet werden kann. Die Meldung nennt den Status.',
      "Le paiement est dans un état non remboursable. Le message indique l'état.",
      'Платёж в состоянии, не допускающем возврат. Сообщение называет состояние.',
      'Платіж у стані, що не допускає повернення. Повідомлення називає стан.',
      'Il pagamento è in uno stato non rimborsabile. Il messaggio indica lo stato.',
      'El pago está en un estado no reembolsable. El mensaje indica el estado.',
    ),
  },
  fully_refunded: {
    status: 422,
    text: t(
      'Everything on this payment has already been returned.',
      'Всичко по това плащане вече е върнато.',
      'Diese Zahlung wurde bereits vollständig erstattet.',
      'Ce paiement a déjà été intégralement remboursé.',
      'Этот платёж уже возвращён полностью.',
      'Цей платіж уже повернуто повністю.',
      'Questo pagamento è già stato interamente rimborsato.',
      'Este pago ya se ha devuelto por completo.',
    ),
  },
  amount_too_large: {
    status: 400,
    text: t(
      'More was asked for than is still refundable. The message says how much remains.',
      'Поискано е повече от оставащото за връщане. Съобщението казва колко остава.',
      'Es wurde mehr verlangt, als noch erstattbar ist. Die Meldung nennt den Restbetrag.',
      'Le montant demandé dépasse le remboursable restant. Le message indique le reste.',
      'Запрошено больше, чем осталось к возврату. Сообщение указывает остаток.',
      'Запитано більше, ніж лишилося до повернення. Повідомлення вказує залишок.',
      'È stato chiesto più di quanto sia ancora rimborsabile. Il messaggio indica il residuo.',
      'Se pidió más de lo que queda por reembolsar. El mensaje indica el resto.',
    ),
  },
  already_paid: {
    status: 409,
    text: t(
      'The payment has already been settled, so its link cannot be renewed.',
      'Плащането вече е уредено, така че линкът му не може да бъде подновен.',
      'Die Zahlung ist bereits abgeschlossen, ihr Link kann nicht erneuert werden.',
      'Le paiement est déjà réglé : son lien ne peut être prolongé.',
      'Платёж уже завершён, продлевать его ссылку нельзя.',
      'Платіж уже завершено, продовжувати його посилання не можна.',
      'Il pagamento è già stato saldato, quindi il suo link non può essere rinnovato.',
      'El pago ya está liquidado, así que su enlace no puede renovarse.',
    ),
  },
  invalid_url: {
    status: 400,
    text: t(
      'The endpoint URL will not parse.',
      'URL адресът не може да бъде разчетен.',
      'Die Endpunkt-URL lässt sich nicht parsen.',
      "L'URL de l'endpoint est illisible.",
      'URL эндпоинта не разбирается.',
      'URL ендпоінта не розбирається.',
      "L'URL dell'endpoint non è interpretabile.",
      'La URL del endpoint no se puede interpretar.',
    ),
  },
  insecure_url: {
    status: 400,
    text: t(
      'Webhook endpoints must use HTTPS. A signed payload over plaintext HTTP is still readable by everyone on the path - the signature only proves nobody changed it.',
      'Webhook адресите трябва да са HTTPS. Подписано тяло по HTTP пак се чете от всички по пътя - подписът доказва само че не е променяно.',
      'Webhook-Endpunkte müssen HTTPS nutzen. Eine signierte Nutzlast über HTTP ist weiterhin für alle auf dem Weg lesbar; die Signatur belegt nur Unverändertheit.',
      "Les endpoints webhook doivent être en HTTPS. Un contenu signé en HTTP reste lisible par tous sur le chemin ; la signature prouve seulement qu'il n'a pas été modifié.",
      'Webhook-адреса должны быть HTTPS. Подписанное тело по HTTP по-прежнему читаемо всеми на пути; подпись доказывает лишь неизменность.',
      'Webhook-адреси мають бути HTTPS. Підписане тіло по HTTP усе одно читається всіма на шляху; підпис доводить лише незмінність.',
      "Gli endpoint webhook devono usare HTTPS. Un payload firmato su HTTP resta leggibile da chiunque sul percorso.",
      'Los endpoints de webhook deben usar HTTPS. Un contenido firmado por HTTP sigue siendo legible por todos en el camino.',
    ),
  },
  no_changes: {
    status: 400,
    text: t(
      'The request asked for nothing to change.',
      'Заявката не иска никаква промяна.',
      'Die Anfrage enthält keine Änderung.',
      "La requête ne demande aucun changement.",
      'Запрос не содержит изменений.',
      'Запит не містить змін.',
      'La richiesta non chiede alcuna modifica.',
      'La petición no solicita ningún cambio.',
    ),
  },
  card_required: {
    status: 400,
    text: t(
      'Card payments cannot be switched off. Everything else is optional; a payment page with no card option is not a payment page.',
      'Плащанията с карта не могат да се изключат. Всичко останало е по избор; страница за плащане без карта не е страница за плащане.',
      'Kartenzahlungen lassen sich nicht abschalten. Alles andere ist optional; eine Zahlungsseite ohne Karte ist keine Zahlungsseite.',
      "Les paiements par carte ne peuvent être désactivés. Tout le reste est optionnel ; une page de paiement sans carte n'en est pas une.",
      'Оплату картой отключить нельзя. Всё остальное необязательно; страница оплаты без карты - не страница оплаты.',
      'Оплату карткою вимкнути не можна. Усе інше необов’язкове; сторінка оплати без картки - не сторінка оплати.',
      'I pagamenti con carta non si possono disattivare. Tutto il resto è opzionale.',
      'Los pagos con tarjeta no se pueden desactivar. Todo lo demás es opcional.',
    ),
  },
  customer_exists: {
    status: 409,
    text: t(
      'You already have a customer with this email address. The existing id is in the response details.',
      'Вече имате клиент с този имейл. Съществуващият id е в детайлите на отговора.',
      'Sie haben bereits einen Kunden mit dieser E-Mail. Die vorhandene id steht in den Antwortdetails.',
      "Vous avez déjà un client avec cette adresse. L'id existant figure dans les détails de la réponse.",
      'У вас уже есть клиент с этим адресом. Существующий id указан в деталях ответа.',
      'У вас уже є клієнт із цією адресою. Наявний id указано в деталях відповіді.',
      "Hai già un cliente con questa email. L'id esistente è nei dettagli della risposta.",
      'Ya tiene un cliente con esa dirección. El id existente está en los detalles de la respuesta.',
    ),
  },
  invalid_customer: {
    status: 400,
    text: t(
      'A customer needs an email address or a name. A record with neither identifies nobody.',
      'Клиентът се нуждае от имейл или име. Запис без нито едно от двете не идентифицира никого.',
      'Ein Kunde braucht eine E-Mail oder einen Namen. Ein Datensatz ohne beides identifiziert niemanden.',
      "Un client doit avoir une adresse ou un nom. Un enregistrement sans l'un ni l'autre n'identifie personne.",
      'Клиенту нужен адрес или имя. Запись без того и другого никого не идентифицирует.',
      'Клієнту потрібна адреса або ім’я. Запис без обох нікого не ідентифікує.',
      'Un cliente ha bisogno di un’email o di un nome. Un record senza nessuno dei due non identifica nessuno.',
      'Un cliente necesita un correo o un nombre. Un registro sin ninguno no identifica a nadie.',
    ),
  },
  payouts_disabled: {
    status: 422,
    text: t(
      'Stripe has not enabled payouts on this account. Check requirements_due on GET /api/v1/account.',
      'Stripe не е разрешил изплащания по този профил. Проверете requirements_due в GET /api/v1/account.',
      'Stripe hat Auszahlungen für dieses Konto nicht freigegeben. Prüfen Sie requirements_due unter GET /api/v1/account.',
      "Stripe n'a pas activé les virements sur ce compte. Vérifiez requirements_due sur GET /api/v1/account.",
      'Stripe не разрешил выплаты по этому аккаунту. Проверьте requirements_due в GET /api/v1/account.',
      'Stripe не дозволив виплати для цього облікового запису. Перевірте requirements_due у GET /api/v1/account.',
      'Stripe non ha abilitato i bonifici su questo account. Controlla requirements_due su GET /api/v1/account.',
      'Stripe no ha habilitado las transferencias en esta cuenta. Revise requirements_due en GET /api/v1/account.',
    ),
  },
  capability_required: {
    status: 403,
    text: t(
      'This account does not hold the capability the endpoint needs. GET /api/v1/features says which capabilities you have, which are locked, and why.',
      'Профилът няма нужната за адреса възможност. GET /api/v1/features казва какво имате, какво е заключено и защо.',
      'Diesem Konto fehlt die nötige Funktionsfreigabe. GET /api/v1/features nennt Ihre Freigaben, die Sperren und den Grund.',
      "Ce compte ne détient pas la capacité requise. GET /api/v1/features indique ce que vous avez, ce qui est verrouillé et pourquoi.",
      'У аккаунта нет нужной возможности. GET /api/v1/features покажет, что есть, что закрыто и почему.',
      'Обліковий запис не має потрібної можливості. GET /api/v1/features покаже, що є, що закрито і чому.',
      "L'account non ha la capacità richiesta. GET /api/v1/features indica cosa hai, cosa è bloccato e perché.",
      'La cuenta no tiene la capacidad requerida. GET /api/v1/features indica qué tiene, qué está bloqueado y por qué.',
    ),
  },
  ceiling_exceeded: {
    status: 403,
    text: t(
      'The amount is above the standard per-payment ceiling of 500000 minor units. Request the high_value_payments capability to raise it; the same limit applies in the panel.',
      'Сумата надхвърля стандартния таван от 500000 в най-малката единица. Поискайте възможността high_value_payments; същият лимит важи и в панела.',
      'Der Betrag liegt über der Standardgrenze von 500000 kleinsten Einheiten. Beantragen Sie high_value_payments; dieselbe Grenze gilt im Panel.',
      "Le montant dépasse le plafond standard de 500000 unités mineures. Demandez la capacité high_value_payments ; la même limite s'applique dans le panneau.",
      'Сумма выше стандартного потолка в 500000 минимальных единиц. Запросите возможность high_value_payments; тот же лимит действует в панели.',
      'Сума вища за стандартну стелю 500000 мінімальних одиниць. Запитайте можливість high_value_payments; той самий ліміт діє в панелі.',
      'L’importo supera il tetto standard di 500000 unità minori. Richiedi la capacità high_value_payments; lo stesso limite vale nel pannello.',
      'El importe supera el techo estándar de 500000 unidades menores. Solicite la capacidad high_value_payments; el mismo límite rige en el panel.',
    ),
  },
  already_granted: {
    status: 409,
    text: t(
      'You already hold this capability, so there is nothing to request.',
      'Вече имате тази възможност, така че няма какво да се иска.',
      'Sie besitzen diese Freigabe bereits.',
      'Vous détenez déjà cette capacité.',
      'Эта возможность у вас уже есть.',
      'Ця можливість у вас уже є.',
      'Hai già questa capacità.',
      'Ya tiene esta capacidad.',
    ),
  },
  request_open: {
    status: 409,
    text: t(
      'You already have an open request for this capability. Reply to it rather than starting another.',
      'Вече имате отворена заявка за тази възможност. Отговорете на нея, вместо да пускате нова.',
      'Für diese Freigabe läuft bereits eine Anfrage. Antworten Sie darauf, statt eine neue zu stellen.',
      "Une demande est déjà ouverte pour cette capacité. Répondez-y plutôt que d'en créer une autre.",
      'По этой возможности уже есть открытая заявка. Ответьте в ней, а не создавайте новую.',
      'За цією можливістю вже є відкрита заявка. Відповідайте в ній, а не створюйте нову.',
      'Hai già una richiesta aperta per questa capacità.',
      'Ya tiene una solicitud abierta para esta capacidad.',
    ),
  },
  request_closed: {
    status: 409,
    text: t(
      'The request has already been decided; a reply would go nowhere.',
      'Заявката вече е решена; отговор няма къде да отиде.',
      'Über die Anfrage wurde bereits entschieden.',
      'La demande a déjà été tranchée.',
      'По заявке уже принято решение.',
      'За заявкою вже ухвалено рішення.',
      'La richiesta è già stata decisa.',
      'La solicitud ya se ha resuelto.',
    ),
  },
  not_requestable: {
    status: 400,
    text: t(
      'That capability is not requested - it unlocks on Stripe verification, or it is on for everybody already.',
      'Тази възможност не се иска - отключва се при верификация в Stripe или вече е включена за всички.',
      'Diese Freigabe wird nicht beantragt - sie entsteht mit der Stripe-Verifizierung oder gilt bereits für alle.',
      "Cette capacité ne se demande pas : elle se débloque à la vérification Stripe, ou est déjà active pour tous.",
      'Эту возможность не запрашивают: она открывается при верификации Stripe или уже включена для всех.',
      'Цю можливість не запитують: вона відкривається при верифікації Stripe або вже увімкнена для всіх.',
      'Quella capacità non si richiede: si sblocca con la verifica Stripe o è già attiva per tutti.',
      'Esa capacidad no se solicita: se desbloquea con la verificación de Stripe o ya está activa para todos.',
    ),
  },
  insufficient_detail: {
    status: 400,
    text: t(
      'The justification or the intended use is too short. A sentence or two each is what the reviewer needs to decide.',
      'Обосновката или предназначението са твърде кратки. По изречение-две за всяко е нужно на рецензента.',
      'Begründung oder Verwendungszweck sind zu kurz. Ein bis zwei Sätze je Feld genügen der prüfenden Person.',
      "La justification ou l'usage prévu est trop bref. Une ou deux phrases chacune suffisent au relecteur.",
      'Обоснование или назначение слишком краткие. Проверяющему хватит одного-двух предложений на каждое.',
      'Обґрунтування або призначення надто короткі. Рецензенту вистачить одного-двох речень на кожне.',
      'La motivazione o l’uso previsto sono troppo brevi. Una o due frasi ciascuno bastano a chi decide.',
      'La justificación o el uso previsto son demasiado breves. Una o dos frases de cada uno bastan.',
    ),
  },
  ticket_closed: {
    status: 400,
    text: t(
      'The ticket is closed and takes no more replies. Open a new one and quote this number.',
      'Тикетът е затворен и не приема повече отговори. Отворете нов и цитирайте този номер.',
      'Das Ticket ist geschlossen und nimmt keine Antworten mehr an. Eröffnen Sie ein neues und nennen Sie diese Nummer.',
      "Le ticket est clos et n'accepte plus de réponses. Ouvrez-en un nouveau en citant ce numéro.",
      'Тикет закрыт и ответов больше не принимает. Откройте новый и укажите этот номер.',
      'Тикет закрито і відповідей більше не приймає. Відкрийте новий і вкажіть цей номер.',
      'Il ticket è chiuso e non accetta altre risposte. Aprine uno nuovo citando questo numero.',
      'El ticket está cerrado y no admite más respuestas. Abra uno nuevo citando este número.',
    ),
  },
  unsupported_attachment: {
    status: 400,
    text: t(
      'Only PNG, JPEG, GIF, WebP and PDF can be attached, and the file is judged by its own leading bytes rather than by its name.',
      'Могат да се прикачват само PNG, JPEG, GIF, WebP и PDF, като файлът се преценява по началните си байтове, а не по името.',
      'Anhänge dürfen nur PNG, JPEG, GIF, WebP oder PDF sein; entschieden wird nach den Anfangsbytes, nicht nach dem Namen.',
      "Seuls PNG, JPEG, GIF, WebP et PDF sont acceptés, et le fichier est jugé sur ses premiers octets, pas sur son nom.",
      'Прикреплять можно только PNG, JPEG, GIF, WebP и PDF, причём тип определяется по первым байтам, а не по имени.',
      'Прикріпляти можна лише PNG, JPEG, GIF, WebP і PDF, а тип визначається за першими байтами, не за іменем.',
      'Si possono allegare solo PNG, JPEG, GIF, WebP e PDF, e il file è giudicato dai suoi primi byte.',
      'Solo se pueden adjuntar PNG, JPEG, GIF, WebP y PDF, y el archivo se juzga por sus bytes iniciales.',
    ),
  },
  attachment_too_large: {
    status: 400,
    text: t(
      'An attachment is limited to 5 MB.',
      'Прикаченият файл е ограничен до 5 MB.',
      'Ein Anhang ist auf 5 MB begrenzt.',
      'Une pièce jointe est limitée à 5 Mo.',
      'Вложение ограничено 5 МБ.',
      'Вкладення обмежене 5 МБ.',
      'Un allegato è limitato a 5 MB.',
      'Un adjunto está limitado a 5 MB.',
    ),
  },
  unsupported_logo: {
    status: 400,
    text: t(
      'A logo must be PNG, JPEG or WebP. SVG is refused because it can carry script, and an SVG served from this origin would run as this origin.',
      'Логото трябва да е PNG, JPEG или WebP. SVG се отказва, защото може да носи скрипт и би се изпълнил като наш произход.',
      'Ein Logo muss PNG, JPEG oder WebP sein. SVG wird abgelehnt, weil es Skript enthalten kann und von dieser Herkunft aus ausgeführt würde.',
      "Un logo doit être PNG, JPEG ou WebP. Le SVG est refusé : il peut porter du script, qui s'exécuterait sous cette origine.",
      'Логотип должен быть PNG, JPEG или WebP. SVG отклоняется: он может нести скрипт, который выполнится от нашего источника.',
      'Логотип має бути PNG, JPEG або WebP. SVG відхиляється: він може нести скрипт, який виконається від нашого джерела.',
      'Un logo deve essere PNG, JPEG o WebP. L’SVG è rifiutato perché può contenere script.',
      'Un logotipo debe ser PNG, JPEG o WebP. El SVG se rechaza porque puede llevar script.',
    ),
  },
  logo_too_large: {
    status: 400,
    text: t(
      'A logo is limited to 128 KB. It is shown at about forty pixels tall, so a smaller file loses nothing.',
      'Логото е ограничено до 128 KB. Показва се на около четиридесет пиксела височина, така че по-малък файл не губи нищо.',
      'Ein Logo ist auf 128 KB begrenzt. Es wird etwa vierzig Pixel hoch gezeigt, eine kleinere Datei verliert also nichts.',
      "Un logo est limité à 128 Ko. Il s'affiche à environ quarante pixels de haut : un fichier plus léger ne perd rien.",
      'Логотип ограничен 128 КБ. Он показывается высотой около сорока пикселей, так что меньший файл ничего не теряет.',
      'Логотип обмежений 128 КБ. Він показується заввишки близько сорока пікселів, тож менший файл нічого не втрачає.',
      'Un logo è limitato a 128 KB. Viene mostrato alto circa quaranta pixel.',
      'Un logotipo está limitado a 128 KB. Se muestra a unos cuarenta píxeles de alto.',
    ),
  },
  card_declined: {
    status: 422,
    text: t(
      'The processor declined. The response carries the decline code Stripe gave, which is the part the customer needs.',
      'Процесорът отказа. Отговорът носи кода на отказа от Stripe, който е нужен на клиента.',
      'Der Abwickler hat abgelehnt. Die Antwort enthält den Ablehnungscode von Stripe.',
      'Le processeur a refusé. La réponse contient le code de refus donné par Stripe.',
      'Процессор отклонил. Ответ содержит код отказа от Stripe.',
      'Процесор відхилив. Відповідь містить код відмови від Stripe.',
      'Il processore ha rifiutato. La risposta riporta il codice di rifiuto di Stripe.',
      'El procesador rechazó. La respuesta lleva el código de rechazo de Stripe.',
    ),
  },
  processor_error: {
    status: 502,
    text: t(
      'Stripe returned an error or could not be reached. Nothing was charged. Retry with the same Idempotency-Key.',
      'Stripe върна грешка или не беше достъпен. Нищо не е таксувано. Опитайте пак със същия Idempotency-Key.',
      'Stripe hat einen Fehler geliefert oder war nicht erreichbar. Nichts wurde belastet. Mit demselben Idempotency-Key erneut versuchen.',
      "Stripe a renvoyé une erreur ou était injoignable. Rien n'a été débité. Réessayez avec la même clé d'idempotence.",
      'Stripe вернул ошибку или был недоступен. Ничего не списано. Повторите с тем же Idempotency-Key.',
      'Stripe повернув помилку або був недоступний. Нічого не списано. Повторіть із тим самим Idempotency-Key.',
      'Stripe ha restituito un errore o non era raggiungibile. Nessun addebito. Riprova con la stessa Idempotency-Key.',
      'Stripe devolvió un error o no se pudo alcanzar. No se cobró nada. Reintente con la misma clave.',
    ),
  },
  not_configured: {
    status: 503,
    text: t(
      'The platform has no Stripe credentials configured, so this endpoint refuses rather than returning a made-up answer.',
      'Платформата няма конфигурирани Stripe идентификационни данни, затова адресът отказва, вместо да върне измислен отговор.',
      'Der Plattform fehlen Stripe-Zugangsdaten, daher verweigert dieser Endpunkt eine erfundene Antwort.',
      "La plateforme n'a pas d'identifiants Stripe : cet endpoint refuse plutôt que d'inventer une réponse.",
      'У платформы не настроены ключи Stripe, поэтому эндпоинт отказывает, а не выдумывает ответ.',
      'У платформи не налаштовані ключі Stripe, тож ендпоінт відмовляє, а не вигадує відповідь.',
      'La piattaforma non ha credenziali Stripe configurate, quindi rifiuta invece di inventare una risposta.',
      'La plataforma no tiene credenciales de Stripe, así que rechaza en vez de inventar una respuesta.',
    ),
  },
};

export const ENDPOINTS: Endpoint[] = [
  {
    id: 'create-payment',
    errors: ['invalid_request', 'invalid_amount', 'ceiling_exceeded', 'capability_required', 'not_found', 'idempotency_conflict', 'idempotency_in_progress'],
    method: 'POST',
    path: '/api/v1/payments',
    group: 'payments',
    title: t('Create a payment', 'Създаване на плащане', 'Zahlung erstellen', 'Créer un paiement', 'Создать платёж', 'Створити платіж', 'Creare un pagamento', 'Crear un pago'),
    summary: t(
      'Creates a payment and, unless you ask otherwise, a link the customer can open to pay it.',
      'Създава плащане и, освен ако не поискате друго, линк, който клиентът може да отвори, за да плати.',
      'Erstellt eine Zahlung und, sofern nicht anders angefordert, einen Link, den der Kunde öffnen kann.',
      'Crée un paiement et, sauf demande contraire, un lien que le client peut ouvrir pour payer.',
      'Создаёт платёж и, если не указано иное, ссылку, по которой клиент может заплатить.',
      'Створює платіж і, якщо не вказано інше, посилання, за яким клієнт може заплатити.',
      'Crea un pagamento e, salvo diversa richiesta, un link che il cliente può aprire per pagare.',
      'Crea un pago y, salvo que indique lo contrario, un enlace que el cliente puede abrir para pagar.',
    ),
    note: t(
      'The link is valid for 24 hours. The full URL is returned once and only its hash is stored, so it cannot be retrieved later - renew or reissue instead.',
      'Линкът важи 24 часа. Пълният URL се връща веднъж и се пази само хешът му, така че после не може да бъде извлечен - вместо това подновете или издайте нов.',
      'Der Link gilt 24 Stunden. Die vollständige URL wird einmal zurückgegeben und nur ihr Hash gespeichert - erneuern oder neu ausstellen statt abrufen.',
      "Le lien est valable 24 heures. L'URL complète est renvoyée une fois et seul son hachage est conservé - renouvelez ou réémettez au lieu de le récupérer.",
      'Ссылка действует 24 часа. Полный URL возвращается один раз, хранится только его хеш - продлевайте или выпускайте новую.',
      'Посилання дійсне 24 години. Повний URL повертається один раз, зберігається лише його хеш - продовжуйте або випускайте нове.',
      "Il link è valido 24 ore. L'URL completo viene restituito una volta e se ne conserva solo l'hash - rinnova o riemetti.",
      'El enlace es válido 24 horas. La URL completa se devuelve una vez y solo se guarda su hash - renueve o reemita.',
    ),
    params: [
      {
        name: 'amount',
        type: 'integer',
        required: true,
        description: t(
          "In the currency's smallest unit. 4990 is $49.90.",
          'В най-малката единица на валутата. 4990 е $49.90.',
          'In der kleinsten Einheit der Währung. 4990 sind 49,90 $.',
          'Dans la plus petite unité de la devise. 4990 vaut 49,90 $.',
          'В наименьшей единице валюты. 4990 это $49.90.',
          'У найменшій одиниці валюти. 4990 це $49.90.',
          "Nell'unità più piccola della valuta. 4990 è $49.90.",
          'En la unidad más pequeña de la moneda. 4990 son $49.90.',
        ),
      },
      {
        name: 'currency',
        type: 'string',
        required: true,
        description: t(
          'Three-letter ISO code, lowercase. Must be one your account supports.',
          'Трибуквен ISO код, малки букви. Трябва да е валута, която профилът ви поддържа.',
          'Dreibuchstabiger ISO-Code, klein. Muss von Ihrem Konto unterstützt werden.',
          'Code ISO à trois lettres, en minuscules. Doit être pris en charge par votre compte.',
          'Трёхбуквенный код ISO, строчными. Должен поддерживаться вашим аккаунтом.',
          'Трилітерний код ISO, малими. Має підтримуватися вашим обліковим записом.',
          'Codice ISO di tre lettere, minuscolo. Deve essere supportato dal tuo account.',
          'Código ISO de tres letras, en minúsculas. Debe estar admitido por su cuenta.',
        ),
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: t(
          'Shown to the customer on the payment page.',
          'Показва се на клиента на страницата за плащане.',
          'Wird dem Kunden auf der Zahlungsseite angezeigt.',
          'Affiché au client sur la page de paiement.',
          'Показывается клиенту на странице оплаты.',
          'Показується клієнту на сторінці оплати.',
          'Mostrato al cliente sulla pagina di pagamento.',
          'Se muestra al cliente en la página de pago.',
        ),
      },
      {
        name: 'merchant_reference',
        type: 'string',
        required: false,
        description: t(
          'Your own invoice or order number. Returned on every webhook about this payment.',
          'Вашият номер на фактура или поръчка. Връща се във всеки webhook за това плащане.',
          'Ihre eigene Rechnungs- oder Bestellnummer. Wird in jedem Webhook zu dieser Zahlung zurückgegeben.',
          'Votre propre numéro de facture ou de commande. Renvoyé dans chaque webhook.',
          'Ваш номер счёта или заказа. Возвращается в каждом вебхуке об этом платеже.',
          'Ваш номер рахунку або замовлення. Повертається у кожному вебхуку.',
          'Il tuo numero di fattura o ordine. Restituito in ogni webhook su questo pagamento.',
          'Su propio número de factura o pedido. Se devuelve en cada webhook.',
        ),
      },
      {
        name: 'create_link',
        type: 'boolean',
        required: false,
        description: t(
          'Defaults to true. Set false to create the payment without a customer link.',
          'По подразбиране true. Задайте false, за да създадете плащането без линк за клиента.',
          'Standard true. Auf false setzen, um die Zahlung ohne Kundenlink zu erstellen.',
          'true par défaut. Mettez false pour créer le paiement sans lien client.',
          'По умолчанию true. Укажите false, чтобы создать платёж без ссылки.',
          'За замовчуванням true. Вкажіть false, щоб створити платіж без посилання.',
          'Predefinito true. Imposta false per creare il pagamento senza link.',
          'Por defecto true. Ponga false para crear el pago sin enlace.',
        ),
      },
    ],
    request: `curl https://api.wyocs.org/api/v1/payments \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Idempotency-Key: YOUR_IDEMPOTENCY_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 4990,
    "currency": "usd",
    "description": "Monthly subscription - July 2026",
    "merchant_reference": "INV-2026-0415"
  }'`,
    response: `{
  "id": "pay_QSW6XBKZ498C1RZS4MB2JEZKTASXPG3R",
  "amount": 4990,
  "currency": "usd",
  "status": "CREATED",
  "description": "Monthly subscription - July 2026",
  "merchant_reference": "INV-2026-0415",
  "created_at": "2026-07-29T10:14:02.881Z",
  "expires_at": "2026-08-28T10:14:02.881Z",
  "link": {
    "url": "https://pay.wyocs.org/p/AY9K2M...",
    "expires_at": "2026-07-30T10:14:02.881Z"
  }
}`,
  },
  {
    id: 'get-payment',
    errors: ['not_found'],
    method: 'GET',
    path: '/api/v1/payments/{id}',
    group: 'payments',
    title: t('Retrieve a payment', 'Извличане на плащане', 'Zahlung abrufen', 'Récupérer un paiement', 'Получить платёж', 'Отримати платіж', 'Recuperare un pagamento', 'Recuperar un pago'),
    summary: t(
      'The current state of one payment, including how much has been refunded and whether it is disputed.',
      'Текущото състояние на едно плащане, включително колко е рефундирано и дали е оспорено.',
      'Der aktuelle Stand einer Zahlung, einschließlich erstatteter Beträge und Streitfälle.',
      "L'état actuel d'un paiement, y compris les remboursements et les litiges.",
      'Текущее состояние платежа, включая возвраты и споры.',
      'Поточний стан платежу, включно з поверненнями та спорами.',
      'Lo stato attuale di un pagamento, inclusi rimborsi e contestazioni.',
      'El estado actual de un pago, incluidos reembolsos y disputas.',
    ),
    response: `{
  "id": "pay_QSW6XBKZ498C1RZS4MB2JEZKTASXPG3R",
  "amount": 4990,
  "amount_refunded": 0,
  "currency": "usd",
  "status": "SUCCEEDED",
  "refund_status": "none",
  "dispute_status": "none",
  "paid_at": "2026-07-29T10:21:44.102Z"
}`,
  },
  {
    id: 'list-payments',
    errors: ['invalid_request', 'invalid_cursor'],
    method: 'GET',
    path: '/api/v1/payments',
    group: 'payments',
    title: t('List payments', 'Списък с плащания', 'Zahlungen auflisten', 'Lister les paiements', 'Список платежей', 'Список платежів', 'Elencare i pagamenti', 'Listar pagos'),
    summary: t(
      'Your payments, newest first. Filter by status with ?state=SUCCEEDED.',
      'Вашите плащания, най-новите първо. Филтрирайте по статус с ?state=SUCCEEDED.',
      'Ihre Zahlungen, neueste zuerst. Filtern mit ?state=SUCCEEDED.',
      'Vos paiements, les plus récents en premier. Filtrez avec ?state=SUCCEEDED.',
      'Ваши платежи, новые сначала. Фильтр ?state=SUCCEEDED.',
      'Ваші платежі, нові спочатку. Фільтр ?state=SUCCEEDED.',
      'I tuoi pagamenti, dal più recente. Filtra con ?state=SUCCEEDED.',
      'Sus pagos, los más recientes primero. Filtre con ?state=SUCCEEDED.',
    ),
    response: `{ "data": [ { "id": "pay_...", "amount": 4990, "status": "SUCCEEDED" } ] }`,
  },
  {
    id: 'renew-link',
    errors: ['invalid_request', 'capability_required', 'not_found', 'already_paid', 'idempotency_conflict'],
    method: 'POST',
    path: '/api/v1/payments/{id}/link',
    group: 'links',
    title: t('Renew or reissue a link', 'Подновяване или преиздаване на линк', 'Link erneuern oder neu ausstellen', 'Renouveler ou réémettre un lien', 'Продлить или выпустить ссылку', 'Продовжити або випустити посилання', 'Rinnovare o riemettere un link', 'Renovar o reemitir un enlace'),
    summary: t(
      'Extends the link the customer already has, or issues a new one and returns its URL.',
      'Удължава линка, който клиентът вече има, или издава нов и връща неговия URL.',
      'Verlängert den vorhandenen Link oder stellt einen neuen aus und gibt dessen URL zurück.',
      "Prolonge le lien que le client possède déjà, ou en émet un nouveau et renvoie son URL.",
      'Продлевает уже имеющуюся у клиента ссылку либо выпускает новую и возвращает её URL.',
      'Продовжує наявне посилання або випускає нове та повертає його URL.',
      'Estende il link già in mano al cliente, oppure ne emette uno nuovo restituendone l’URL.',
      'Amplía el enlace que el cliente ya tiene, o emite uno nuevo y devuelve su URL.',
    ),
    note: t(
      'mode=renew extends the existing link and returns no URL, because only its hash is stored. mode=reissue creates a new link and returns the URL - the old one keeps working until it expires unless you revoke it.',
      'mode=renew удължава съществуващия линк и не връща URL, защото се пази само хешът му. mode=reissue създава нов линк и връща URL - старият продължава да работи до изтичането си, освен ако не го отнемете.',
      'mode=renew verlängert den bestehenden Link und gibt keine URL zurück, da nur der Hash gespeichert ist. mode=reissue erstellt einen neuen Link und gibt die URL zurück.',
      "mode=renew prolonge le lien existant sans renvoyer d'URL, seul son hachage étant conservé. mode=reissue crée un nouveau lien et renvoie l'URL.",
      'mode=renew продлевает существующую ссылку и не возвращает URL, так как хранится только хеш. mode=reissue создаёт новую ссылку и возвращает URL.',
      'mode=renew продовжує наявне посилання і не повертає URL, бо зберігається лише хеш. mode=reissue створює нове посилання та повертає URL.',
      "mode=renew estende il link esistente e non restituisce l'URL, poiché se ne conserva solo l'hash. mode=reissue crea un nuovo link e restituisce l'URL.",
      'mode=renew amplía el enlace existente y no devuelve URL, ya que solo se guarda su hash. mode=reissue crea uno nuevo y devuelve la URL.',
    ),
    params: [
      {
        name: 'mode',
        type: 'string',
        required: false,
        description: t(
          '"renew" or "reissue". Defaults to renew.',
          '„renew" или „reissue". По подразбиране renew.',
          '"renew" oder "reissue". Standard: renew.',
          '"renew" ou "reissue". Par défaut renew.',
          '«renew» или «reissue». По умолчанию renew.',
          '«renew» або «reissue». За замовчуванням renew.',
          '"renew" o "reissue". Predefinito renew.',
          '"renew" o "reissue". Por defecto renew.',
        ),
      },
    ],
    request: `curl -X POST https://api.wyocs.org/api/v1/payments/pay_.../link \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"mode": "reissue"}'`,
    response: `{
  "mode": "reissue",
  "url": "https://pay.wyocs.org/p/7HK2Q9...",
  "expires_at": "2026-07-30T10:14:02.881Z"
}`,
  },
  {
    id: 'refund',
    errors: ['invalid_request', 'invalid_amount', 'capability_required', 'not_found', 'not_settled', 'not_refundable', 'fully_refunded', 'amount_too_large', 'idempotency_conflict', 'processor_error', 'not_configured'],
    method: 'POST',
    path: '/api/v1/payments/{id}/refund',
    group: 'refunds',
    title: t('Refund a payment', 'Рефунд на плащане', 'Zahlung erstatten', 'Rembourser un paiement', 'Возврат платежа', 'Повернення платежу', 'Rimborsare un pagamento', 'Reembolsar un pago'),
    summary: t(
      'Returns money to the customer. Omit amount for a full refund.',
      'Връща пари на клиента. Пропуснете amount за пълен рефунд.',
      'Gibt Geld an den Kunden zurück. Ohne amount wird voll erstattet.',
      'Restitue de l’argent au client. Omettez amount pour un remboursement total.',
      'Возвращает деньги клиенту. Без amount - полный возврат.',
      'Повертає гроші клієнту. Без amount - повне повернення.',
      "Restituisce denaro al cliente. Ometti amount per un rimborso totale.",
      'Devuelve dinero al cliente. Omita amount para un reembolso total.',
    ),
    note: t(
      'The payment does not change state immediately. It moves to REFUNDED or PARTIALLY_REFUNDED once Stripe confirms, which is what the webhook tells you.',
      'Плащането не сменя състоянието си веднага. Минава в REFUNDED или PARTIALLY_REFUNDED, когато Stripe потвърди - това ви казва webhook-ът.',
      'Die Zahlung ändert ihren Status nicht sofort, sondern erst nach Bestätigung durch Stripe.',
      "Le paiement ne change pas d'état immédiatement ; il passe à REFUNDED après confirmation de Stripe.",
      'Платёж не меняет состояние сразу - он переходит в REFUNDED после подтверждения Stripe.',
      'Платіж не змінює стан одразу - він переходить у REFUNDED після підтвердження Stripe.',
      'Il pagamento non cambia stato subito: passa a REFUNDED dopo la conferma di Stripe.',
      'El pago no cambia de estado de inmediato; pasa a REFUNDED tras la confirmación de Stripe.',
    ),
    params: [
      {
        name: 'amount',
        type: 'integer',
        required: false,
        description: t(
          'Minor units. Omit for everything still refundable.',
          'В най-малката единица. Пропуснете за всичко, което още може да се върне.',
          'Kleinste Einheit. Ohne Angabe wird der gesamte erstattbare Betrag zurückgegeben.',
          'Unité mineure. Omettez pour tout le montant remboursable.',
          'В минимальных единицах. Без указания - вся возвратная сумма.',
          'У мінімальних одиницях. Без зазначення - уся сума до повернення.',
          'Unità minori. Ometti per tutto il rimborsabile.',
          'Unidades menores. Omita para todo lo reembolsable.',
        ),
      },
      {
        name: 'reason',
        type: 'string',
        required: false,
        description: t(
          'duplicate, fraudulent or requested_by_customer.',
          'duplicate, fraudulent или requested_by_customer.',
          'duplicate, fraudulent oder requested_by_customer.',
          'duplicate, fraudulent ou requested_by_customer.',
          'duplicate, fraudulent или requested_by_customer.',
          'duplicate, fraudulent або requested_by_customer.',
          'duplicate, fraudulent o requested_by_customer.',
          'duplicate, fraudulent o requested_by_customer.',
        ),
      },
    ],
    request: `curl -X POST https://api.wyocs.org/api/v1/payments/pay_.../refund \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Idempotency-Key: YOUR_IDEMPOTENCY_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"amount": 1000, "reason": "requested_by_customer"}'`,
    response: `{
  "id": "re_3TyHHwCgosl30auT0",
  "amount": 1000,
  "currency": "usd",
  "status": "succeeded",
  "payment_id": "pay_...",
  "remaining": 3990
}`,
  },
  {
    id: 'webhooks',
    method: 'POST',
    path: 'your endpoint',
    group: 'webhooks',
    title: t('Receiving webhooks', 'Получаване на webhooks', 'Webhooks empfangen', 'Recevoir les webhooks', 'Приём вебхуков', 'Отримання вебхуків', 'Ricevere i webhook', 'Recibir webhooks'),
    summary: t(
      'We POST an event to your endpoint whenever a payment changes. Verify the signature before trusting the body.',
      'Изпращаме POST събитие към вашия адрес при всяка промяна на плащане. Проверете подписа, преди да се доверите на тялото.',
      'Wir senden ein Ereignis per POST an Ihren Endpunkt. Prüfen Sie die Signatur, bevor Sie dem Body vertrauen.',
      'Nous envoyons un événement en POST à votre endpoint. Vérifiez la signature avant de faire confiance au corps.',
      'Мы отправляем POST-событие на ваш адрес. Проверьте подпись, прежде чем доверять телу.',
      'Ми надсилаємо POST-подію на вашу адресу. Перевірте підпис, перш ніж довіряти тілу.',
      'Inviamo un evento in POST al tuo endpoint. Verifica la firma prima di fidarti del corpo.',
      'Enviamos un evento por POST a su endpoint. Verifique la firma antes de confiar en el cuerpo.',
    ),
    note: t(
      'The signature is HMAC-SHA-256 over a canonical string built from four length-prefixed fields: the version tag "v1", the timestamp, the event id and the raw body. Compute it over the raw bytes - parsing the JSON and re-serialising changes them and the signature will not match. Reject anything older than five minutes. During a secret rotation the header carries more than one v1; accept the delivery if any of them matches.',
      'Подписът е HMAC-SHA-256 върху канонична форма от четири полета с префикс за дължина: етикета на версията „v1", времевата отметка, номера на събитието и суровото тяло. Смятайте го върху суровите байтове - парсването на JSON и повторното сериализиране ги променя и подписът няма да съвпадне. Отхвърляйте всичко по-старо от пет минути. При подмяна на тайната заглавката носи повече от едно v1; приемете, ако някое от тях съвпада.',
      'Die Signatur ist HMAC-SHA-256 über eine kanonische Zeichenkette aus vier längenpräfixierten Feldern: Versionskennung "v1", Zeitstempel, Ereignis-ID und Rohtext. Über die Rohbytes berechnen - JSON parsen und neu serialisieren verändert sie. Bei einem Wechsel des Geheimnisses enthält der Header mehrere v1; eines muss passen.',
      'La signature est un HMAC-SHA-256 sur une chaîne canonique de quatre champs préfixés par leur longueur : la version "v1", l\'horodatage, l\'identifiant de l\'événement et le corps brut. Calculez-la sur les octets bruts. Lors d\'une rotation, l\'en-tête porte plusieurs v1 ; il suffit qu\'un seul corresponde.',
      'Подпись - HMAC-SHA-256 по канонической строке из четырёх полей с префиксом длины: метка версии «v1», отметка времени, идентификатор события и сырое тело. Считайте по сырым байтам. При смене секрета заголовок несёт несколько v1; достаточно совпадения одного.',
      'Підпис - HMAC-SHA-256 за канонічним рядком із чотирьох полів із префіксом довжини: позначка версії «v1», час, ідентифікатор події та сире тіло. Обчислюйте по сирих байтах. Під час зміни секрету заголовок несе кілька v1; достатньо збігу одного.',
      'La firma è HMAC-SHA-256 su una stringa canonica di quattro campi con prefisso di lunghezza: la versione "v1", il timestamp, l\'id dell\'evento e il corpo grezzo. Calcolala sui byte grezzi. Durante una rotazione l\'intestazione porta più v1; ne basta uno.',
      'La firma es HMAC-SHA-256 sobre una cadena canónica de cuatro campos con prefijo de longitud: la versión "v1", la marca de tiempo, el id del evento y el cuerpo en bruto. Calcúlela sobre los bytes en bruto. Durante una rotación la cabecera lleva varios v1; basta con que uno coincida.',
    ),
    request: `// Node.js
import { createHmac, timingSafeEqual } from 'node:crypto';

// The signed string is the four fields below, each written as its length in
// bytes, a colon, then the field. Length-prefixing means no rearrangement of
// the values can produce the same bytes.
function canonical(timestamp, eventId, rawBody) {
  return ['v1', String(timestamp), eventId, rawBody]
    .map((p) => \`\${Buffer.byteLength(p, 'utf8')}:\${p}\`)
    .join('');
}

function verify(rawBody, header, secret) {
  // t=<unix>,id=<evt_...>,v1=<hex>[,v1=<hex>]  - more than one v1 during a
  // secret rotation, and any of them matching is enough.
  const parts = Object.create(null);
  const signatures = [];
  for (const piece of header.split(',')) {
    const [key, value] = piece.trim().split('=');
    if (key === 'v1') signatures.push(value);
    else parts[key] = value;
  }

  if (Math.abs(Date.now() / 1000 - Number(parts.t)) > 300) return false;

  const expected = createHmac('sha256', secret)
    .update(canonical(parts.t, parts.id, rawBody), 'utf8')
    .digest('hex');
  const b = Buffer.from(expected, 'hex');

  return signatures.some((sig) => {
    const a = Buffer.from(sig, 'hex');
    return a.length === b.length && timingSafeEqual(a, b);
  });
}`,
    response: `{
  "id": "evt_...",
  "object": "event",
  "type": "payment.succeeded",
  "created_at": "2026-07-29T10:21:44.102Z",
  "api_version": "2026-07-01",
  "livemode": true,
  "data": {
    "object": {
      "id": "pay_...",
      "amount": 4990,
      "currency": "usd",
      "status": "SUCCEEDED",
      "merchant_reference": "INV-2026-0415"
    }
  }
}`,
  },
  {
    id: 'attempts',
    errors: ['not_found'],
    method: 'GET',
    path: '/api/v1/payments/{id}/attempts',
    group: 'payments',
    title: t('List attempts', 'Списък с опити', 'Versuche auflisten', 'Lister les tentatives', 'Список попыток', 'Список спроб', 'Elencare i tentativi', 'Listar intentos'),
    summary: t(
      'Every attempt made on one payment, including the ones that failed and why.',
      'Всеки опит по едно плащане, включително неуспешните и защо са се провалили.',
      'Jeder Versuch zu einer Zahlung, auch die gescheiterten und ihr Grund.',
      "Chaque tentative sur un paiement, y compris celles qui ont échoué et pourquoi.",
      'Каждая попытка по платежу, включая неудачные и их причину.',
      'Кожна спроба за платежем, включно з невдалими та їх причиною.',
      'Ogni tentativo su un pagamento, inclusi quelli falliti e il motivo.',
      'Cada intento sobre un pago, incluidos los fallidos y por qué.',
    ),
    note: t(
      'A declined card produces an attempt with a decline_code. That code is the issuer’s, not ours, and is what you quote when a customer asks their bank.',
      'Отказана карта дава опит с decline_code. Този код е на банката издател, не наш, и с него клиентът пита банката си.',
      'Eine abgelehnte Karte erzeugt einen Versuch mit decline_code - der Code stammt von der Bank des Kunden.',
      "Une carte refusée produit une tentative avec decline_code : ce code vient de la banque émettrice.",
      'Отклонённая карта даёт попытку с decline_code - это код банка-эмитента.',
      'Відхилена картка дає спробу з decline_code - це код банку-емітента.',
      'Una carta rifiutata produce un tentativo con decline_code, che è il codice dell’emittente.',
      'Una tarjeta rechazada produce un intento con decline_code, el código del emisor.',
    ),
    response: `{
  "object": "list",
  "data": [
    {
      "id": "attempt_...",
      "sequence": 1,
      "status": "FAILED",
      "payment_method_type": "card",
      "failure_code": "card_declined",
      "decline_code": "insufficient_funds",
      "failure_message": "Your card has insufficient funds.",
      "created_at": "2026-07-29T10:19:03.220Z"
    }
  ]
}`,
  },
  {
    id: 'list-refunds',
    errors: ['not_found', 'processor_error', 'not_configured'],
    method: 'GET',
    path: '/api/v1/payments/{id}/refunds',
    group: 'refunds',
    title: t('List refunds', 'Списък с рефунди', 'Rückerstattungen auflisten', 'Lister les remboursements', 'Список возвратов', 'Список повернень', 'Elencare i rimborsi', 'Listar reembolsos'),
    summary: t(
      'Every refund on one payment, read from Stripe rather than from a local copy.',
      'Всеки рефунд по едно плащане, прочетен от Stripe, а не от локално копие.',
      'Jede Rückerstattung zu einer Zahlung, direkt aus Stripe gelesen.',
      'Chaque remboursement sur un paiement, lu depuis Stripe.',
      'Каждый возврат по платежу, прочитанный из Stripe.',
      'Кожне повернення за платежем, прочитане зі Stripe.',
      'Ogni rimborso su un pagamento, letto da Stripe.',
      'Cada reembolso sobre un pago, leído desde Stripe.',
    ),
    response: `{
  "object": "list",
  "data": [
    {
      "id": "re_...",
      "amount": 1000,
      "currency": "usd",
      "status": "succeeded",
      "reason": "requested_by_customer",
      "created": "2026-07-29T11:02:10.000Z",
      "requested_by": "usr_..."
    }
  ]
}`,
  },
  {
    id: 'revoke-link',
    errors: ['not_found'],
    method: 'POST',
    path: '/api/v1/payments/{paymentId}/links/{linkId}/revoke',
    group: 'links',
    title: t('Revoke a link', 'Отнемане на линк', 'Link widerrufen', 'Révoquer un lien', 'Отозвать ссылку', 'Відкликати посилання', 'Revocare un link', 'Revocar un enlace'),
    summary: t(
      'Stops a link working immediately, without touching the payment itself.',
      'Спира линка веднага, без да засяга самото плащане.',
      'Setzt einen Link sofort außer Kraft, ohne die Zahlung selbst zu berühren.',
      'Désactive un lien immédiatement, sans toucher au paiement lui-même.',
      'Немедленно отключает ссылку, не затрагивая сам платёж.',
      'Негайно вимикає посилання, не торкаючись самого платежу.',
      'Disattiva subito un link, senza toccare il pagamento.',
      'Desactiva un enlace de inmediato, sin tocar el pago.',
    ),
    note: t(
      'The payment stays payable - issue a new link with mode=reissue if the customer still needs to pay.',
      'Плащането остава платимо - издайте нов линк с mode=reissue, ако клиентът още трябва да плати.',
      'Die Zahlung bleibt zahlbar - stellen Sie mit mode=reissue einen neuen Link aus.',
      'Le paiement reste payable : émettez un nouveau lien avec mode=reissue.',
      'Платёж остаётся оплачиваемым - выпустите новую ссылку с mode=reissue.',
      'Платіж залишається оплачуваним - випустіть нове посилання з mode=reissue.',
      'Il pagamento resta pagabile: emetti un nuovo link con mode=reissue.',
      'El pago sigue siendo pagable: emita un nuevo enlace con mode=reissue.',
    ),
    response: `{ "revoked": true, "payment_id": "pay_...", "link_id": "plink_..." }`,
  },
  {
    id: 'events',
    errors: ['invalid_request', 'invalid_cursor'],
    method: 'GET',
    path: '/api/v1/events',
    group: 'webhooks',
    title: t('List events', 'Списък със събития', 'Ereignisse auflisten', 'Lister les événements', 'Список событий', 'Список подій', 'Elencare gli eventi', 'Listar eventos'),
    summary: t(
      'The same events we send to your webhook endpoint, so you can catch up after downtime.',
      'Същите събития, които изпращаме към вашия webhook адрес, за да наваксате след прекъсване.',
      'Dieselben Ereignisse, die wir an Ihren Webhook senden - zum Nachholen nach einem Ausfall.',
      'Les mêmes événements que ceux envoyés à votre webhook, pour rattraper après une panne.',
      'Те же события, что мы шлём на ваш вебхук - чтобы наверстать после простоя.',
      'Ті самі події, що ми надсилаємо на ваш вебхук - щоб надолужити після простою.',
      'Gli stessi eventi che inviamo al tuo webhook, per recuperare dopo un fermo.',
      'Los mismos eventos que enviamos a su webhook, para ponerse al día tras una caída.',
    ),
    note: t(
      'Webhooks remain the primary channel. Polling this on a timer under normal operation is not the intended use and will be rate limited.',
      'Webhooks остават основният канал. Периодичното дърпане оттук при нормална работа не е предназначението и се ограничава по скорост.',
      'Webhooks bleiben der primäre Kanal. Regelmäßiges Abfragen im Normalbetrieb ist nicht vorgesehen.',
      "Les webhooks restent le canal principal. L'interrogation périodique en fonctionnement normal n'est pas prévue.",
      'Вебхуки остаются основным каналом. Регулярный опрос при нормальной работе не предусмотрен.',
      'Вебхуки залишаються основним каналом. Регулярне опитування за нормальної роботи не передбачене.',
      'I webhook restano il canale primario. Il polling periodico non è previsto.',
      'Los webhooks son el canal principal. El sondeo periódico no está previsto.',
    ),
    response: `{
  "object": "list",
  "data": [
    { "id": "evt_...", "type": "payment.succeeded", "data": { "id": "pay_...", "status": "SUCCEEDED" } }
  ]
}`,
  },
  {
    id: 'account',
    method: 'GET',
    path: '/api/v1/account',
    group: 'account',
    title: t('Your account', 'Вашият профил', 'Ihr Konto', 'Votre compte', 'Ваш аккаунт', 'Ваш обліковий запис', 'Il tuo account', 'Su cuenta'),
    summary: t(
      'What your account is and what it can currently do, including anything Stripe is still waiting for.',
      'Какъв е профилът ви и какво може в момента, включително какво още чака Stripe.',
      'Was Ihr Konto ist und aktuell kann, einschließlich offener Stripe-Anforderungen.',
      "Ce qu'est votre compte et ce qu'il peut faire, y compris ce que Stripe attend encore.",
      'Что представляет собой ваш аккаунт и что он может, включая ожидания Stripe.',
      'Чим є ваш обліковий запис і що він може, включно з очікуваннями Stripe.',
      'Cos’è il tuo account e cosa può fare, inclusi i requisiti Stripe in sospeso.',
      'Qué es su cuenta y qué puede hacer, incluidos los requisitos pendientes de Stripe.',
    ),
    note: t(
      'settlement_currency is what Stripe pays you in and follows your account country. It can differ from the currency you charge in.',
      'settlement_currency е валутата, в която Stripe ви плаща, и следва държавата на профила. Може да се различава от валутата, в която таксувате.',
      'settlement_currency ist die Auszahlungswährung und folgt dem Kontoland; sie kann von der Belastungswährung abweichen.',
      "settlement_currency est la devise de versement, liée au pays du compte ; elle peut différer de la devise de facturation.",
      'settlement_currency - валюта выплат, зависит от страны аккаунта и может отличаться от валюты списания.',
      'settlement_currency - валюта виплат, залежить від країни облікового запису.',
      'settlement_currency è la valuta di accredito e segue il paese dell’account.',
      'settlement_currency es la moneda de abono y sigue el país de la cuenta.',
    ),
    response: `{
  "object": "account",
  "id": "mch_...",
  "name": "Northwind Supply Co.",
  "country": "US",
  "default_currency": "usd",
  "settlement_currency": "usd",
  "status": "ACTIVE",
  "charges_enabled": true,
  "payouts_enabled": true,
  "details_submitted": true,
  "requirements_due": [],
  "fee": { "percent_bps": 290, "fixed": 30 }
}`,
  },
  {
    id: 'balance',
    errors: ['not_onboarded', 'processor_error', 'not_configured'],
    method: 'GET',
    path: '/api/v1/balance',
    group: 'account',
    title: t('Balance', 'Баланс', 'Guthaben', 'Solde', 'Баланс', 'Баланс', 'Saldo', 'Saldo'),
    summary: t(
      'What is on your Stripe balance now and what is still settling, read live from Stripe.',
      'Какво има в баланса ви в Stripe сега и какво още се урежда, прочетено на живо от Stripe.',
      'Was aktuell auf Ihrem Stripe-Guthaben liegt und was noch aussteht, live von Stripe gelesen.',
      'Ce qui est sur votre solde Stripe et ce qui est en cours, lu en direct depuis Stripe.',
      'Что сейчас на балансе Stripe и что ещё в пути, прочитано напрямую из Stripe.',
      'Що зараз на балансі Stripe і що ще в дорозі, прочитано напряму зі Stripe.',
      'Cosa c’è ora sul saldo Stripe e cosa è in arrivo, letto in diretta da Stripe.',
      'Lo que hay en su saldo de Stripe y lo que está en camino, leído en directo.',
    ),
    note: t(
      'Returned as an array per currency. If you charge in more than one currency they are separate balances and must not be added together.',
      'Връща се масив по валута. Ако таксувате в повече от една валута, това са отделни баланси и не се събират.',
      'Als Array je Währung. Mehrere Währungen sind getrennte Guthaben und dürfen nicht addiert werden.',
      "Renvoyé sous forme de tableau par devise : ce sont des soldes distincts, à ne pas additionner.",
      'Возвращается массивом по валютам - это отдельные балансы, их нельзя складывать.',
      'Повертається масивом за валютами - це окремі баланси, їх не можна додавати.',
      'Restituito come array per valuta: sono saldi distinti e non vanno sommati.',
      'Se devuelve como array por moneda: son saldos distintos y no deben sumarse.',
    ),
    response: `{
  "object": "balance",
  "settlement_currency": "usd",
  "available": [ { "amount": 0, "currency": "usd" } ],
  "pending":   [ { "amount": 551713, "currency": "usd" } ]
}`,
  },
  {
    id: 'payouts',
    errors: ['invalid_request', 'not_onboarded', 'processor_error', 'not_configured'],
    method: 'GET',
    path: '/api/v1/payouts',
    group: 'account',
    title: t('List payouts', 'Списък с изплащания', 'Auszahlungen auflisten', 'Lister les versements', 'Список выплат', 'Список виплат', 'Elencare i versamenti', 'Listar pagos a banco'),
    summary: t(
      'Money moved from your Stripe balance to your bank, with arrival dates and any failure reason.',
      'Пари, преместени от баланса ви в Stripe към банката, с дати на пристигане и причина при неуспех.',
      'Geld von Ihrem Stripe-Guthaben auf Ihr Bankkonto, mit Ankunftsdatum und Fehlergrund.',
      "L'argent transféré de votre solde Stripe vers votre banque, avec dates et motifs d'échec.",
      'Деньги, переведённые с баланса Stripe в банк, с датами и причинами сбоя.',
      'Гроші, переведені з балансу Stripe у банк, з датами та причинами збою.',
      'Denaro trasferito dal saldo Stripe alla tua banca, con date e motivi di fallimento.',
      'Dinero movido de su saldo de Stripe a su banco, con fechas y motivos de fallo.',
    ),
    response: `{
  "object": "list",
  "data": [
    {
      "id": "po_...",
      "amount": 551713,
      "currency": "usd",
      "status": "paid",
      "method": "standard",
      "arrival_date": "2026-08-01T00:00:00.000Z",
      "failure_code": null,
      "failure_message": null
    }
  ]
}`,
  },
  {
    id: 'disputes',
    errors: ['invalid_request', 'not_onboarded', 'processor_error', 'not_configured'],
    method: 'GET',
    path: '/api/v1/disputes',
    group: 'refunds',
    title: t('List disputes', 'Списък със спорове', 'Streitfälle auflisten', 'Lister les litiges', 'Список споров', 'Список спорів', 'Elencare le contestazioni', 'Listar disputas'),
    summary: t(
      'Chargebacks against your account, with the deadline for submitting evidence.',
      'Оспорвания срещу профила ви, със срока за подаване на доказателства.',
      'Rückbuchungen gegen Ihr Konto, mit der Frist zur Beweisvorlage.',
      'Les litiges contre votre compte, avec la date limite pour fournir des preuves.',
      'Чарджбэки против вашего аккаунта, со сроком подачи доказательств.',
      'Чарджбеки проти вашого облікового запису, зі строком подання доказів.',
      'Contestazioni contro il tuo account, con la scadenza per le prove.',
      'Contracargos contra su cuenta, con la fecha límite para presentar pruebas.',
    ),
    note: t(
      'The disputed amount and Stripe’s dispute fee are debited from your balance when the dispute opens, and returned only if you win.',
      'Оспорената сума и таксата на Stripe за спор се теглят от баланса ви при откриване на спора и се връщат само ако спечелите.',
      'Der strittige Betrag und die Stripe-Gebühr werden bei Eröffnung abgebucht und nur bei Gewinn erstattet.',
      "Le montant contesté et les frais de litige Stripe sont débités à l'ouverture et rendus seulement si vous gagnez.",
      'Оспариваемая сумма и комиссия Stripe списываются при открытии спора и возвращаются только при выигрыше.',
      'Оспорювана сума та комісія Stripe списуються при відкритті спору й повертаються лише при виграші.',
      'L’importo contestato e la commissione Stripe vengono addebitati all’apertura e restituiti solo se vinci.',
      'El importe disputado y la comisión de Stripe se cargan al abrirse y se devuelven solo si gana.',
    ),
    response: `{
  "object": "list",
  "data": [
    {
      "id": "dp_...",
      "amount": 4990,
      "currency": "usd",
      "reason": "fraudulent",
      "status": "needs_response",
      "evidence_due_by": "2026-08-12T23:59:59.000Z",
      "payment_intent": "pi_..."
    }
  ]
}`,
  },
  {
    id: 'payment-methods',
    errors: ['not_onboarded', 'processor_error', 'not_configured'],
    method: 'GET',
    path: '/api/v1/payment-methods',
    group: 'account',
    title: t('Payment methods', 'Методи за плащане', 'Zahlungsmethoden', 'Moyens de paiement', 'Способы оплаты', 'Способи оплати', 'Metodi di pagamento', 'Métodos de pago'),
    summary: t(
      'Which methods are switched on for your account, and which Stripe will not offer.',
      'Кои методи са включени за профила ви и кои Stripe няма да предложи.',
      'Welche Methoden für Ihr Konto aktiv sind und welche Stripe nicht anbietet.',
      'Quels moyens sont activés sur votre compte et lesquels Stripe ne proposera pas.',
      'Какие способы включены для вашего аккаунта и какие Stripe не предложит.',
      'Які способи ввімкнені для вашого облікового запису і які Stripe не запропонує.',
      'Quali metodi sono attivi sul tuo account e quali Stripe non offrirà.',
      'Qué métodos están activos en su cuenta y cuáles Stripe no ofrecerá.',
    ),
    note: t(
      'Change these in the merchant panel. A method being on does not guarantee it appears - the amount, currency and customer country all narrow what Stripe shows.',
      'Променят се от merchant панела. Включен метод не гарантира, че ще се появи - сумата, валутата и държавата на клиента стесняват какво показва Stripe.',
      'Änderbar im Händler-Panel. Aktiv heißt nicht immer sichtbar - Betrag, Währung und Kundenland schränken ein.',
      "Modifiable dans le panneau marchand. Activé ne signifie pas toujours affiché.",
      'Меняются в панели продавца. Включённый способ не всегда отображается.',
      'Змінюються в панелі продавця. Увімкнений спосіб не завжди відображається.',
      'Modificabili nel pannello. Attivo non significa sempre visibile.',
      'Se cambian en el panel. Activo no significa siempre visible.',
    ),
    response: `{
  "configuration_id": "pmc_...",
  "groups": [
    {
      "group": "Cards and wallets",
      "methods": [
        { "key": "card", "label": "Cards", "enabled": true, "available": true, "unavailableReason": null }
      ]
    }
  ],
  "other": []
}`,
  },

  // -------------------------------------------------------------------------
  // Payments, continued
  // -------------------------------------------------------------------------

  {
    id: 'payment-timeline',
    errors: ['not_found'],
    method: 'GET',
    path: '/api/v1/payments/{id}/timeline',
    group: 'payments',
    title: t('Payment timeline', 'История на плащане', 'Zahlungsverlauf', 'Historique du paiement', 'История платежа', 'Історія платежу', 'Cronologia del pagamento', 'Historial del pago'),
    summary: t(
      'Everything that happened to one payment, in reverse order: who created it, who issued or revoked a link, what Stripe reported and when a refund was asked for.',
      'Всичко случило се с едно плащане, в обратен ред: кой го е създал, кой е издал или отнел линк, какво е съобщил Stripe и кога е поискан рефунд.',
      'Alles, was mit einer Zahlung geschah, in umgekehrter Reihenfolge: wer sie anlegte, wer einen Link ausstellte oder widerrief, was Stripe meldete und wann eine Erstattung angefragt wurde.',
      "Tout ce qui est arrivé à un paiement, en ordre inverse : qui l'a créé, qui a émis ou révoqué un lien, ce que Stripe a signalé et quand un remboursement a été demandé.",
      'Всё, что происходило с платежом, в обратном порядке: кто создал, кто выпустил или отозвал ссылку, что сообщил Stripe и когда запросили возврат.',
      'Усе, що відбувалося з платежем, у зворотному порядку: хто створив, хто випустив або відкликав посилання, що повідомив Stripe і коли запросили повернення.',
      "Tutto ciò che è accaduto a un pagamento, in ordine inverso: chi l'ha creato, chi ha emesso o revocato un link, cosa ha riferito Stripe e quando è stato chiesto un rimborso.",
      'Todo lo ocurrido con un pago, en orden inverso: quién lo creó, quién emitió o revocó un enlace, qué informó Stripe y cuándo se pidió un reembolso.',
    ),
    note: t(
      'This is the answer to "why is this payment in this state". It is read from the platform\'s hash-chained audit log, so an entry cannot be altered afterwards without the chain failing verification. It is a separate call rather than extra fields on GET /payments/{id} because that response is fetched on every reconciliation run and should not carry an unbounded array.',
      'Това е отговорът на „защо това плащане е в това състояние". Чете се от хеш-верижния одитен журнал, така че запис не може да бъде променен после, без веригата да се провали при проверка. Отделно извикване е, а не допълнителни полета в GET /payments/{id}, защото онзи отговор се тегли при всяка равносметка и не бива да носи неограничен масив.',
      'Das ist die Antwort auf "warum ist diese Zahlung in diesem Status". Gelesen wird aus dem hash-verketteten Audit-Log, ein Eintrag lässt sich also nachträglich nicht ändern, ohne dass die Kette bei der Prüfung scheitert. Ein eigener Aufruf statt zusätzlicher Felder in GET /payments/{id}, weil jene Antwort bei jedem Abgleich geholt wird.',
      "C'est la réponse à « pourquoi ce paiement est-il dans cet état ». Lue depuis le journal d'audit chaîné par hachage : une entrée ne peut être modifiée après coup sans que la chaîne échoue à la vérification. Appel distinct plutôt que des champs en plus sur GET /payments/{id}, car cette réponse est récupérée à chaque rapprochement.",
      'Это ответ на вопрос «почему платёж в этом состоянии». Читается из хеш-цепочечного журнала аудита, поэтому запись нельзя изменить задним числом без провала проверки цепочки. Отдельный вызов, а не лишние поля в GET /payments/{id}, потому что тот ответ забирают при каждой сверке.',
      'Це відповідь на питання «чому платіж у цьому стані». Читається з хеш-ланцюгового журналу аудиту, тож запис не можна змінити заднім числом без провалу перевірки ланцюга. Окремий виклик, а не зайві поля в GET /payments/{id}, бо ту відповідь забирають при кожній звірці.',
      "È la risposta a \"perché questo pagamento è in questo stato\". Viene letta dal registro di audit concatenato per hash, quindi una voce non può essere alterata dopo senza che la catena fallisca la verifica. Chiamata separata anziché campi in più su GET /payments/{id}, perché quella risposta viene richiesta a ogni riconciliazione.",
      'Es la respuesta a «por qué este pago está en este estado». Se lee del registro de auditoría encadenado por hash, así que una entrada no puede alterarse después sin que la cadena falle la verificación. Llamada aparte y no campos extra en GET /payments/{id}, porque esa respuesta se pide en cada conciliación.',
    ),
    params: [
      {
        name: 'limit',
        type: 'integer',
        required: false,
        description: t(
          '1 to 200. Defaults to 100.',
          'От 1 до 200. По подразбиране 100.',
          '1 bis 200. Standard 100.',
          '1 à 200. Par défaut 100.',
          'От 1 до 200. По умолчанию 100.',
          'Від 1 до 200. За замовчуванням 100.',
          'Da 1 a 200. Predefinito 100.',
          'De 1 a 200. Por defecto 100.',
        ),
      },
    ],
    request: `curl https://api.wyocs.org/api/v1/payments/pay_.../timeline \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "object": "list",
  "data": [
    {
      "at": "2026-07-29T10:22:07.551Z",
      "action": "payment.state_changed",
      "actor": "stripe_webhook",
      "data": { "from": "PROCESSING", "to": "SUCCEEDED" }
    },
    {
      "at": "2026-07-29T10:14:02.881Z",
      "action": "payment_link.created",
      "actor": "api_key:ak_...",
      "data": { "linkId": "plink_...", "expiresAt": "2026-07-30T10:14:02.881Z" }
    },
    {
      "at": "2026-07-29T10:14:02.744Z",
      "action": "payment.created",
      "actor": "api_key:ak_...",
      "data": { "amount": 4990, "currency": "usd", "merchantReference": "INV-2026-0415" }
    }
  ]
}`,
  },

  {
    id: 'list-links',
    errors: ['not_found'],
    method: 'GET',
    path: '/api/v1/payments/{id}/links',
    group: 'links',
    title: t('List a payment’s links', 'Линкове на плащане', 'Links einer Zahlung', 'Liens d’un paiement', 'Ссылки платежа', 'Посилання платежу', 'Link di un pagamento', 'Enlaces de un pago'),
    summary: t(
      'Every link ever issued for one payment, with whether it has expired, been revoked or been used.',
      'Всеки издаван някога линк за едно плащане, с това дали е изтекъл, отнет или използван.',
      'Jeder je ausgestellte Link zu einer Zahlung, mit Ablauf, Widerruf und Nutzung.',
      "Tous les liens jamais émis pour un paiement, avec expiration, révocation et utilisation.",
      'Все когда-либо выпущенные ссылки платежа, с признаками истечения, отзыва и использования.',
      'Усі коли-небудь випущені посилання платежу, з ознаками спливання, відкликання та використання.',
      'Ogni link mai emesso per un pagamento, con scadenza, revoca e utilizzo.',
      'Todos los enlaces emitidos para un pago, con caducidad, revocación y uso.',
    ),
    note: t(
      'The URLs are not here and cannot be. Only a hash of each link token is stored, which is what stops a database dump from becoming a set of working payment URLs. Reissuing returns a new URL once; renewing extends the one the customer already holds.',
      'URL адресите не са тук и не могат да бъдат. Пази се само хеш на всеки токен, което спира един database dump да се превърне в комплект работещи адреси за плащане. Преиздаването връща нов URL веднъж; подновяването удължава онзи, който клиентът вече има.',
      'Die URLs stehen hier nicht und können es nicht. Gespeichert wird nur ein Hash je Token - das verhindert, dass ein Datenbankabzug zu einem Satz funktionierender Zahlungs-URLs wird.',
      "Les URL n'y figurent pas et ne le peuvent pas. Seul un hachage de chaque jeton est conservé, ce qui empêche une copie de la base de devenir un jeu d'URL de paiement fonctionnelles.",
      'URL здесь нет и быть не может. Хранится только хеш каждого токена - именно это не даёт дампу базы стать набором рабочих платёжных ссылок.',
      'URL тут немає і бути не може. Зберігається лише хеш кожного токена - саме це не дає дампу бази стати набором робочих платіжних посилань.',
      "Gli URL non ci sono e non possono esserci. Si conserva solo un hash di ogni token, il che impedisce a un dump del database di diventare un insieme di URL di pagamento funzionanti.",
      'Las URL no están aquí ni pueden estarlo. Solo se guarda un hash de cada token, lo que impide que un volcado de la base sea un juego de URL de pago funcionales.',
    ),
    request: `curl https://api.wyocs.org/api/v1/payments/pay_.../links \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "object": "list",
  "data": [
    {
      "id": "plink_7QK2...",
      "created_at": "2026-07-29T10:14:02.881Z",
      "expires_at": "2026-07-30T10:14:02.881Z",
      "expired": false,
      "revoked": false,
      "revoked_at": null,
      "used_at": null
    }
  ]
}`,
  },

  // -------------------------------------------------------------------------
  // Refunds, continued
  // -------------------------------------------------------------------------

  {
    id: 'get-refund',
    errors: ['not_found', 'not_onboarded', 'processor_error', 'not_configured'],
    method: 'GET',
    path: '/api/v1/refunds/{id}',
    group: 'refunds',
    title: t('Retrieve a refund', 'Извличане на рефунд', 'Erstattung abrufen', 'Récupérer un remboursement', 'Получить возврат', 'Отримати повернення', 'Recuperare un rimborso', 'Recuperar un reembolso'),
    summary: t(
      'One refund by the identifier the refund call returned, with its current status.',
      'Един рефунд по идентификатора, върнат от заявката за рефунд, с текущия му статус.',
      'Eine Erstattung anhand der zurückgegebenen Kennung, mit ihrem aktuellen Status.',
      "Un remboursement par l'identifiant renvoyé lors de sa création, avec son état actuel.",
      'Один возврат по идентификатору, который вернул вызов возврата, с текущим статусом.',
      'Одне повернення за ідентифікатором, який повернув виклик повернення, з поточним статусом.',
      "Un rimborso tramite l'identificatore restituito alla creazione, con lo stato attuale.",
      'Un reembolso por el identificador devuelto al crearlo, con su estado actual.',
    ),
    note: t(
      'A refund is not instant. It leaves as pending and becomes succeeded or failed once the customer’s bank has answered, which can take days on some methods - this is where you check. A refund belonging to another merchant answers exactly as an invented identifier does, because the connected account being asked is resolved from your key rather than taken from the request.',
      'Рефундът не е моментален. Тръгва като pending и става succeeded или failed, след като банката на клиента отговори, което при някои методи отнема дни - тук проверявате. Рефунд на друг търговец отговаря точно както измислен идентификатор, защото свързаният акаунт се извежда от вашия ключ, а не се взема от заявката.',
      'Eine Erstattung ist nicht sofort. Sie startet als pending und wird succeeded oder failed, sobald die Bank des Kunden geantwortet hat - bei manchen Methoden dauert das Tage. Eine Erstattung eines anderen Händlers antwortet exakt wie eine erfundene Kennung, weil das abgefragte Konto aus Ihrem Schlüssel stammt.',
      "Un remboursement n'est pas instantané. Il part en pending puis devient succeeded ou failed une fois la banque du client revenue, ce qui prend des jours sur certains moyens. Un remboursement appartenant à un autre marchand répond exactement comme un identifiant inventé, car le compte interrogé vient de votre clé.",
      'Возврат не мгновенен. Он уходит как pending и становится succeeded или failed после ответа банка клиента, что на некоторых способах занимает дни. Возврат другого продавца отвечает точно как выдуманный идентификатор, потому что запрашиваемый счёт выводится из вашего ключа.',
      'Повернення не миттєве. Воно йде як pending і стає succeeded або failed після відповіді банку клієнта, що на деяких способах займає дні. Повернення іншого продавця відповідає точно як вигаданий ідентифікатор, бо рахунок виводиться з вашого ключа.',
      "Un rimborso non è istantaneo. Parte come pending e diventa succeeded o failed quando la banca del cliente risponde. Un rimborso di un altro esercente risponde esattamente come un identificatore inventato, perché l'account interrogato deriva dalla tua chiave.",
      'Un reembolso no es instantáneo. Sale como pending y pasa a succeeded o failed cuando responde el banco del cliente. Un reembolso de otro comercio responde igual que un identificador inventado, porque la cuenta consultada se deriva de su clave.',
    ),
    request: `curl https://api.wyocs.org/api/v1/refunds/re_3TyHHwCgosl30auT0 \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "id": "re_3TyHHwCgosl30auT0",
  "object": "refund",
  "amount": 1000,
  "currency": "usd",
  "status": "succeeded",
  "reason": "requested_by_customer",
  "created": "2026-07-29T11:02:18.000Z",
  "failure_reason": null,
  "payment_id": "pay_...",
  "requested_by": "api_key:ak_..."
}`,
  },

  // -------------------------------------------------------------------------
  // Disputes
  // -------------------------------------------------------------------------

  {
    id: 'get-dispute',
    errors: ['not_found', 'not_onboarded', 'processor_error', 'not_configured'],
    method: 'GET',
    path: '/api/v1/disputes/{id}',
    group: 'disputes',
    title: t('Retrieve a dispute', 'Извличане на спор', 'Streitfall abrufen', 'Récupérer un litige', 'Получить спор', 'Отримати спір', 'Recuperare una contestazione', 'Recuperar una disputa'),
    summary: t(
      'One dispute, with the amount at stake, the reason the cardholder gave, whether evidence has been submitted and the deadline for doing so.',
      'Един спор, със сумата на риск, причината, посочена от картодържателя, дали са подадени доказателства и крайния срок за това.',
      'Ein Streitfall mit dem strittigen Betrag, dem Grund des Karteninhabers, dem Stand der Beweisvorlage und der Frist dafür.',
      "Un litige, avec le montant en jeu, le motif invoqué par le porteur, l'état des preuves et la date limite.",
      'Один спор: сумма под риском, причина, названная держателем карты, поданы ли доказательства и срок для этого.',
      'Один спір: сума під ризиком, причина, названа власником картки, чи подані докази та термін для цього.',
      "Una contestazione, con l'importo in gioco, il motivo indicato dal titolare, lo stato delle prove e la scadenza.",
      'Una disputa, con el importe en juego, el motivo del titular, si se han presentado pruebas y el plazo.',
    ),
    note: t(
      'Read-only, deliberately. Evidence is submitted in the merchant panel, which renders Stripe’s own dispute component: the receipts, correspondence and delivery records go from your browser straight to Stripe and never pass through this platform. Building an upload endpoint here would put all of that through our servers for no gain. Watch evidence_due_by - a dispute with no evidence by that date is lost automatically.',
      'Само за четене, нарочно. Доказателствата се подават в merchant панела, който показва собствения компонент на Stripe: касовите бележки, кореспонденцията и товарителниците отиват от вашия браузър направо към Stripe и никога не минават през тази платформа. Следете evidence_due_by - спор без доказателства до тази дата се губи автоматично.',
      'Bewusst nur lesend. Beweise werden im Händler-Panel eingereicht, das Stripes eigene Komponente einbindet: Belege, Korrespondenz und Liefernachweise gehen direkt vom Browser zu Stripe und nie über diese Plattform. Beachten Sie evidence_due_by - ohne Beweise bis dahin ist der Fall automatisch verloren.',
      "En lecture seule, délibérément. Les preuves se déposent dans le panneau marchand, qui affiche le composant Stripe : reçus, correspondance et preuves de livraison vont du navigateur à Stripe sans passer par cette plateforme. Surveillez evidence_due_by : sans preuve à cette date, le litige est perdu d'office.",
      'Только чтение, намеренно. Доказательства подаются в панели продавца, где отображается компонент самого Stripe: чеки, переписка и подтверждения доставки идут из браузера прямо в Stripe и не проходят через эту платформу. Следите за evidence_due_by - спор без доказательств к этой дате проигрывается автоматически.',
      'Лише читання, навмисно. Докази подаються в панелі продавця, де відображається компонент самого Stripe: чеки, листування та підтвердження доставки йдуть із браузера прямо в Stripe і не проходять через цю платформу. Стежте за evidence_due_by - спір без доказів до цієї дати програється автоматично.',
      "Di sola lettura, deliberatamente. Le prove si caricano nel pannello, che mostra il componente di Stripe: ricevute, corrispondenza e prove di consegna vanno dal browser direttamente a Stripe. Attenzione a evidence_due_by: senza prove entro quella data la contestazione è persa d'ufficio.",
      'Solo lectura, a propósito. Las pruebas se envían en el panel, que muestra el componente de Stripe: recibos, correspondencia y comprobantes de entrega van del navegador a Stripe. Vigile evidence_due_by: sin pruebas en esa fecha la disputa se pierde automáticamente.',
    ),
    request: `curl https://api.wyocs.org/api/v1/disputes/dp_1TyH... \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "id": "dp_1TyH...",
  "object": "dispute",
  "amount": 4990,
  "currency": "usd",
  "reason": "product_not_received",
  "status": "needs_response",
  "created": "2026-07-28T08:11:02.000Z",
  "evidence_due_by": "2026-08-11T23:59:59.000Z",
  "has_evidence": false,
  "submission_count": 0,
  "payment_intent": "pi_..."
}`,
  },

  // -------------------------------------------------------------------------
  // Balance and payouts
  // -------------------------------------------------------------------------

  {
    id: 'balance-transactions',
    errors: ['invalid_request', 'not_onboarded', 'processor_error', 'not_configured'],
    method: 'GET',
    path: '/api/v1/balance/transactions',
    group: 'balance',
    title: t('Balance transactions', 'Движения по баланса', 'Guthabenbewegungen', 'Mouvements du solde', 'Движения по балансу', 'Рухи по балансу', 'Movimenti del saldo', 'Movimientos del saldo'),
    summary: t(
      'The ledger behind the balance: every charge, fee, refund, dispute and payout that moved it, in the order they were applied.',
      'Регистърът зад баланса: всяко плащане, такса, рефунд, спор и изплащане, което го е движило, в реда на прилагане.',
      'Das Hauptbuch hinter dem Guthaben: jede Belastung, Gebühr, Erstattung, Rückbuchung und Auszahlung, in der Reihenfolge ihrer Anwendung.',
      "Le grand livre derrière le solde : chaque encaissement, frais, remboursement, litige et virement, dans l'ordre où ils ont été appliqués.",
      'Регистр за балансом: каждое списание, комиссия, возврат, спор и выплата, в порядке применения.',
      'Реєстр за балансом: кожне списання, комісія, повернення, спір і виплата, у порядку застосування.',
      "Il libro mastro dietro il saldo: ogni addebito, commissione, rimborso, contestazione e bonifico, nell'ordine in cui sono stati applicati.",
      'El libro mayor tras el saldo: cada cargo, comisión, reembolso, disputa y transferencia, en el orden en que se aplicaron.',
    ),
    note: t(
      'This is what reconciliation actually needs. A balance says what is there now and nothing about how it got there; each row here carries amount, fee and net, so the gap between what a customer paid and what reached the bank is arithmetic rather than guesswork. available_on is the date the money becomes payable, which is usually later than created. The fee shown is Stripe\'s own processing fee, debited from this account directly; the platform\'s application fee is a separate line.',
      'Това е, което равнението наистина иска. Балансът казва какво има сега и нищо за това как е стигнало дотам; всеки ред тук носи amount, fee и net, така че разликата между платеното от клиента и стигналото до банката е аритметика, а не догадка. available_on е датата, на която парите стават изплатими, обикновено по-късно от created. Показаната такса е собствената такса на Stripe, дебитирана директно от този акаунт; таксата на платформата е отделен ред.',
      'Genau das braucht ein Abgleich. Ein Guthaben sagt, was jetzt da ist, und nichts darüber, wie es dorthin kam; jede Zeile trägt amount, fee und net, sodass die Differenz zwischen Kundenzahlung und Bankeingang Rechnen statt Raten ist. available_on ist das Datum der Auszahlbarkeit, meist später als created.',
      "C'est ce dont le rapprochement a besoin. Un solde dit ce qu'il y a maintenant, pas comment il y est arrivé ; chaque ligne porte amount, fee et net, si bien que l'écart entre ce que le client a payé et ce qui arrive en banque relève du calcul et non de la supposition. available_on est la date à laquelle l'argent devient virable, généralement postérieure à created.",
      'Именно это нужно сверке. Баланс говорит, что есть сейчас, и ничего о том, как оно туда попало; в каждой строке есть amount, fee и net, поэтому разница между уплаченным клиентом и дошедшим до банка - арифметика, а не догадка. available_on - дата, когда деньги становятся доступны к выплате, обычно позже created.',
      'Саме це потрібно звірці. Баланс каже, що є зараз, і нічого про те, як воно туди потрапило; у кожному рядку є amount, fee і net, тож різниця між сплаченим клієнтом і тим, що дійшло до банку, - арифметика, а не здогад. available_on - дата, коли гроші стають доступні до виплати, зазвичай пізніше за created.',
      "È ciò che serve davvero alla riconciliazione. Un saldo dice cosa c'è ora e nulla su come ci è arrivato; ogni riga porta amount, fee e net. available_on è la data in cui il denaro diventa pagabile, di norma successiva a created.",
      'Es lo que la conciliación necesita. Un saldo dice qué hay ahora y nada de cómo llegó; cada fila lleva amount, fee y net. available_on es la fecha en que el dinero puede transferirse, normalmente posterior a created.',
    ),
    params: [
      {
        name: 'limit',
        type: 'integer',
        required: false,
        description: t('1 to 100. Defaults to 25.', 'От 1 до 100. По подразбиране 25.', '1 bis 100. Standard 25.', '1 à 100. Par défaut 25.', 'От 1 до 100. По умолчанию 25.', 'Від 1 до 100. За замовчуванням 25.', 'Da 1 a 100. Predefinito 25.', 'De 1 a 100. Por defecto 25.'),
      },
      {
        name: 'starting_after',
        type: 'string',
        required: false,
        description: t(
          'A balance transaction id from the previous page.',
          'Id на движение от предишната страница.',
          'Eine Buchungs-id von der vorigen Seite.',
          "Un id de mouvement de la page précédente.",
          'Id движения с предыдущей страницы.',
          'Id руху з попередньої сторінки.',
          'Un id di movimento dalla pagina precedente.',
          'Un id de movimiento de la página anterior.',
        ),
      },
      {
        name: 'type',
        type: 'string',
        required: false,
        description: t(
          'Narrow to one kind, e.g. charge, refund, payout, adjustment or stripe_fee.',
          'Стеснява до един вид, напр. charge, refund, payout, adjustment или stripe_fee.',
          'Auf eine Art einschränken, z. B. charge, refund, payout, adjustment oder stripe_fee.',
          'Restreindre à un type, par exemple charge, refund, payout, adjustment ou stripe_fee.',
          'Сузить до одного вида: charge, refund, payout, adjustment или stripe_fee.',
          'Звузити до одного виду: charge, refund, payout, adjustment або stripe_fee.',
          'Restringi a un tipo, ad esempio charge, refund, payout, adjustment o stripe_fee.',
          'Limitar a un tipo, por ejemplo charge, refund, payout, adjustment o stripe_fee.',
        ),
      },
      {
        name: 'payout',
        type: 'string',
        required: false,
        description: t(
          'Only the movements that made up one payout - the way to answer "what was in that bank deposit".',
          'Само движенията, съставили едно изплащане - начинът да отговорите „какво имаше в онзи банков превод".',
          'Nur die Bewegungen einer bestimmten Auszahlung - so beantworten Sie "was steckte in dieser Gutschrift".',
          "Seuls les mouvements composant un virement donné - de quoi répondre « qu'y avait-il dans ce dépôt ».",
          'Только движения, составившие одну выплату - так отвечают на «что было в том зачислении».',
          'Лише рухи, що склали одну виплату - так відповідають на «що було в тому зарахуванні».',
          'Solo i movimenti che compongono un bonifico - per rispondere a "cosa c\'era in quell\'accredito".',
          'Solo los movimientos que componen una transferencia - para responder «qué había en ese abono».',
        ),
      },
    ],
    request: `curl "https://api.wyocs.org/api/v1/balance/transactions?limit=2&type=charge" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "object": "list",
  "has_more": true,
  "data": [
    {
      "id": "txn_3TyH...",
      "amount": 4990,
      "net": 4761,
      "fee": 229,
      "currency": "usd",
      "type": "charge",
      "status": "available",
      "description": "Invoice INV-2026-0415",
      "created": "2026-07-29T10:22:07.000Z",
      "available_on": "2026-08-01T00:00:00.000Z"
    }
  ]
}`,
  },

  {
    id: 'get-payout',
    errors: ['not_found', 'not_onboarded', 'processor_error', 'not_configured'],
    method: 'GET',
    path: '/api/v1/payouts/{id}',
    group: 'balance',
    title: t('Retrieve a payout', 'Извличане на изплащане', 'Auszahlung abrufen', 'Récupérer un virement', 'Получить выплату', 'Отримати виплату', 'Recuperare un bonifico', 'Recuperar una transferencia'),
    summary: t(
      'One bank settlement, with its status, the date it should arrive and, if it bounced, why.',
      'Едно банково уреждане, със статуса му, датата на очаквано пристигане и, ако е върнато, защо.',
      'Eine Bankgutschrift mit Status, erwartetem Eingangsdatum und - falls fehlgeschlagen - dem Grund.',
      "Un règlement bancaire, avec son état, la date d'arrivée prévue et, en cas d'échec, la raison.",
      'Одно банковское зачисление: статус, ожидаемая дата поступления и, если не прошло, причина.',
      'Одне банківське зарахування: статус, очікувана дата надходження і, якщо не пройшло, причина.',
      'Un accredito bancario, con stato, data prevista di arrivo e, in caso di fallimento, il motivo.',
      'Una liquidación bancaria, con su estado, la fecha prevista de llegada y, si falló, por qué.',
    ),
    note: t(
      'A payout that failed carries failure_code and failure_message, and the money returns to your Stripe balance rather than vanishing - the usual cause is a bank detail that changed. Pair this with GET /api/v1/balance/transactions?payout={id} to see exactly which charges and refunds made up the amount.',
      'Неуспешно изплащане носи failure_code и failure_message, а парите се връщат в Stripe баланса ви, вместо да изчезнат - обичайната причина е променен банков реквизит. Комбинирайте с GET /api/v1/balance/transactions?payout={id}, за да видите точно кои плащания и рефунди са формирали сумата.',
      'Eine fehlgeschlagene Auszahlung trägt failure_code und failure_message; das Geld kehrt in Ihr Stripe-Guthaben zurück, statt zu verschwinden - meist wegen geänderter Bankdaten. Kombinieren Sie das mit GET /api/v1/balance/transactions?payout={id}.',
      "Un virement échoué porte failure_code et failure_message, et l'argent revient sur votre solde Stripe plutôt que de disparaître - la cause habituelle est un RIB modifié. À combiner avec GET /api/v1/balance/transactions?payout={id}.",
      'Неудачная выплата несёт failure_code и failure_message, а деньги возвращаются на баланс Stripe, а не исчезают - обычная причина в изменившихся банковских реквизитах. Сочетайте с GET /api/v1/balance/transactions?payout={id}.',
      'Невдала виплата несе failure_code і failure_message, а гроші повертаються на баланс Stripe, а не зникають - звична причина у змінених банківських реквізитах. Поєднуйте з GET /api/v1/balance/transactions?payout={id}.',
      'Un bonifico fallito riporta failure_code e failure_message e il denaro torna sul saldo Stripe anziché sparire. Da abbinare a GET /api/v1/balance/transactions?payout={id}.',
      'Una transferencia fallida lleva failure_code y failure_message, y el dinero vuelve a su saldo de Stripe en vez de desaparecer. Combínelo con GET /api/v1/balance/transactions?payout={id}.',
    ),
    request: `curl https://api.wyocs.org/api/v1/payouts/po_1TyH... \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "id": "po_1TyH...",
  "object": "payout",
  "amount": 128400,
  "currency": "usd",
  "status": "in_transit",
  "method": "standard",
  "description": "Weekly settlement",
  "statement_descriptor": null,
  "arrival_date": "2026-08-03T00:00:00.000Z",
  "created": "2026-08-01T09:00:00.000Z",
  "failure_code": null,
  "failure_message": null
}`,
  },

  {
    id: 'create-payout',
    errors: ['invalid_request', 'idempotency_key_required', 'idempotency_conflict', 'capability_required', 'not_onboarded', 'payouts_disabled', 'processor_error', 'not_configured'],
    method: 'POST',
    path: '/api/v1/payouts',
    group: 'balance',
    title: t('Create a payout', 'Създаване на изплащане', 'Auszahlung erstellen', 'Créer un virement', 'Создать выплату', 'Створити виплату', 'Creare un bonifico', 'Crear una transferencia'),
    summary: t(
      'Move settled balance to your bank, rather than waiting for the automatic schedule.',
      'Премества уредения баланс към банката ви, вместо да чакате автоматичния график.',
      'Überweist verfügbares Guthaben an Ihre Bank, statt den automatischen Zeitplan abzuwarten.',
      "Transfère le solde disponible vers votre banque, sans attendre le calendrier automatique.",
      'Переводит доступный баланс в ваш банк, не дожидаясь автоматического графика.',
      'Переказує доступний баланс у ваш банк, не чекаючи автоматичного графіка.',
      "Trasferisce il saldo disponibile sulla tua banca, senza attendere la pianificazione automatica.",
      'Mueve el saldo disponible a su banco, sin esperar al calendario automático.',
    ),
    note: t(
      'There is no destination parameter and there will not be. Where the money lands is a property of your Stripe account, set during verification, so this endpoint can make your money arrive sooner and cannot make it arrive anywhere else - which is the reason it is safe to expose to a key at all. An Idempotency-Key is required rather than optional here, and it is the only endpoint where that is true: two identical payout requests on the same day are a perfectly reasonable thing to want, so the key is the only thing that can tell a retry from a second instruction. method=instant needs the instant_payouts capability and carries a Stripe fee; standard does not.',
      'Няма параметър destination и няма да има. Къде отиват парите е свойство на вашия Stripe акаунт, зададено при верификацията, така че този адрес може да ускори пристигането им, но не може да ги прати другаде - именно затова е безопасно да се даде на ключ. Idempotency-Key тук е задължителен, а не по избор, и това е единственият такъв адрес: две еднакви заявки за изплащане в един ден са напълно разумно желание, така че ключът е единственото, което различава повторение от втора инструкция. method=instant изисква възможността instant_payouts и носи такса от Stripe; standard не.',
      'Es gibt keinen destination-Parameter und wird keinen geben. Wohin das Geld geht, ist eine Eigenschaft Ihres Stripe-Kontos aus der Verifizierung - dieser Endpunkt kann Geld also früher ankommen lassen, aber nirgendwo anders. Ein Idempotency-Key ist hier Pflicht: zwei identische Auszahlungswünsche am selben Tag sind legitim, nur der Schlüssel unterscheidet Wiederholung von zweiter Anweisung. method=instant erfordert die Freigabe instant_payouts und kostet eine Stripe-Gebühr.',
      "Il n'y a pas de paramètre destination et il n'y en aura pas. La destination est une propriété de votre compte Stripe fixée à la vérification : cet endpoint peut faire arriver votre argent plus tôt, jamais ailleurs. La clé d'idempotence est obligatoire ici, seul cas sur cette API : deux demandes de virement identiques le même jour sont légitimes, seule la clé distingue une reprise d'une seconde instruction. method=instant exige la capacité instant_payouts et coûte des frais Stripe.",
      'Параметра destination нет и не будет. Куда уходят деньги - свойство вашего аккаунта Stripe, заданное при верификации: этот эндпоинт может ускорить поступление, но не может отправить их куда-то ещё. Idempotency-Key здесь обязателен, и это единственный такой эндпоинт: две одинаковые заявки на выплату в один день - вполне разумное желание, и только ключ отличает повтор от второй инструкции. method=instant требует возможности instant_payouts и облагается комиссией Stripe.',
      'Параметра destination немає і не буде. Куди йдуть гроші - властивість вашого акаунта Stripe, задана при верифікації: цей ендпоінт може пришвидшити надходження, але не може надіслати їх кудись іще. Idempotency-Key тут обов’язковий, і це єдиний такий ендпоінт: два однакові запити на виплату в один день - цілком розумне бажання, і лише ключ відрізняє повтор від другої інструкції. method=instant вимагає можливості instant_payouts і має комісію Stripe.',
      "Non c'è un parametro destination e non ci sarà. La destinazione è una proprietà del tuo account Stripe fissata in verifica: questo endpoint può far arrivare prima il denaro, mai altrove. L'Idempotency-Key qui è obbligatoria, unico caso: due richieste identiche nello stesso giorno sono legittime e solo la chiave distingue un nuovo tentativo da una seconda istruzione. method=instant richiede la capacità instant_payouts.",
      'No hay parámetro destination ni lo habrá. El destino es una propiedad de su cuenta de Stripe fijada en la verificación: este endpoint puede adelantar la llegada, nunca desviarla. La clave de idempotencia es obligatoria aquí, único caso: dos peticiones idénticas el mismo día son legítimas y solo la clave distingue un reintento de una segunda instrucción. method=instant exige la capacidad instant_payouts.',
    ),
    params: [
      {
        name: 'amount',
        type: 'integer',
        required: true,
        description: t(
          'Minor units, and no more than the available balance.',
          'В най-малката единица и не повече от наличния баланс.',
          'Kleinste Einheit, höchstens das verfügbare Guthaben.',
          'Unités mineures, dans la limite du solde disponible.',
          'В минимальных единицах, не больше доступного баланса.',
          'У мінімальних одиницях, не більше доступного балансу.',
          'Unità minori, non oltre il saldo disponibile.',
          'Unidades menores, sin exceder el saldo disponible.',
        ),
      },
      {
        name: 'currency',
        type: 'string',
        required: true,
        description: t(
          'Three-letter ISO code. Must be a currency your balance actually holds.',
          'Трибуквен ISO код. Трябва да е валута, която балансът ви наистина държи.',
          'Dreibuchstabiger ISO-Code. Muss eine Währung sein, die Ihr Guthaben tatsächlich führt.',
          'Code ISO à trois lettres. Doit être une devise réellement présente sur votre solde.',
          'Трёхбуквенный код ISO. Должна быть валюта, реально имеющаяся на балансе.',
          'Трилітерний код ISO. Має бути валюта, що реально є на балансі.',
          'Codice ISO di tre lettere. Deve essere una valuta effettivamente presente nel saldo.',
          'Código ISO de tres letras. Debe ser una moneda que su saldo realmente tenga.',
        ),
      },
      {
        name: 'method',
        type: 'string',
        required: false,
        description: t(
          '"standard" or "instant". Defaults to standard.',
          '„standard" или „instant". По подразбиране standard.',
          '"standard" oder "instant". Standard: standard.',
          '"standard" ou "instant". Par défaut standard.',
          '«standard» или «instant». По умолчанию standard.',
          '«standard» або «instant». За замовчуванням standard.',
          '"standard" o "instant". Predefinito standard.',
          '"standard" o "instant". Por defecto standard.',
        ),
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: t(
          'For your own records. Not shown to anybody else.',
          'За вашите записи. Не се показва на никого другиго.',
          'Für Ihre eigenen Aufzeichnungen. Wird niemandem sonst gezeigt.',
          'Pour vos propres registres. Non visible par des tiers.',
          'Для ваших записей. Никому больше не показывается.',
          'Для ваших записів. Нікому більше не показується.',
          'Per i tuoi registri. Non mostrato ad altri.',
          'Para sus registros. No se muestra a nadie más.',
        ),
      },
      {
        name: 'statement_descriptor',
        type: 'string',
        required: false,
        description: t(
          'Up to 22 characters, shown on your own bank statement.',
          'До 22 знака, показва се в собственото ви банково извлечение.',
          'Bis zu 22 Zeichen, erscheint auf Ihrem Kontoauszug.',
          'Jusqu’à 22 caractères, affichés sur votre relevé bancaire.',
          'До 22 символов, отображается в вашей банковской выписке.',
          'До 22 символів, відображається у вашій банківській виписці.',
          'Fino a 22 caratteri, mostrati sul tuo estratto conto.',
          'Hasta 22 caracteres, visibles en su extracto bancario.',
        ),
      },
    ],
    request: `curl -X POST https://api.wyocs.org/api/v1/payouts \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Idempotency-Key: YOUR_IDEMPOTENCY_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"amount": 128400, "currency": "usd", "description": "Weekly settlement"}'`,
    response: `{
  "id": "po_1TyH...",
  "object": "payout",
  "amount": 128400,
  "currency": "usd",
  "status": "pending",
  "method": "standard",
  "description": "Weekly settlement",
  "statement_descriptor": null,
  "arrival_date": "2026-08-03T00:00:00.000Z",
  "created": "2026-08-01T09:00:00.000Z",
  "failure_code": null,
  "failure_message": null
}`,
  },

  // -------------------------------------------------------------------------
  // Customers
  // -------------------------------------------------------------------------

  {
    id: 'list-customers',
    errors: ['invalid_request', 'invalid_cursor'],
    method: 'GET',
    path: '/api/v1/customers',
    group: 'customers',
    title: t('List customers', 'Списък с клиенти', 'Kunden auflisten', 'Lister les clients', 'Список клиентов', 'Список клієнтів', 'Elenca i clienti', 'Listar clientes'),
    summary: t(
      'Your own customer list, newest first, optionally narrowed by a search across name, email and id.',
      'Вашият списък с клиенти, най-новите отпред, по избор стеснен с търсене по име, имейл и id.',
      'Ihre Kundenliste, neueste zuerst, optional per Suche über Name, E-Mail und id eingegrenzt.',
      "Votre liste de clients, du plus récent au plus ancien, avec une recherche facultative sur le nom, l'e-mail et l'id.",
      'Ваш список клиентов, новые сверху, с необязательным поиском по имени, адресу и id.',
      'Ваш список клієнтів, нові згори, з необов’язковим пошуком за іменем, адресою та id.',
      'Il tuo elenco clienti, dal più recente, con ricerca facoltativa su nome, email e id.',
      'Su lista de clientes, del más reciente, con búsqueda opcional por nombre, correo e id.',
    ),
    note: t(
      'A customer here is a name and an email address, nothing more. There is no card, no expiry and no saved payment method anywhere in this object or the table behind it, and none will be added: the platform holds a PCI DSS v4.0.1 SAQ A attestation whose whole premise is that card details go from the payer\'s browser to Stripe and never reach these servers. Storing a token that could be charged without the cardholder present would make that statement untrue. This is an address book, so that a run of payments can be grouped under one person.',
      'Клиентът тук е име и имейл, нищо повече. Няма карта, няма срок на валидност и няма запазен метод за плащане нито в този обект, нито в таблицата зад него, и няма да бъдат добавени: платформата държи атестация PCI DSS v4.0.1 SAQ A, чиято цялата предпоставка е, че данните на картата отиват от браузъра на платеца към Stripe и никога не достигат тези сървъри. Това е адресна книга, за да могат няколко плащания да се групират под един човек.',
      'Ein Kunde ist hier ein Name und eine E-Mail-Adresse, mehr nicht. Weder in diesem Objekt noch in der Tabelle dahinter gibt es Karte, Ablaufdatum oder gespeicherte Zahlungsmethode, und es wird auch keine geben: die Plattform hält eine PCI-DSS-v4.0.1-SAQ-A-Bescheinigung, deren Prämisse ist, dass Kartendaten vom Browser zu Stripe gehen und diese Server nie erreichen. Das hier ist ein Adressbuch.',
      "Un client, ici, c'est un nom et une adresse e-mail, rien de plus. Ni carte, ni date d'expiration, ni moyen de paiement enregistré dans cet objet ou la table derrière, et il n'y en aura pas : la plateforme détient une attestation PCI DSS v4.0.1 SAQ A dont la prémisse est que les données de carte vont du navigateur du payeur à Stripe sans jamais atteindre ces serveurs. C'est un carnet d'adresses.",
      'Клиент здесь - это имя и адрес почты, не более. Ни карты, ни срока действия, ни сохранённого способа оплаты ни в этом объекте, ни в таблице за ним, и не появится: платформа держит аттестацию PCI DSS v4.0.1 SAQ A, вся посылка которой в том, что данные карты идут из браузера плательщика в Stripe и никогда не достигают этих серверов. Это адресная книга.',
      'Клієнт тут - це ім’я та адреса пошти, не більше. Ні картки, ні терміну дії, ні збереженого способу оплати ні в цьому об’єкті, ні в таблиці за ним, і не з’явиться: платформа тримає атестацію PCI DSS v4.0.1 SAQ A, уся посилка якої в тому, що дані картки йдуть із браузера платника в Stripe і ніколи не досягають цих серверів. Це адресна книга.',
      "Un cliente qui è un nome e un'email, nulla di più. Nessuna carta, nessuna scadenza, nessun metodo di pagamento salvato in questo oggetto né nella tabella sottostante: la piattaforma detiene un'attestazione PCI DSS v4.0.1 SAQ A la cui premessa è che i dati di carta vanno dal browser a Stripe e non toccano mai questi server. È una rubrica.",
      'Un cliente aquí es un nombre y un correo, nada más. Ni tarjeta, ni caducidad, ni método de pago guardado en este objeto ni en la tabla que hay detrás: la plataforma tiene una atestación PCI DSS v4.0.1 SAQ A cuya premisa es que los datos de tarjeta van del navegador del pagador a Stripe y nunca llegan a estos servidores. Esto es una agenda.',
    ),
    params: [
      {
        name: 'limit',
        type: 'integer',
        required: false,
        description: t('1 to 100. Defaults to 25.', 'От 1 до 100. По подразбиране 25.', '1 bis 100. Standard 25.', '1 à 100. Par défaut 25.', 'От 1 до 100. По умолчанию 25.', 'Від 1 до 100. За замовчуванням 25.', 'Da 1 a 100. Predefinito 25.', 'De 1 a 100. Por defecto 25.'),
      },
      {
        name: 'starting_after',
        type: 'string',
        required: false,
        description: t(
          'A customer id of yours from the previous page. An id you do not own is refused rather than silently ignored.',
          'Ваш клиентски id от предишната страница. Чужд id се отказва, а не се игнорира мълчаливо.',
          'Eine eigene Kunden-id von der vorigen Seite. Eine fremde id wird abgelehnt statt still ignoriert.',
          "Un id client vous appartenant, issu de la page précédente. Un id qui n'est pas le vôtre est refusé, pas ignoré.",
          'Ваш id клиента с предыдущей страницы. Чужой id отклоняется, а не игнорируется молча.',
          'Ваш id клієнта з попередньої сторінки. Чужий id відхиляється, а не ігнорується мовчки.',
          'Un tuo id cliente dalla pagina precedente. Un id non tuo viene rifiutato, non ignorato.',
          'Un id de cliente suyo de la página anterior. Un id ajeno se rechaza, no se ignora.',
        ),
      },
      {
        name: 'search',
        type: 'string',
        required: false,
        description: t(
          'Matches part of a name, an email address or an id.',
          'Съвпада с част от име, имейл или id.',
          'Trifft auf Teile von Name, E-Mail oder id zu.',
          "Correspond à une partie d'un nom, d'un e-mail ou d'un id.",
          'Совпадает с частью имени, адреса или id.',
          'Збігається з частиною імені, адреси або id.',
          'Corrisponde a parte di un nome, un’email o un id.',
          'Coincide con parte de un nombre, un correo o un id.',
        ),
      },
    ],
    request: `curl "https://api.wyocs.org/api/v1/customers?search=ana" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "object": "list",
  "has_more": false,
  "data": [
    {
      "id": "cus_9FJ2...",
      "object": "customer",
      "email": "ana@example.com",
      "name": "Ana Petrova",
      "created_at": "2026-07-14T08:31:00.512Z"
    }
  ]
}`,
  },

  {
    id: 'create-customer',
    errors: ['invalid_request', 'invalid_customer', 'customer_exists', 'idempotency_conflict'],
    method: 'POST',
    path: '/api/v1/customers',
    group: 'customers',
    title: t('Create a customer', 'Създаване на клиент', 'Kunden anlegen', 'Créer un client', 'Создать клиента', 'Створити клієнта', 'Creare un cliente', 'Crear un cliente'),
    summary: t(
      'Add somebody to your customer list so that later payments can be grouped under them.',
      'Добавя човек във вашия списък с клиенти, за да могат по-късни плащания да се групират под него.',
      'Fügt jemanden Ihrer Kundenliste hinzu, damit spätere Zahlungen darunter gruppiert werden können.',
      'Ajoute une personne à votre liste de clients pour y rattacher les paiements à venir.',
      'Добавляет человека в ваш список клиентов, чтобы будущие платежи группировались под ним.',
      'Додає людину до вашого списку клієнтів, щоб майбутні платежі групувалися під нею.',
      'Aggiunge una persona al tuo elenco clienti, così i pagamenti futuri possono esservi raggruppati.',
      'Añade a alguien a su lista de clientes para agrupar bajo él los pagos posteriores.',
    ),
    note: t(
      'Usually you do not need this. Sending customer_email when you create a payment resolves the customer for you, creating the record on first sight and reusing it afterwards, which is the path most integrations want. Create one directly when you are importing an existing list, or when you want the record to exist before the first payment does. Email addresses are folded to lowercase before they are stored and compared, so "Ana@Example.com" and "ana@example.com" are one person rather than two. An address is unique within your account only - another merchant may have the same customer, and neither of you can see the other\'s record.',
      'Обикновено не ви трябва. Изпращането на customer_email при създаване на плащане разрешава клиента вместо вас, като създава записа при първа поява и после го преизползва - това е пътят, който повечето интеграции искат. Създавайте директно, когато внасяте съществуващ списък или когато искате записът да съществува преди първото плащане. Имейлите се свеждат до малки букви преди запис и сравнение, така че „Ana@Example.com" и „ana@example.com" са един човек, а не двама. Адресът е уникален само в рамките на вашия профил.',
      'Meist brauchen Sie das nicht: customer_email beim Anlegen einer Zahlung löst den Kunden auf, legt den Datensatz beim ersten Mal an und nutzt ihn danach wieder. Direkt anlegen lohnt beim Import einer bestehenden Liste. E-Mail-Adressen werden vor dem Speichern kleingeschrieben, "Ana@Example.com" und "ana@example.com" sind also eine Person. Eindeutig ist eine Adresse nur innerhalb Ihres Kontos.',
      "En général, inutile : envoyer customer_email à la création d'un paiement résout le client, crée l'enregistrement au premier passage puis le réutilise. Créez directement pour importer une liste existante. Les adresses sont mises en minuscules avant stockage et comparaison, si bien que « Ana@Example.com » et « ana@example.com » sont une seule personne. Une adresse n'est unique qu'au sein de votre compte.",
      'Обычно это не нужно: передача customer_email при создании платежа сама находит клиента, создаёт запись при первой встрече и переиспользует потом. Создавайте напрямую при импорте готового списка. Адреса приводятся к нижнему регистру до записи и сравнения, поэтому «Ana@Example.com» и «ana@example.com» - один человек. Адрес уникален только в пределах вашего аккаунта.',
      'Зазвичай це не потрібно: передача customer_email при створенні платежу сама знаходить клієнта, створює запис при першій зустрічі і перевикористовує потім. Створюйте напряму при імпорті готового списку. Адреси зводяться до нижнього регістру до запису та порівняння, тож «Ana@Example.com» і «ana@example.com» - одна людина. Адреса унікальна лише в межах вашого облікового запису.',
      "Di solito non serve: inviare customer_email alla creazione di un pagamento risolve il cliente, creando il record la prima volta e riusandolo poi. Crealo direttamente per importare un elenco esistente. Le email sono convertite in minuscolo prima di essere salvate e confrontate. Un indirizzo è univoco solo all'interno del tuo account.",
      'Normalmente no hace falta: enviar customer_email al crear un pago resuelve el cliente, creando el registro la primera vez y reutilizándolo después. Créelo directamente para importar una lista existente. Los correos se pasan a minúsculas antes de guardarse y compararse. Una dirección es única solo dentro de su cuenta.',
    ),
    params: [
      {
        name: 'email',
        type: 'string',
        required: false,
        description: t(
          'Required unless a name is given. Folded to lowercase and unique within your account.',
          'Задължителен, освен ако не е подадено име. Свежда се до малки букви и е уникален в профила ви.',
          'Pflicht, sofern kein Name angegeben ist. Wird kleingeschrieben und ist in Ihrem Konto eindeutig.',
          "Requis sauf si un nom est fourni. Mis en minuscules et unique dans votre compte.",
          'Обязателен, если не указано имя. Приводится к нижнему регистру, уникален в аккаунте.',
          'Обов’язковий, якщо не вказано ім’я. Зводиться до нижнього регістру, унікальний в обліковому записі.',
          'Obbligatoria salvo che sia indicato un nome. Convertita in minuscolo e univoca nel tuo account.',
          'Obligatorio salvo que se dé un nombre. Se pasa a minúsculas y es único en su cuenta.',
        ),
      },
      {
        name: 'name',
        type: 'string',
        required: false,
        description: t(
          'Required unless an email is given. A record with neither identifies nobody.',
          'Задължително, освен ако не е подаден имейл. Запис без нито едно от двете не идентифицира никого.',
          'Pflicht, sofern keine E-Mail angegeben ist. Ein Datensatz ohne beides identifiziert niemanden.',
          "Requis sauf si un e-mail est fourni. Un enregistrement sans l'un ni l'autre n'identifie personne.",
          'Обязательно, если не указан адрес. Запись без обоих никого не идентифицирует.',
          'Обов’язкове, якщо не вказано адресу. Запис без обох нікого не ідентифікує.',
          'Obbligatorio salvo che sia indicata un’email. Un record senza nessuno dei due non identifica nessuno.',
          'Obligatorio salvo que se dé un correo. Un registro sin ninguno no identifica a nadie.',
        ),
      },
    ],
    request: `curl -X POST https://api.wyocs.org/api/v1/customers \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Idempotency-Key: YOUR_IDEMPOTENCY_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"email": "ana@example.com", "name": "Ana Petrova"}'`,
    response: `{
  "id": "cus_9FJ2...",
  "object": "customer",
  "email": "ana@example.com",
  "name": "Ana Petrova",
  "created_at": "2026-07-14T08:31:00.512Z"
}`,
  },

  {
    id: 'get-customer',
    errors: ['not_found'],
    method: 'GET',
    path: '/api/v1/customers/{id}',
    group: 'customers',
    title: t('Retrieve a customer', 'Извличане на клиент', 'Kunden abrufen', 'Récupérer un client', 'Получить клиента', 'Отримати клієнта', 'Recuperare un cliente', 'Recuperar un cliente'),
    summary: t(
      'One customer of yours by id.',
      'Един ваш клиент по id.',
      'Einen Ihrer Kunden anhand der id.',
      'Un de vos clients par son id.',
      'Один ваш клиент по id.',
      'Один ваш клієнт за id.',
      'Un tuo cliente tramite id.',
      'Un cliente suyo por id.',
    ),
    request: `curl https://api.wyocs.org/api/v1/customers/cus_9FJ2... \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "id": "cus_9FJ2...",
  "object": "customer",
  "email": "ana@example.com",
  "name": "Ana Petrova",
  "created_at": "2026-07-14T08:31:00.512Z"
}`,
  },

  {
    id: 'update-customer',
    errors: ['invalid_request', 'no_changes', 'not_found', 'customer_exists'],
    method: 'PATCH',
    path: '/api/v1/customers/{id}',
    group: 'customers',
    title: t('Update a customer', 'Промяна на клиент', 'Kunden ändern', 'Modifier un client', 'Изменить клиента', 'Змінити клієнта', 'Modificare un cliente', 'Actualizar un cliente'),
    summary: t(
      'Change a customer’s name or email address. Only the fields you send are touched.',
      'Променя името или имейла на клиент. Засягат се само подадените полета.',
      'Ändert Name oder E-Mail eines Kunden. Nur die gesendeten Felder werden berührt.',
      "Modifie le nom ou l'e-mail d'un client. Seuls les champs envoyés sont touchés.",
      'Меняет имя или адрес клиента. Затрагиваются только переданные поля.',
      'Змінює ім’я або адресу клієнта. Зачіпаються лише передані поля.',
      "Modifica nome o email di un cliente. Solo i campi inviati vengono toccati.",
      'Cambia el nombre o el correo de un cliente. Solo se tocan los campos enviados.',
    ),
    note: t(
      'Send null to clear a field rather than omitting it - omitting means "leave alone". Payments already attached to this customer stay attached; changing an address does not move anybody\'s history. Moving an address onto a customer when another of your customers already holds it is a conflict, and the response names the id that has it.',
      'Изпратете null, за да изчистите поле, вместо да го пропуснете - пропускането значи „не пипай". Плащанията, вече свързани с този клиент, си остават; смяната на адрес не мести ничия история. Прехвърлянето на адрес към клиент, когато друг ваш клиент вече го държи, е конфликт и отговорът посочва id-то, което го има.',
      'Senden Sie null, um ein Feld zu leeren - Weglassen bedeutet "unverändert". Bereits zugeordnete Zahlungen bleiben zugeordnet. Eine Adresse, die schon ein anderer Ihrer Kunden trägt, führt zum Konflikt; die Antwort nennt die betreffende id.',
      "Envoyez null pour vider un champ ; l'omettre signifie « ne pas toucher ». Les paiements déjà rattachés le restent. Une adresse déjà détenue par un autre de vos clients provoque un conflit, et la réponse nomme l'id concerné.",
      'Отправьте null, чтобы очистить поле; пропуск означает «не трогать». Уже привязанные платежи остаются привязанными. Адрес, который уже держит другой ваш клиент, вызывает конфликт, и ответ называет этот id.',
      'Надішліть null, щоб очистити поле; пропуск означає «не чіпати». Уже прив’язані платежі лишаються прив’язаними. Адреса, яку вже тримає інший ваш клієнт, спричиняє конфлікт, і відповідь називає цей id.',
      "Invia null per svuotare un campo; ometterlo significa \"non toccare\". I pagamenti già collegati restano collegati. Un indirizzo già in uso da un altro tuo cliente genera un conflitto e la risposta indica l'id.",
      'Envíe null para vaciar un campo; omitirlo significa «no tocar». Los pagos ya vinculados siguen vinculados. Una dirección que ya tiene otro cliente suyo provoca conflicto y la respuesta indica ese id.',
    ),
    request: `curl -X PATCH https://api.wyocs.org/api/v1/customers/cus_9FJ2... \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Ana Petrova-Ilieva"}'`,
    response: `{
  "id": "cus_9FJ2...",
  "object": "customer",
  "email": "ana@example.com",
  "name": "Ana Petrova-Ilieva",
  "created_at": "2026-07-14T08:31:00.512Z"
}`,
  },

  {
    id: 'customer-payments',
    errors: ['invalid_request', 'not_found'],
    method: 'GET',
    path: '/api/v1/customers/{id}/payments',
    group: 'customers',
    title: t('A customer’s payments', 'Плащания на клиент', 'Zahlungen eines Kunden', 'Paiements d’un client', 'Платежи клиента', 'Платежі клієнта', 'Pagamenti di un cliente', 'Pagos de un cliente'),
    summary: t(
      'Everything this customer has ever been asked to pay, newest first.',
      'Всичко, което този клиент някога е бил помолен да плати, най-новото отпред.',
      'Alles, worum dieser Kunde je gebeten wurde, neueste zuerst.',
      'Tout ce que ce client a été invité à payer, du plus récent au plus ancien.',
      'Всё, что этому клиенту когда-либо предлагали оплатить, новое сверху.',
      'Усе, що цьому клієнту коли-небудь пропонували оплатити, нове згори.',
      'Tutto ciò che a questo cliente è stato chiesto di pagare, dal più recente.',
      'Todo lo que se ha pedido pagar a este cliente, del más reciente.',
    ),
    note: t(
      'The same as GET /api/v1/payments?customer_id={id}, and the customer is checked first, so an id belonging to another merchant is refused before any payment is read rather than quietly returning an empty list.',
      'Същото като GET /api/v1/payments?customer_id={id}, като клиентът се проверява първо - чужд id се отказва, преди да е прочетено плащане, вместо тихо да се върне празен списък.',
      'Dasselbe wie GET /api/v1/payments?customer_id={id}; der Kunde wird zuerst geprüft, eine fremde id also abgelehnt, bevor eine Zahlung gelesen wird.',
      "Identique à GET /api/v1/payments?customer_id={id}, le client étant vérifié d'abord : un id d'un autre marchand est refusé avant toute lecture de paiement.",
      'То же, что GET /api/v1/payments?customer_id={id}, но клиент проверяется первым: чужой id отклоняется до чтения платежей.',
      'Те саме, що GET /api/v1/payments?customer_id={id}, але клієнта перевіряють першим: чужий id відхиляється до читання платежів.',
      "Come GET /api/v1/payments?customer_id={id}, con il cliente verificato per primo: un id altrui è rifiutato prima di leggere qualsiasi pagamento.",
      'Igual que GET /api/v1/payments?customer_id={id}, con el cliente comprobado primero: un id ajeno se rechaza antes de leer ningún pago.',
    ),
    request: `curl https://api.wyocs.org/api/v1/customers/cus_9FJ2.../payments \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "object": "list",
  "data": [
    {
      "id": "pay_...",
      "object": "payment",
      "amount": 4990,
      "currency": "usd",
      "status": "SUCCEEDED",
      "customer_id": "cus_9FJ2...",
      "merchant_reference": "INV-2026-0415",
      "created_at": "2026-07-29T10:14:02.744Z"
    }
  ]
}`,
  },

  // -------------------------------------------------------------------------
  // Webhook endpoints and deliveries
  // -------------------------------------------------------------------------

  {
    id: 'list-endpoints',
    method: 'GET',
    path: '/api/v1/webhooks/endpoints',
    group: 'webhooks',
    title: t('List webhook endpoints', 'Списък с webhook адреси', 'Webhook-Endpunkte auflisten', 'Lister les endpoints webhook', 'Список webhook-адресов', 'Список webhook-адрес', 'Elenca gli endpoint webhook', 'Listar endpoints de webhook'),
    summary: t(
      'The endpoints events are delivered to, with how many signing secrets each currently has and whether a rotation is part-way through.',
      'Адресите, към които се доставят събития, с това колко подписващи ключа има всеки и дали има недовършена ротация.',
      'Die Endpunkte, an die Ereignisse gehen, mit der Zahl aktueller Signaturschlüssel und laufender Rotation.',
      "Les endpoints où les événements sont livrés, avec le nombre de secrets de signature actifs et l'état d'une rotation en cours.",
      'Адреса, куда доставляются события, с числом действующих подписывающих секретов и признаком незавершённой ротации.',
      'Адреси, куди доставляються події, з числом чинних підписувальних секретів і ознакою незавершеної ротації.',
      'Gli endpoint a cui vengono consegnati gli eventi, con il numero di segreti di firma attivi e una rotazione in corso.',
      'Los endpoints a los que se entregan eventos, con cuántos secretos de firma tiene cada uno y si hay una rotación en curso.',
    ),
    note: t(
      'active_secrets above one means a rotation is in flight and both secrets are signing every payload. Verify against either and you will not miss an event during the overlap. An empty enabled_events means every event type, which is what a new endpoint gets.',
      'active_secrets над едно значи, че тече ротация и и двата ключа подписват всяко тяло. Проверявайте срещу който и да е и няма да пропуснете събитие по време на застъпването. Празен enabled_events значи всички типове събития - това получава новият адрес.',
      'active_secrets über eins bedeutet eine laufende Rotation, bei der beide Schlüssel jede Nutzlast signieren. Prüfen Sie gegen beide, dann verpassen Sie in der Überlappung kein Ereignis. Leeres enabled_events heißt: alle Ereignistypen.',
      "active_secrets supérieur à un signifie qu'une rotation est en cours et que les deux secrets signent chaque contenu. Vérifiez contre l'un ou l'autre et vous ne manquerez rien pendant le chevauchement. Un enabled_events vide signifie tous les types.",
      'active_secrets больше единицы означает идущую ротацию, когда оба секрета подписывают каждое тело. Проверяйте против любого - в период перекрытия ничего не пропустите. Пустой enabled_events означает все типы событий.',
      'active_secrets більше одиниці означає ротацію, коли обидва секрети підписують кожне тіло. Перевіряйте проти будь-якого - у період перекриття нічого не пропустите. Порожній enabled_events означає всі типи подій.',
      'active_secrets maggiore di uno indica una rotazione in corso: entrambi i segreti firmano ogni payload. Un enabled_events vuoto significa tutti i tipi di evento.',
      'active_secrets mayor que uno indica una rotación en curso: ambos secretos firman cada contenido. Un enabled_events vacío significa todos los tipos de evento.',
    ),
    request: `curl https://api.wyocs.org/api/v1/webhooks/endpoints \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "object": "list",
  "data": [
    {
      "id": "whe_4KP8...",
      "object": "webhook_endpoint",
      "url": "https://hooks.example.com/wyocspay",
      "description": "Production ledger",
      "status": "ACTIVE",
      "enabled_events": [],
      "created_at": "2026-06-02T14:00:11.004Z",
      "active_secrets": 1,
      "rotation_in_progress": false
    }
  ]
}`,
  },

  {
    id: 'create-endpoint',
    errors: ['invalid_request', 'invalid_url', 'insecure_url', 'capability_required', 'idempotency_conflict'],
    method: 'POST',
    path: '/api/v1/webhooks/endpoints',
    group: 'webhooks',
    title: t('Register an endpoint', 'Регистриране на адрес', 'Endpunkt registrieren', 'Enregistrer un endpoint', 'Зарегистрировать адрес', 'Зареєструвати адресу', 'Registrare un endpoint', 'Registrar un endpoint'),
    summary: t(
      'Tell the platform where to POST your events, and receive the signing secret.',
      'Казва на платформата къде да праща вашите събития и връща подписващия ключ.',
      'Teilt der Plattform mit, wohin Ihre Ereignisse gehen, und liefert den Signaturschlüssel.',
      'Indique à la plateforme où envoyer vos événements et renvoie le secret de signature.',
      'Сообщает платформе, куда слать ваши события, и возвращает подписывающий секрет.',
      'Повідомляє платформі, куди слати ваші події, і повертає підписувальний секрет.',
      'Indica alla piattaforma dove inviare i tuoi eventi e restituisce il segreto di firma.',
      'Indica a la plataforma dónde enviar sus eventos y devuelve el secreto de firma.',
    ),
    note: t(
      'The secret is returned once, in this response, and never again - only a rotation produces another. Store it before you do anything else. HTTPS is required: a signed payload over plaintext HTTP is still readable by everyone on the path, and the signature only proves that nobody changed it. This endpoint needs the webhooks capability, which is approval-gated because the platform will be sending payment data to a URL and wants to know it is yours rather than a shared or third-party address.',
      'Ключът се връща веднъж, в този отговор, и никога повече - само ротация ражда нов. Запазете го, преди да правите каквото и да е друго. HTTPS е задължителен: подписано тяло по обикновен HTTP пак се чете от всички по пътя, а подписът доказва само че не е променяно. Този адрес изисква възможността webhooks, която минава през одобрение, защото платформата ще праща данни за плащания към URL и иска да знае, че е ваш, а не споделен или на трета страна.',
      'Der Schlüssel erscheint genau einmal, in dieser Antwort - nur eine Rotation erzeugt einen neuen. Speichern Sie ihn zuerst. HTTPS ist Pflicht: eine signierte Nutzlast über HTTP bleibt für alle auf dem Weg lesbar, die Signatur belegt nur Unverändertheit. Der Endpunkt erfordert die Freigabe webhooks, die geprüft wird, weil Zahlungsdaten an eine URL gehen, die Ihnen gehören muss.',
      "Le secret n'est renvoyé qu'une fois, ici, et jamais ensuite : seule une rotation en produit un autre. Stockez-le avant toute chose. HTTPS est obligatoire : un contenu signé en HTTP reste lisible par tous sur le chemin. Cet endpoint requiert la capacité webhooks, soumise à approbation car la plateforme enverra des données de paiement vers une URL qui doit être la vôtre.",
      'Секрет возвращается один раз, в этом ответе, и больше никогда - новый даёт только ротация. Сохраните его прежде всего. HTTPS обязателен: подписанное тело по обычному HTTP по-прежнему читаемо всеми на пути. Эндпоинт требует возможности webhooks, проходящей одобрение, потому что платформа будет слать платёжные данные на URL, который должен быть вашим.',
      'Секрет повертається один раз, у цій відповіді, і більше ніколи - новий дає лише ротація. Збережіть його насамперед. HTTPS обов’язковий: підписане тіло по звичайному HTTP усе одно читається всіма на шляху. Ендпоінт вимагає можливості webhooks, що проходить схвалення, бо платформа слатиме платіжні дані на URL, який має бути вашим.',
      "Il segreto viene restituito una sola volta, qui, e mai più: solo una rotazione ne produce un altro. Salvalo subito. HTTPS è obbligatorio. L'endpoint richiede la capacità webhooks, soggetta ad approvazione perché la piattaforma invierà dati di pagamento a un URL che deve essere tuo.",
      'El secreto se devuelve una sola vez, aquí, y nunca más: solo una rotación produce otro. Guárdelo antes que nada. HTTPS es obligatorio. Este endpoint requiere la capacidad webhooks, sujeta a aprobación porque la plataforma enviará datos de pago a una URL que debe ser suya.',
    ),
    params: [
      {
        name: 'url',
        type: 'string',
        required: true,
        description: t(
          'Must be https. Localhost is allowed for development.',
          'Трябва да е https. Localhost се допуска за разработка.',
          'Muss https sein. Localhost ist für die Entwicklung erlaubt.',
          'Doit être en https. Localhost est admis en développement.',
          'Должен быть https. Localhost допускается для разработки.',
          'Має бути https. Localhost дозволено для розробки.',
          'Deve essere https. Localhost è ammesso in sviluppo.',
          'Debe ser https. Localhost se admite en desarrollo.',
        ),
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: t(
          'For your own use when you have several.',
          'За ваше удобство, когато имате няколко.',
          'Für Ihre eigene Übersicht bei mehreren Endpunkten.',
          'Pour votre usage quand vous en avez plusieurs.',
          'Для вашего удобства, когда их несколько.',
          'Для вашої зручності, коли їх кілька.',
          'Per tuo uso quando ne hai diversi.',
          'Para su uso cuando tenga varios.',
        ),
      },
      {
        name: 'enabled_events',
        type: 'string[]',
        required: false,
        description: t(
          'Event types to receive. Omit or send an empty array for all of them.',
          'Типове събития за получаване. Пропуснете или подайте празен масив за всички.',
          'Zu empfangende Ereignistypen. Weglassen oder leeres Array für alle.',
          "Types d'événements à recevoir. Omettre ou envoyer un tableau vide pour tous.",
          'Типы событий для получения. Пропустите или отправьте пустой массив для всех.',
          'Типи подій для отримання. Пропустіть або надішліть порожній масив для всіх.',
          'Tipi di evento da ricevere. Ometti o invia un array vuoto per tutti.',
          'Tipos de evento a recibir. Omita o envíe un arreglo vacío para todos.',
        ),
      },
    ],
    request: `curl -X POST https://api.wyocs.org/api/v1/webhooks/endpoints \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
        "url": "https://hooks.example.com/wyocspay",
        "description": "Production ledger",
        "enabled_events": ["payment.succeeded", "payment.refunded"]
      }'`,
    response: `{
  "id": "whe_4KP8...",
  "object": "webhook_endpoint",
  "url": "https://hooks.example.com/wyocspay",
  "secret": "whsec_..."
}`,
  },

  {
    id: 'get-endpoint',
    errors: ['not_found'],
    method: 'GET',
    path: '/api/v1/webhooks/endpoints/{id}',
    group: 'webhooks',
    title: t('Retrieve an endpoint', 'Извличане на адрес', 'Endpunkt abrufen', 'Récupérer un endpoint', 'Получить адрес', 'Отримати адресу', 'Recuperare un endpoint', 'Recuperar un endpoint'),
    summary: t(
      'One endpoint, with its current URL, event selection, status and secret count.',
      'Един адрес, с текущия си URL, избор на събития, състояние и брой ключове.',
      'Ein Endpunkt mit aktueller URL, Ereignisauswahl, Status und Schlüsselanzahl.',
      "Un endpoint, avec son URL, sa sélection d'événements, son état et son nombre de secrets.",
      'Один адрес: текущий URL, набор событий, состояние и число секретов.',
      'Одна адреса: поточний URL, набір подій, стан і число секретів.',
      'Un endpoint, con URL attuale, selezione eventi, stato e numero di segreti.',
      'Un endpoint, con su URL, selección de eventos, estado y número de secretos.',
    ),
    note: t(
      'The secrets themselves are never returned - only how many are live and whether a rotation is part-way through. Read this after a rotation to confirm the overlap really is in force before you deploy the new secret.',
      'Самите ключове никога не се връщат - само колко са живи и дали тече ротация. Прочетете това след ротация, за да потвърдите, че застъпването наистина е в сила, преди да пуснете новия ключ.',
      'Die Schlüssel selbst werden nie zurückgegeben - nur ihre Anzahl und ob eine Rotation läuft. Nach einer Rotation hier prüfen, ob die Überlappung wirklich greift, bevor Sie den neuen Schlüssel ausrollen.',
      "Les secrets eux-mêmes ne sont jamais renvoyés : seulement leur nombre et l'état d'une rotation. À consulter après une rotation pour confirmer le chevauchement avant de déployer.",
      'Сами секреты никогда не возвращаются - только их число и признак идущей ротации. Читайте это после ротации, чтобы убедиться, что перекрытие действительно действует, прежде чем выкатывать новый секрет.',
      'Самі секрети ніколи не повертаються - лише їх число та ознака ротації. Читайте це після ротації, щоб переконатися, що перекриття справді діє, перш ніж викочувати новий секрет.',
      'I segreti non vengono mai restituiti: solo il loro numero e l’eventuale rotazione in corso.',
      'Los secretos nunca se devuelven: solo cuántos hay y si hay una rotación en curso.',
    ),
    request: `curl https://api.wyocs.org/api/v1/webhooks/endpoints/whe_4KP8... \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "id": "whe_4KP8...",
  "object": "webhook_endpoint",
  "url": "https://hooks.example.com/wyocspay",
  "description": "Production ledger",
  "status": "ACTIVE",
  "enabled_events": ["payment.succeeded", "payment.refunded"],
  "created_at": "2026-06-02T14:00:11.004Z",
  "active_secrets": 2,
  "rotation_in_progress": true
}`,
  },

  {
    id: 'update-endpoint',
    errors: ['invalid_request', 'no_changes', 'invalid_url', 'insecure_url', 'not_found'],
    method: 'PATCH',
    path: '/api/v1/webhooks/endpoints/{id}',
    group: 'webhooks',
    title: t('Update an endpoint', 'Промяна на адрес', 'Endpunkt ändern', 'Modifier un endpoint', 'Изменить адрес', 'Змінити адресу', 'Modificare un endpoint', 'Actualizar un endpoint'),
    summary: t(
      'Change where events go, which types are sent, or pause delivery without destroying anything.',
      'Променя къде отиват събитията, кои типове се пращат, или спира доставката, без да унищожава нищо.',
      'Ändert Ziel und Ereignistypen oder pausiert die Zustellung, ohne etwas zu zerstören.',
      'Change la destination, les types envoyés, ou met la livraison en pause sans rien détruire.',
      'Меняет, куда идут события и какие типы шлются, либо ставит доставку на паузу, ничего не разрушая.',
      'Змінює, куди йдуть події та які типи шлються, або ставить доставку на паузу, нічого не руйнуючи.',
      'Cambia destinazione e tipi inviati, oppure mette in pausa la consegna senza distruggere nulla.',
      'Cambia el destino, los tipos enviados, o pausa la entrega sin destruir nada.',
    ),
    note: t(
      'status DISABLED is what you want for maintenance. It stops deliveries while keeping the endpoint, its signing secret and its whole delivery log; deleting and recreating would mint a new secret and throw the log away, which is exactly the record you need afterwards to work out what was missed. The URL cannot be edited down to plaintext HTTP - the same rule that applies when the endpoint is created applies when it is changed, or the check could be walked around in two steps.',
      'status DISABLED е това, което искате за поддръжка. Спира доставките, като запазва адреса, подписващия ключ и целия дневник; изтриване и създаване наново би родило нов ключ и би изхвърлило дневника - точно записа, който после ви трябва, за да разберете какво е пропуснато. URL не може да бъде свален до обикновен HTTP - същото правило важи и при промяна, иначе проверката би се заобиколила на две стъпки.',
      'status DISABLED ist die richtige Wahl für Wartung: Zustellungen stoppen, Endpunkt, Signaturschlüssel und gesamtes Zustellprotokoll bleiben. Löschen und neu anlegen erzeugte einen neuen Schlüssel und verwürfe das Protokoll. Die URL lässt sich nicht auf HTTP herabsetzen - dieselbe Regel wie beim Anlegen, sonst wäre die Prüfung in zwei Schritten umgangen.',
      "status DISABLED est ce qu'il faut pour une maintenance : la livraison s'arrête, l'endpoint, son secret et tout son journal restent. Supprimer puis recréer produirait un nouveau secret et jetterait le journal. L'URL ne peut être rétrogradée en HTTP : la règle de la création s'applique aussi à la modification, sinon le contrôle se contournerait en deux temps.",
      'status DISABLED - то, что нужно на время обслуживания: доставки останавливаются, а адрес, секрет и весь журнал сохраняются. Удаление и пересоздание дало бы новый секрет и выбросило журнал. URL нельзя понизить до обычного HTTP: правило создания действует и при изменении, иначе проверку обошли бы в два шага.',
      'status DISABLED - те, що потрібно на час обслуговування: доставки зупиняються, а адреса, секрет і весь журнал зберігаються. Видалення й повторне створення дало б новий секрет і викинуло журнал. URL не можна знизити до звичайного HTTP: правило створення діє і при зміні, інакше перевірку обійшли б у два кроки.',
      "status DISABLED è la scelta giusta per la manutenzione: le consegne si fermano, endpoint, segreto e registro restano. L'URL non può essere declassato a HTTP: la stessa regola della creazione vale per la modifica.",
      'status DISABLED es lo adecuado para mantenimiento: se detienen las entregas y se conservan el endpoint, su secreto y todo el registro. La URL no puede degradarse a HTTP: la regla de la creación rige también al modificar.',
    ),
    params: [
      {
        name: 'url',
        type: 'string',
        required: false,
        description: t('Must still be https.', 'Пак трябва да е https.', 'Muss weiterhin https sein.', 'Doit rester en https.', 'По-прежнему должен быть https.', 'Так само має бути https.', 'Deve restare https.', 'Debe seguir siendo https.'),
      },
      {
        name: 'description',
        type: 'string',
        required: false,
        description: t('Send null to clear it.', 'Изпратете null, за да го изчистите.', 'null zum Leeren senden.', 'Envoyez null pour l’effacer.', 'Отправьте null, чтобы очистить.', 'Надішліть null, щоб очистити.', 'Invia null per svuotarlo.', 'Envíe null para vaciarlo.'),
      },
      {
        name: 'enabled_events',
        type: 'string[]',
        required: false,
        description: t(
          'Replaces the list entirely. An empty array means every event type.',
          'Замества целия списък. Празен масив значи всички типове събития.',
          'Ersetzt die Liste vollständig. Ein leeres Array bedeutet alle Ereignistypen.',
          'Remplace toute la liste. Un tableau vide signifie tous les types.',
          'Полностью заменяет список. Пустой массив означает все типы событий.',
          'Повністю замінює список. Порожній масив означає всі типи подій.',
          "Sostituisce l'intero elenco. Un array vuoto significa tutti i tipi.",
          'Sustituye la lista entera. Un arreglo vacío significa todos los tipos.',
        ),
      },
      {
        name: 'status',
        type: 'string',
        required: false,
        description: t(
          'ACTIVE or DISABLED. A disabled endpoint receives nothing and loses nothing.',
          'ACTIVE или DISABLED. Спрян адрес не получава нищо и не губи нищо.',
          'ACTIVE oder DISABLED. Ein deaktivierter Endpunkt empfängt nichts und verliert nichts.',
          'ACTIVE ou DISABLED. Un endpoint désactivé ne reçoit rien et ne perd rien.',
          'ACTIVE или DISABLED. Отключённый адрес ничего не получает и ничего не теряет.',
          'ACTIVE або DISABLED. Вимкнена адреса нічого не отримує і нічого не втрачає.',
          'ACTIVE o DISABLED. Un endpoint disabilitato non riceve nulla e non perde nulla.',
          'ACTIVE o DISABLED. Un endpoint desactivado no recibe nada ni pierde nada.',
        ),
      },
    ],
    request: `curl -X PATCH https://api.wyocs.org/api/v1/webhooks/endpoints/whe_4KP8... \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"status": "DISABLED"}'`,
    response: `{
  "id": "whe_4KP8...",
  "object": "webhook_endpoint",
  "url": "https://hooks.example.com/wyocspay",
  "description": "Production ledger",
  "status": "DISABLED",
  "enabled_events": ["payment.succeeded", "payment.refunded"],
  "created_at": "2026-06-02T14:00:11.004Z",
  "active_secrets": 1,
  "rotation_in_progress": false
}`,
  },

  {
    id: 'delete-endpoint',
    errors: ['not_found'],
    method: 'DELETE',
    path: '/api/v1/webhooks/endpoints/{id}',
    group: 'webhooks',
    title: t('Delete an endpoint', 'Изтриване на адрес', 'Endpunkt löschen', 'Supprimer un endpoint', 'Удалить адрес', 'Видалити адресу', 'Eliminare un endpoint', 'Eliminar un endpoint'),
    summary: t(
      'Remove an endpoint and everything recorded about its deliveries.',
      'Премахва адрес и всичко записано за неговите доставки.',
      'Entfernt einen Endpunkt samt allem, was über seine Zustellungen festgehalten wurde.',
      'Supprime un endpoint et tout ce qui a été consigné sur ses livraisons.',
      'Удаляет адрес и всё, что записано о его доставках.',
      'Видаляє адресу і все, що записано про її доставки.',
      'Rimuove un endpoint e tutto ciò che è stato registrato sulle sue consegne.',
      'Elimina un endpoint y todo lo registrado sobre sus entregas.',
    ),
    note: t(
      'Disabling is almost always the better move. The delivery log goes with the endpoint, and that log is the only thing that can answer "why did we never get that event" afterwards. The events themselves survive - they belong to your account, not to the endpoint - so GET /api/v1/events still has them.',
      'Спирането почти винаги е по-добрият ход. Дневникът на доставките си отива с адреса, а точно той е единственото, което после може да отговори „защо така и не получихме онова събитие". Самите събития остават - те са на профила, не на адреса - така че GET /api/v1/events още ги има.',
      'Deaktivieren ist fast immer besser. Das Zustellprotokoll verschwindet mit dem Endpunkt, und nur dieses Protokoll beantwortet später "warum kam dieses Ereignis nie an". Die Ereignisse selbst bleiben - sie gehören dem Konto, nicht dem Endpunkt.',
      "Désactiver vaut presque toujours mieux. Le journal de livraison disparaît avec l'endpoint, or c'est la seule chose qui répond après coup à « pourquoi n'avons-nous jamais reçu cet événement ». Les événements eux-mêmes subsistent : ils appartiennent au compte.",
      'Отключение почти всегда лучше. Журнал доставок уходит вместе с адресом, а именно он потом отвечает на вопрос «почему мы так и не получили то событие». Сами события остаются - они принадлежат аккаунту, а не адресу.',
      'Вимкнення майже завжди краще. Журнал доставок іде разом з адресою, а саме він потім відповідає на питання «чому ми так і не отримали ту подію». Самі події лишаються - вони належать обліковому запису, а не адресі.',
      "Disabilitare è quasi sempre meglio. Il registro delle consegne se ne va con l'endpoint ed è l'unica cosa che poi risponde a \"perché quell'evento non è mai arrivato\". Gli eventi restano: appartengono all'account.",
      'Desactivar casi siempre es mejor. El registro de entregas se va con el endpoint y es lo único que después responde a «por qué nunca recibimos ese evento». Los eventos siguen: pertenecen a la cuenta.',
    ),
    request: `curl -X DELETE https://api.wyocs.org/api/v1/webhooks/endpoints/whe_4KP8... \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "deleted": true,
  "id": "whe_4KP8..."
}`,
  },

  {
    id: 'rotate-secret',
    errors: ['invalid_request', 'not_found', 'idempotency_conflict'],
    method: 'POST',
    path: '/api/v1/webhooks/endpoints/{id}/rotate',
    group: 'webhooks',
    title: t('Rotate a signing secret', 'Ротация на подписващ ключ', 'Signaturschlüssel rotieren', 'Faire tourner le secret', 'Ротация подписывающего секрета', 'Ротація підписувального секрету', 'Ruotare il segreto di firma', 'Rotar el secreto de firma'),
    summary: t(
      'Issue a new signing secret while the old one keeps working for a while.',
      'Издава нов подписващ ключ, докато старият още работи известно време.',
      'Stellt einen neuen Signaturschlüssel aus, während der alte eine Weile weiter gilt.',
      "Émet un nouveau secret de signature pendant que l'ancien reste valable un temps.",
      'Выпускает новый подписывающий секрет, пока старый ещё какое-то время работает.',
      'Випускає новий підписувальний секрет, поки старий ще якийсь час працює.',
      'Emette un nuovo segreto di firma mentre il vecchio resta valido per un po’.',
      'Emite un nuevo secreto de firma mientras el antiguo sigue válido un tiempo.',
    ),
    note: t(
      'During the overlap both secrets sign every payload, so you can deploy the new one whenever it suits and nothing is missed in between. That is the whole point: rotating a secret should not require downtime. overlap_seconds 0 is the compromised-secret path - the old secret stops immediately and you take the gap deliberately. Send an Idempotency-Key. Without one, a client that retries on a timeout mints a second new secret, and the one it never saw is now signing payloads it cannot verify.',
      'По време на застъпването и двата ключа подписват всяко тяло, така че може да пуснете новия когато ви е удобно и нищо да не се пропусне. Точно това е смисълът: ротацията на ключ не бива да иска престой. overlap_seconds 0 е пътят при компрометиран ключ - старият спира веднага и вие поемате прекъсването нарочно. Изпратете Idempotency-Key. Без него клиент, който повтори при таймаут, ражда втори нов ключ, а онзи, който не е видял, вече подписва тела, които не може да провери.',
      'Während der Überlappung signieren beide Schlüssel jede Nutzlast, Sie können den neuen also einspielen, wann es passt, ohne etwas zu verlieren. Genau darum geht es: eine Rotation darf keine Ausfallzeit erfordern. overlap_seconds 0 ist der Weg beim kompromittierten Schlüssel. Senden Sie einen Idempotency-Key, sonst erzeugt ein Timeout-Retry einen zweiten neuen Schlüssel.',
      "Pendant le chevauchement, les deux secrets signent chaque contenu : vous déployez le nouveau quand cela vous arrange, sans rien perdre. C'est tout l'objet : une rotation ne doit pas imposer d'interruption. overlap_seconds 0 correspond au secret compromis. Envoyez une clé d'idempotence, faute de quoi une reprise sur timeout créerait un second secret.",
      'Во время перекрытия оба секрета подписывают каждое тело, поэтому новый можно выкатить когда удобно и ничего не потерять. В этом весь смысл: ротация не должна требовать простоя. overlap_seconds 0 - путь для скомпрометированного секрета. Отправляйте Idempotency-Key, иначе повтор по таймауту породит второй новый секрет.',
      'Під час перекриття обидва секрети підписують кожне тіло, тож новий можна викотити коли зручно і нічого не втратити. У цьому весь сенс: ротація не має вимагати простою. overlap_seconds 0 - шлях для скомпрометованого секрету. Надсилайте Idempotency-Key, інакше повтор за таймаутом породить другий новий секрет.',
      "Durante la sovrapposizione entrambi i segreti firmano ogni payload, così puoi distribuire il nuovo quando ti conviene. overlap_seconds 0 è il percorso per un segreto compromesso. Invia una Idempotency-Key, altrimenti un nuovo tentativo dopo un timeout crea un secondo segreto.",
      'Durante el solape ambos secretos firman cada contenido, así que puede desplegar el nuevo cuando le convenga. overlap_seconds 0 es la vía para un secreto comprometido. Envíe una clave de idempotencia; si no, un reintento por timeout crearía un segundo secreto.',
    ),
    params: [
      {
        name: 'overlap_seconds',
        type: 'integer',
        required: false,
        description: t(
          '0 to 604800. Defaults to 86400, one day. Use 0 when the old secret has leaked.',
          'От 0 до 604800. По подразбиране 86400, един ден. Ползвайте 0, когато старият ключ е изтекъл навън.',
          '0 bis 604800. Standard 86400, ein Tag. 0 verwenden, wenn der alte Schlüssel geleakt ist.',
          "0 à 604800. Par défaut 86400, un jour. Utilisez 0 si l'ancien secret a fuité.",
          'От 0 до 604800. По умолчанию 86400, сутки. Ставьте 0, если старый секрет утёк.',
          'Від 0 до 604800. За замовчуванням 86400, доба. Ставте 0, якщо старий секрет витік.',
          'Da 0 a 604800. Predefinito 86400, un giorno. Usa 0 se il vecchio segreto è trapelato.',
          'De 0 a 604800. Por defecto 86400, un día. Use 0 si el secreto antiguo se filtró.',
        ),
      },
    ],
    request: `curl -X POST https://api.wyocs.org/api/v1/webhooks/endpoints/whe_4KP8.../rotate \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Idempotency-Key: YOUR_IDEMPOTENCY_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"overlap_seconds": 86400}'`,
    response: `{
  "endpoint_id": "whe_4KP8...",
  "secret": "whsec_...",
  "old_secret_expires_at": "2026-08-02T09:00:00.000Z"
}`,
  },

  {
    id: 'list-deliveries',
    errors: ['invalid_request'],
    method: 'GET',
    path: '/api/v1/webhooks/deliveries',
    group: 'webhooks',
    title: t('List deliveries', 'Списък с доставки', 'Zustellungen auflisten', 'Lister les livraisons', 'Список доставок', 'Список доставок', 'Elenca le consegne', 'Listar entregas'),
    summary: t(
      'Every attempt to hand an event to one of your endpoints, with the HTTP status your server answered and when the next retry is due.',
      'Всеки опит събитие да бъде подадено на ваш адрес, с HTTP статуса, който вашият сървър е отговорил, и кога е следващият опит.',
      'Jeder Versuch, ein Ereignis an einen Ihrer Endpunkte zu übergeben, mit dem HTTP-Status Ihres Servers und dem nächsten Wiederholungszeitpunkt.',
      "Chaque tentative de remise d'un événement à un de vos endpoints, avec le statut HTTP renvoyé par votre serveur et la prochaine tentative.",
      'Каждая попытка передать событие на один из ваших адресов, с HTTP-статусом вашего сервера и временем следующей попытки.',
      'Кожна спроба передати подію на одну з ваших адрес, з HTTP-статусом вашого сервера і часом наступної спроби.',
      'Ogni tentativo di consegnare un evento a un tuo endpoint, con lo stato HTTP risposto dal tuo server e il prossimo tentativo.',
      'Cada intento de entregar un evento a uno de sus endpoints, con el estado HTTP de su servidor y el próximo reintento.',
    ),
    note: t(
      'This is the first place to look when a merchant system and the platform disagree. A delivery in FAILED with next_attempt_at set is still being retried; EXHAUSTED means the platform has given up and a replay is the way back. is_replay marks the ones an operator or an API call re-sent, so a duplicate in your own logs can be explained rather than investigated.',
      'Това е първото място за проверка, когато търговската система и платформата не са съгласни. Доставка във FAILED с попълнено next_attempt_at още се повтаря; EXHAUSTED значи, че платформата се е отказала и повторното пускане е пътят назад. is_replay маркира тези, които оператор или API са пратили отново, така че дубликат във вашите логове може да бъде обяснен, а не разследван.',
      'Der erste Ort, wenn Händlersystem und Plattform sich widersprechen. FAILED mit gesetztem next_attempt_at wird noch wiederholt; EXHAUSTED heißt, die Plattform hat aufgegeben und ein Replay ist der Weg zurück. is_replay kennzeichnet erneut gesendete Zustellungen.',
      "C'est le premier endroit à regarder quand le système marchand et la plateforme divergent. FAILED avec next_attempt_at renseigné est encore réessayé ; EXHAUSTED signifie que la plateforme a renoncé et qu'un rejeu est la solution. is_replay marque les renvois.",
      'Первое место для проверки, когда система продавца и платформа расходятся. FAILED с заполненным next_attempt_at ещё повторяется; EXHAUSTED означает, что платформа сдалась и путь назад - повтор. is_replay помечает повторно отправленные.',
      'Перше місце для перевірки, коли система продавця і платформа розходяться. FAILED із заповненим next_attempt_at ще повторюється; EXHAUSTED означає, що платформа здалася і шлях назад - повтор. is_replay позначає повторно надіслані.',
      "È il primo posto da guardare quando il sistema dell'esercente e la piattaforma non concordano. FAILED con next_attempt_at è ancora in ritentativo; EXHAUSTED significa che la piattaforma ha rinunciato. is_replay segna i rinvii.",
      'Es el primer sitio donde mirar cuando el sistema del comercio y la plataforma discrepan. FAILED con next_attempt_at aún se reintenta; EXHAUSTED significa que la plataforma se rindió. is_replay marca los reenvíos.',
    ),
    params: [
      {
        name: 'limit',
        type: 'integer',
        required: false,
        description: t('1 to 100. Defaults to 25.', 'От 1 до 100. По подразбиране 25.', '1 bis 100. Standard 25.', '1 à 100. Par défaut 25.', 'От 1 до 100. По умолчанию 25.', 'Від 1 до 100. За замовчуванням 25.', 'Da 1 a 100. Predefinito 25.', 'De 1 a 100. Por defecto 25.'),
      },
      {
        name: 'status',
        type: 'string',
        required: false,
        description: t(
          'PENDING, DELIVERED, FAILED or EXHAUSTED.',
          'PENDING, DELIVERED, FAILED или EXHAUSTED.',
          'PENDING, DELIVERED, FAILED oder EXHAUSTED.',
          'PENDING, DELIVERED, FAILED ou EXHAUSTED.',
          'PENDING, DELIVERED, FAILED или EXHAUSTED.',
          'PENDING, DELIVERED, FAILED або EXHAUSTED.',
          'PENDING, DELIVERED, FAILED o EXHAUSTED.',
          'PENDING, DELIVERED, FAILED o EXHAUSTED.',
        ),
      },
      {
        name: 'endpoint_id',
        type: 'string',
        required: false,
        description: t(
          'Only deliveries to one of your endpoints.',
          'Само доставки към един ваш адрес.',
          'Nur Zustellungen an einen Ihrer Endpunkte.',
          'Uniquement les livraisons vers un de vos endpoints.',
          'Только доставки на один из ваших адресов.',
          'Лише доставки на одну з ваших адрес.',
          'Solo le consegne a un tuo endpoint.',
          'Solo entregas a uno de sus endpoints.',
        ),
      },
      {
        name: 'event_id',
        type: 'string',
        required: false,
        description: t(
          'Every attempt at one event, across all your endpoints.',
          'Всеки опит за едно събитие, през всички ваши адреси.',
          'Alle Versuche zu einem Ereignis über sämtliche Endpunkte.',
          'Toutes les tentatives pour un événement, sur tous vos endpoints.',
          'Все попытки по одному событию, по всем вашим адресам.',
          'Усі спроби за однією подією, по всіх ваших адресах.',
          'Tutti i tentativi per un evento, su tutti i tuoi endpoint.',
          'Todos los intentos de un evento, en todos sus endpoints.',
        ),
      },
    ],
    request: `curl "https://api.wyocs.org/api/v1/webhooks/deliveries?status=FAILED" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "object": "list",
  "data": [
    {
      "id": "whd_2NQ7...",
      "object": "webhook_delivery",
      "event_id": "evt_...",
      "event_type": "payment.succeeded",
      "endpoint_id": "whe_4KP8...",
      "endpoint_url": "https://hooks.example.com/wyocspay",
      "status": "FAILED",
      "attempts": 3,
      "http_status": 502,
      "error": "upstream returned 502",
      "created_at": "2026-07-29T10:22:08.104Z",
      "last_attempt_at": "2026-07-29T10:34:08.902Z",
      "next_attempt_at": "2026-07-29T11:06:08.902Z",
      "is_replay": false,
      "replay_of": null
    }
  ]
}`,
  },

  {
    id: 'get-delivery',
    errors: ['not_found'],
    method: 'GET',
    path: '/api/v1/webhooks/deliveries/{id}',
    group: 'webhooks',
    title: t('Retrieve a delivery', 'Извличане на доставка', 'Zustellung abrufen', 'Récupérer une livraison', 'Получить доставку', 'Отримати доставку', 'Recuperare una consegna', 'Recuperar una entrega'),
    summary: t(
      'One delivery, including the body your own server sent back.',
      'Една доставка, включително тялото, което вашият собствен сървър е върнал.',
      'Eine Zustellung, samt der Antwort, die Ihr eigener Server zurückgab.',
      'Une livraison, y compris le corps renvoyé par votre propre serveur.',
      'Одна доставка, включая тело ответа вашего собственного сервера.',
      'Одна доставка, включно з тілом відповіді вашого власного сервера.',
      'Una consegna, incluso il corpo restituito dal tuo server.',
      'Una entrega, incluido el cuerpo que devolvió su propio servidor.',
    ),
    note: t(
      'response_body is the field that settles "it returned 200, why is it marked failed" and its opposite. It is truncated by the dispatcher, and it is your own server\'s output rather than anything of the platform\'s, which is why it can be shown in full detail here when Stripe error text elsewhere cannot.',
      'response_body е полето, което решава „върна 200, защо е маркирано като неуспешно" и обратното. Съкратено е от диспечера и е изход на вашия собствен сървър, а не на платформата - затова тук може да се показва подробно, докато текстът на грешките от Stripe другаде не може.',
      'response_body klärt "es kam 200 zurück, warum gilt es als fehlgeschlagen" und den umgekehrten Fall. Der Dispatcher kürzt es; es ist die Ausgabe Ihres eigenen Servers und nicht die der Plattform - deshalb darf es hier im Detail stehen, während Stripe-Fehlertexte es andernorts nicht dürfen.',
      "response_body tranche « il a renvoyé 200, pourquoi est-ce marqué en échec » et l'inverse. Il est tronqué par le distributeur ; c'est la sortie de votre serveur, pas de la plateforme, d'où sa présence détaillée ici alors que le texte d'erreur Stripe ne l'est jamais.",
      'response_body решает вопрос «вернул 200, почему помечено как неудача» и обратный. Оно усечено диспетчером и является выводом вашего сервера, а не платформы, - поэтому здесь его можно показать подробно, тогда как текст ошибок Stripe в других местах нельзя.',
      'response_body вирішує питання «повернув 200, чому позначено як невдача» і зворотне. Воно скорочене диспетчером і є виводом вашого сервера, а не платформи, - тому тут його можна показати докладно, тоді як текст помилок Stripe в інших місцях не можна.',
      "response_body risolve \"ha restituito 200, perché risulta fallita\" e il contrario. È troncato dal dispatcher ed è l'output del tuo server, non della piattaforma.",
      'response_body resuelve «devolvió 200, por qué figura como fallida» y lo contrario. Lo trunca el despachador y es la salida de su propio servidor, no de la plataforma.',
    ),
    request: `curl https://api.wyocs.org/api/v1/webhooks/deliveries/whd_2NQ7... \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "id": "whd_2NQ7...",
  "object": "webhook_delivery",
  "event_id": "evt_...",
  "event_type": "payment.succeeded",
  "endpoint_id": "whe_4KP8...",
  "endpoint_url": "https://hooks.example.com/wyocspay",
  "status": "FAILED",
  "attempts": 3,
  "http_status": 502,
  "error": "upstream returned 502",
  "created_at": "2026-07-29T10:22:08.104Z",
  "last_attempt_at": "2026-07-29T10:34:08.902Z",
  "next_attempt_at": "2026-07-29T11:06:08.902Z",
  "is_replay": false,
  "replay_of": null,
  "response_body": "<html><head><title>502 Bad Gateway</title>..."
}`,
  },

  {
    id: 'replay-delivery',
    errors: ['not_found', 'idempotency_conflict'],
    method: 'POST',
    path: '/api/v1/webhooks/deliveries/{id}/replay',
    group: 'webhooks',
    title: t('Replay a delivery', 'Повторна доставка', 'Zustellung erneut senden', 'Rejouer une livraison', 'Повторить доставку', 'Повторити доставку', 'Riprodurre una consegna', 'Reenviar una entrega'),
    summary: t(
      'Send an event to your endpoint again, after an outage on your side or a bug that dropped it.',
      'Праща събитие към вашия адрес отново, след срив от ваша страна или бъг, който го е изпуснал.',
      'Sendet ein Ereignis erneut an Ihren Endpunkt, nach einem Ausfall bei Ihnen oder einem Fehler, der es verwarf.',
      "Renvoie un événement à votre endpoint, après une panne chez vous ou un bug qui l'a perdu.",
      'Отправляет событие на ваш адрес снова - после сбоя у вас или ошибки, которая его потеряла.',
      'Надсилає подію на вашу адресу знову - після збою у вас або помилки, що її загубила.',
      'Invia di nuovo un evento al tuo endpoint, dopo un guasto dalla tua parte o un bug che l’ha perso.',
      'Vuelve a enviar un evento a su endpoint, tras una caída suya o un fallo que lo perdió.',
    ),
    note: t(
      'This re-sends a notification and nothing else. It does not recreate the underlying Stripe transaction, does not touch the payment, and does not reach Stripe at all - it is a re-POST of a payload that was already true when it was first sent. Your handler must therefore be idempotent on event id: a replay carries the same evt_ identifier as the original, and treating it as a new event would double whatever it triggers on your side.',
      'Това праща наново известие и нищо друго. Не пресъздава Stripe транзакцията, не пипа плащането и изобщо не стига до Stripe - това е повторно изпращане на тяло, което вече е било вярно. Затова вашият обработчик трябва да е идемпотентен по id на събитието: повторението носи същия evt_ идентификатор като оригинала и третирането му като ново събитие би удвоило каквото и да задейства при вас.',
      'Das sendet nur eine Benachrichtigung erneut. Es erzeugt keine Stripe-Transaktion neu, berührt die Zahlung nicht und erreicht Stripe gar nicht. Ihr Handler muss daher idempotent bezüglich der Ereignis-id sein: ein Replay trägt dieselbe evt_-Kennung wie das Original.',
      "Cela renvoie une notification, rien d'autre. Aucune transaction Stripe n'est recréée, le paiement n'est pas touché et Stripe n'est pas sollicité. Votre gestionnaire doit donc être idempotent sur l'id d'événement : un rejeu porte le même identifiant evt_ que l'original.",
      'Это повторная отправка уведомления и ничего больше. Транзакция Stripe не пересоздаётся, платёж не трогается, до Stripe запрос не доходит. Поэтому ваш обработчик должен быть идемпотентен по id события: повтор несёт тот же идентификатор evt_, что и оригинал.',
      'Це повторне надсилання сповіщення і нічого більше. Транзакція Stripe не перестворюється, платіж не чіпається, до Stripe запит не доходить. Тому ваш обробник має бути ідемпотентним за id події: повтор несе той самий ідентифікатор evt_, що й оригінал.',
      "Rinvia una notifica e nulla più. Non ricrea la transazione Stripe, non tocca il pagamento e non raggiunge Stripe. Il tuo handler deve quindi essere idempotente sull'id evento: un replay porta lo stesso identificatore evt_ dell'originale.",
      'Esto reenvía una notificación y nada más. No recrea la transacción de Stripe, no toca el pago y no llega a Stripe. Su manejador debe ser idempotente por id de evento: un reenvío lleva el mismo identificador evt_ que el original.',
    ),
    request: `curl -X POST https://api.wyocs.org/api/v1/webhooks/deliveries/whd_2NQ7.../replay \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Idempotency-Key: YOUR_IDEMPOTENCY_KEY"`,
    response: `{
  "id": "whd_8SR1...",
  "object": "webhook_delivery",
  "status": "PENDING",
  "replay_of": "whd_2NQ7..."
}`,
  },

  {
    id: 'get-event',
    errors: ['not_found'],
    method: 'GET',
    path: '/api/v1/events/{id}',
    group: 'webhooks',
    title: t('Retrieve an event', 'Извличане на събитие', 'Ereignis abrufen', 'Récupérer un événement', 'Получить событие', 'Отримати подію', 'Recuperare un evento', 'Recuperar un evento'),
    summary: t(
      'One event, in exactly the form it was signed and sent.',
      'Едно събитие, точно във вида, в който е било подписано и изпратено.',
      'Ein Ereignis, exakt in der Form, in der es signiert und gesendet wurde.',
      "Un événement, exactement sous la forme signée et envoyée.",
      'Одно событие ровно в том виде, в каком оно было подписано и отправлено.',
      'Одна подія рівно в тому вигляді, у якому її було підписано й надіслано.',
      "Un evento, esattamente nella forma in cui è stato firmato e inviato.",
      'Un evento, exactamente en la forma en que se firmó y envió.',
    ),
    note: t(
      'The stored payload is the envelope as it was signed, byte for byte, so comparing this against what arrived at your endpoint compares like with like. If the two differ, something on the path rewrote the body.',
      'Съхраненото тяло е пликът така, както е бил подписан, буква по буква, така че сравняването му с пристигналото на вашия адрес сравнява еднакво с еднакво. Ако двете се различават, нещо по пътя е пренаписало тялото.',
      'Die gespeicherte Nutzlast ist der Umschlag, wie er signiert wurde, Byte für Byte - ein Vergleich mit dem bei Ihnen Angekommenen vergleicht also Gleiches mit Gleichem. Unterschiede bedeuten, dass etwas auf dem Weg den Body umgeschrieben hat.',
      "La charge stockée est l'enveloppe telle que signée, octet pour octet : la comparer à ce qui est arrivé chez vous compare bien la même chose. Une différence signifie qu'un intermédiaire a réécrit le corps.",
      'Сохранённое тело - это конверт в том виде, в каком он был подписан, байт в байт, так что сравнение с пришедшим на ваш адрес сравнивает одно и то же. Различие означает, что что-то на пути переписало тело.',
      'Збережене тіло - це конверт у тому вигляді, у якому його було підписано, байт у байт, тож порівняння з тим, що прийшло на вашу адресу, порівнює одне й те саме. Різниця означає, що щось на шляху переписало тіло.',
      "Il payload memorizzato è la busta così come è stata firmata, byte per byte. Se differisce da quanto arrivato, qualcosa lungo il percorso ha riscritto il corpo.",
      'El contenido guardado es el sobre tal como se firmó, byte a byte. Si difiere de lo que llegó, algo en el camino reescribió el cuerpo.',
    ),
    request: `curl https://api.wyocs.org/api/v1/events/evt_... \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "id": "evt_...",
  "object": "event",
  "type": "payment.succeeded",
  "created_at": "2026-07-29T10:22:07.998Z",
  "api_version": "2026-01-01",
  "livemode": true,
  "data": {
    "object": {
      "id": "pay_...",
      "amount": 4990,
      "currency": "usd",
      "status": "SUCCEEDED",
      "merchant_reference": "INV-2026-0415"
    }
  }
}`,
  },

  // -------------------------------------------------------------------------
  // Capabilities
  // -------------------------------------------------------------------------

  {
    id: 'features',
    method: 'GET',
    path: '/api/v1/features',
    group: 'capabilities',
    title: t('List capabilities', 'Списък с възможности', 'Freigaben auflisten', 'Lister les capacités', 'Список возможностей', 'Список можливостей', 'Elenca le capacità', 'Listar capacidades'),
    summary: t(
      'What this account can do, what is locked, why it is locked and what would unlock it.',
      'Какво може този профил, какво е заключено, защо е заключено и какво би го отключило.',
      'Was dieses Konto darf, was gesperrt ist, warum, und was es freischalten würde.',
      "Ce que ce compte peut faire, ce qui est verrouillé, pourquoi, et ce qui le débloquerait.",
      'Что может этот аккаунт, что закрыто, почему закрыто и что откроет.',
      'Що може цей обліковий запис, що закрито, чому закрито і що відкриє.',
      'Cosa può fare questo account, cosa è bloccato, perché e cosa lo sbloccherebbe.',
      'Qué puede hacer esta cuenta, qué está bloqueado, por qué y qué lo desbloquearía.',
    ),
    note: t(
      'Capabilities come in three kinds. Open ones are on for everybody. Verification-gated ones - payment links, refunds, payouts - unlock by themselves the moment Stripe reports the account verified, and nobody at the platform decides anything. Approval-gated ones - API access, webhooks, high-value payments, instant payouts, multi-currency, recurring - are read by a person, because each of them widens what a compromised account could do. When one is locked, blockedReason says what to do about it and questions lists what the reviewer will want answered; conditions carries any ceiling attached to a grant, such as maxPaymentAmount. This is the endpoint to consult when something returns 403.',
      'Възможностите са три вида. Отворените са включени за всички. Верификационните - линкове за плащане, рефунди, изплащания - се отключват сами в мига, в който Stripe съобщи, че профилът е верифициран, и никой в платформата не решава нищо. Одобрителните - API достъп, webhooks, плащания с висока стойност, мигновени изплащания, много валути, повтарящи се - се четат от човек, защото всяка от тях разширява какво би могъл да направи компрометиран профил. Когато нещо е заключено, blockedReason казва какво да се направи, а questions изброява какво ще иска да чуе рецензентът; conditions носи всеки таван, прикачен към разрешението, например maxPaymentAmount. Това е адресът за справка, когато нещо върне 403.',
      'Freigaben gibt es in drei Arten. Offene gelten für alle. Verifizierungsabhängige - Zahlungslinks, Erstattungen, Auszahlungen - schalten sich selbst frei, sobald Stripe das Konto als verifiziert meldet. Genehmigungspflichtige - API-Zugang, Webhooks, Hochbetragszahlungen, Sofortauszahlungen, Mehrwährung, wiederkehrend - liest ein Mensch, weil jede den Schaden eines kompromittierten Kontos vergrößert. blockedReason sagt, was zu tun ist, questions nennt die Rückfragen, conditions trägt Obergrenzen wie maxPaymentAmount. Diese Auskunft hilft bei jedem 403.',
      "Les capacités sont de trois sortes. Les ouvertes valent pour tous. Celles liées à la vérification - liens de paiement, remboursements, virements - se débloquent seules dès que Stripe déclare le compte vérifié. Celles soumises à approbation - accès API, webhooks, gros montants, virements instantanés, multidevise, récurrent - sont lues par une personne, car chacune élargit ce qu'un compte compromis pourrait faire. blockedReason dit quoi faire, questions liste les questions du relecteur, conditions porte les plafonds tels que maxPaymentAmount. À consulter dès qu'un 403 apparaît.",
      'Возможности бывают трёх видов. Открытые включены у всех. Верификационные - платёжные ссылки, возвраты, выплаты - открываются сами, как только Stripe сообщит, что аккаунт проверен. Одобряемые - доступ по API, вебхуки, крупные платежи, мгновенные выплаты, мультивалютность, регулярные списания - читает человек, потому что каждая расширяет ущерб от скомпрометированного аккаунта. blockedReason говорит, что делать, questions перечисляет вопросы проверяющего, conditions несёт потолки вроде maxPaymentAmount. Сюда стоит смотреть при любом 403.',
      'Можливості бувають трьох видів. Відкриті ввімкнені у всіх. Верифікаційні - платіжні посилання, повернення, виплати - відкриваються самі, щойно Stripe повідомить, що обліковий запис перевірено. Ті, що потребують схвалення - доступ по API, вебхуки, великі платежі, миттєві виплати, мультивалютність, регулярні списання - читає людина, бо кожна розширює шкоду від скомпрометованого запису. blockedReason каже, що робити, questions перелічує питання рецензента, conditions несе стелі на кшталт maxPaymentAmount. Сюди варто дивитися при будь-якому 403.',
      "Le capacità sono di tre tipi. Quelle aperte valgono per tutti. Quelle legate alla verifica - link di pagamento, rimborsi, bonifici - si sbloccano da sole quando Stripe dichiara verificato l'account. Quelle soggette ad approvazione - accesso API, webhook, importi elevati, bonifici istantanei, multivaluta, ricorrenti - le legge una persona. blockedReason dice cosa fare, questions elenca le domande, conditions porta i tetti come maxPaymentAmount.",
      'Las capacidades son de tres clases. Las abiertas rigen para todos. Las ligadas a la verificación - enlaces de pago, reembolsos, transferencias - se desbloquean solas cuando Stripe declara verificada la cuenta. Las sujetas a aprobación - acceso API, webhooks, importes altos, transferencias instantáneas, multidivisa, recurrentes - las lee una persona. blockedReason dice qué hacer, questions enumera las preguntas, conditions lleva los techos como maxPaymentAmount.',
    ),
    request: `curl https://api.wyocs.org/api/v1/features \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "object": "list",
  "data": [
    {
      "key": "refunds",
      "label": "Refunds",
      "description": "Return money to a customer, in full or in part.",
      "gate": "verification",
      "available": true,
      "blockedReason": null,
      "conditions": {},
      "request": null
    },
    {
      "key": "high_value_payments",
      "label": "High-value payments",
      "description": "Accept single payments above the standard per-payment ceiling.",
      "gate": "approval",
      "available": false,
      "blockedReason": "Request access - the platform team reviews each one.",
      "conditions": {},
      "questions": [
        "What is the largest single payment you expect, and what is it for?",
        "How do you verify the customer before invoicing them?"
      ],
      "request": null
    }
  ]
}`,
  },

  {
    id: 'list-feature-requests',
    method: 'GET',
    path: '/api/v1/feature-requests',
    group: 'capabilities',
    title: t('List capability requests', 'Списък със заявки за възможности', 'Freigabeanträge auflisten', 'Lister les demandes de capacité', 'Список заявок на возможности', 'Список заявок на можливості', 'Elenca le richieste di capacità', 'Listar solicitudes de capacidad'),
    summary: t(
      'Your requests to the platform team, with the whole conversation attached to each one.',
      'Вашите заявки към екипа на платформата, с целия разговор към всяка.',
      'Ihre Anträge an das Plattform-Team, jeweils mit dem gesamten Gesprächsverlauf.',
      "Vos demandes à l'équipe de la plateforme, avec l'échange complet attaché à chacune.",
      'Ваши заявки команде платформы, с полной перепиской по каждой.',
      'Ваші заявки команді платформи, з повним листуванням за кожною.',
      'Le tue richieste al team della piattaforma, con l’intera conversazione allegata.',
      'Sus solicitudes al equipo de la plataforma, con toda la conversación adjunta.',
    ),
    request: `curl https://api.wyocs.org/api/v1/feature-requests \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "object": "list",
  "data": [
    {
      "object": "feature_request",
      "id": "freq_5DL9...",
      "feature": "high_value_payments",
      "feature_label": "High-value payments",
      "status": "INFO_REQUESTED",
      "justification": "Our largest annual contracts invoice at around 12000 USD.",
      "intended_use": "Single invoices to established corporate customers.",
      "expected_volume": "Three or four a month",
      "created_at": "2026-07-20T09:12:44.001Z",
      "decided_by": null,
      "decided_at": null,
      "decision_note": null,
      "conditions": null,
      "messages": [
        {
          "id": "msg_...",
          "author_role": "ADMIN",
          "body": "How do you verify a new corporate customer before invoicing?",
          "created_at": "2026-07-21T11:03:00.220Z"
        }
      ]
    }
  ]
}`,
  },

  {
    id: 'create-feature-request',
    errors: ['invalid_request', 'insufficient_detail', 'not_requestable', 'already_granted', 'request_open', 'idempotency_conflict'],
    method: 'POST',
    path: '/api/v1/feature-requests',
    group: 'capabilities',
    title: t('Request a capability', 'Заявка за възможност', 'Freigabe beantragen', 'Demander une capacité', 'Запросить возможность', 'Запитати можливість', 'Richiedere una capacità', 'Solicitar una capacidad'),
    summary: t(
      'Ask the platform team to unlock something an approval gate is holding.',
      'Моли екипа на платформата да отключи нещо, задържано от одобрителна врата.',
      'Bittet das Plattform-Team, etwas freizuschalten, das eine Genehmigungsschranke hält.',
      "Demande à l'équipe de débloquer ce qu'une barrière d'approbation retient.",
      'Просит команду платформы открыть то, что удерживает шлюз одобрения.',
      'Просить команду платформи відкрити те, що утримує шлюз схвалення.',
      'Chiede al team di sbloccare ciò che un cancello di approvazione trattiene.',
      'Pide al equipo desbloquear lo que retiene una barrera de aprobación.',
    ),
    note: t(
      'This returns an identifier, not an answer. A person reads the justification and may come back with a question, at which point the request moves to INFO_REQUESTED and waits for your reply - it does not expire and it is not lost. Both text fields need at least twenty characters, which is not bureaucracy: an empty justification simply gets asked the same question by hand a day later. GET /api/v1/features lists the questions worth answering up front for each capability. Only one open request per capability at a time.',
      'Това връща идентификатор, не отговор. Човек чете обосновката и може да се върне с въпрос, при което заявката минава в INFO_REQUESTED и чака вашия отговор - не изтича и не се губи. И двете текстови полета искат поне двайсет знака, което не е бюрокрация: празна обосновка просто получава същия въпрос ръчно ден по-късно. GET /api/v1/features изброява въпросите, които си струва да се отговорят предварително за всяка възможност. Само една отворена заявка на възможност наведнъж.',
      'Das liefert eine Kennung, keine Antwort. Ein Mensch liest die Begründung und stellt womöglich eine Rückfrage; der Antrag geht dann auf INFO_REQUESTED und wartet auf Sie - er verfällt nicht. Beide Textfelder brauchen mindestens zwanzig Zeichen: eine leere Begründung führt nur dazu, dass dieselbe Frage einen Tag später von Hand gestellt wird. GET /api/v1/features nennt die Fragen vorab. Pro Freigabe nur ein offener Antrag.',
      "Cela renvoie un identifiant, pas une réponse. Une personne lit la justification et peut poser une question ; la demande passe alors en INFO_REQUESTED et attend votre réponse, sans expirer. Les deux champs texte exigent au moins vingt caractères : une justification vide provoque simplement la même question un jour plus tard. GET /api/v1/features liste les questions à l'avance. Une seule demande ouverte par capacité.",
      'Это возвращает идентификатор, а не ответ. Человек читает обоснование и может задать вопрос; заявка переходит в INFO_REQUESTED и ждёт вашего ответа, не истекая. Оба текстовых поля требуют минимум двадцати символов: пустое обоснование просто вызовет тот же вопрос вручную днём позже. GET /api/v1/features перечисляет вопросы заранее. По одной открытой заявке на возможность.',
      'Це повертає ідентифікатор, а не відповідь. Людина читає обґрунтування і може поставити питання; заявка переходить у INFO_REQUESTED і чекає вашої відповіді, не спливаючи. Обидва текстові поля вимагають щонайменше двадцяти символів. GET /api/v1/features перелічує питання заздалегідь. По одній відкритій заявці на можливість.',
      "Restituisce un identificatore, non una risposta. Una persona legge la motivazione e può porre una domanda; la richiesta passa a INFO_REQUESTED e attende la tua risposta, senza scadere. Entrambi i campi richiedono almeno venti caratteri. GET /api/v1/features elenca le domande in anticipo. Una sola richiesta aperta per capacità.",
      'Devuelve un identificador, no una respuesta. Una persona lee la justificación y puede preguntar; la solicitud pasa a INFO_REQUESTED y espera su respuesta, sin caducar. Ambos campos exigen al menos veinte caracteres. GET /api/v1/features enumera las preguntas de antemano. Una sola solicitud abierta por capacidad.',
    ),
    params: [
      {
        name: 'feature',
        type: 'string',
        required: true,
        description: t(
          'A capability key from GET /api/v1/features whose gate is "approval".',
          'Ключ на възможност от GET /api/v1/features, чиято врата е „approval".',
          'Ein Freigabeschlüssel aus GET /api/v1/features mit gate "approval".',
          'Une clé de capacité de GET /api/v1/features dont le gate vaut « approval ».',
          'Ключ возможности из GET /api/v1/features, у которой gate равен «approval».',
          'Ключ можливості з GET /api/v1/features, у якої gate дорівнює «approval».',
          'Una chiave di capacità da GET /api/v1/features con gate "approval".',
          'Una clave de capacidad de GET /api/v1/features cuyo gate sea «approval».',
        ),
      },
      {
        name: 'justification',
        type: 'string',
        required: true,
        description: t(
          'At least 20 characters. Why the business needs this.',
          'Поне 20 знака. Защо бизнесът има нужда от това.',
          'Mindestens 20 Zeichen. Warum das Geschäft dies braucht.',
          "Au moins 20 caractères. Pourquoi l'activité en a besoin.",
          'Не менее 20 символов. Зачем это бизнесу.',
          'Щонайменше 20 символів. Навіщо це бізнесу.',
          'Almeno 20 caratteri. Perché serve alla tua attività.',
          'Al menos 20 caracteres. Por qué lo necesita el negocio.',
        ),
      },
      {
        name: 'intended_use',
        type: 'string',
        required: true,
        description: t(
          'At least 20 characters. What it will actually be used for.',
          'Поне 20 знака. За какво реално ще се използва.',
          'Mindestens 20 Zeichen. Wofür es tatsächlich genutzt wird.',
          "Au moins 20 caractères. À quoi cela servira réellement.",
          'Не менее 20 символов. Для чего это будет использоваться на деле.',
          'Щонайменше 20 символів. Для чого це використовуватиметься насправді.',
          'Almeno 20 caratteri. A cosa servirà davvero.',
          'Al menos 20 caracteres. Para qué se usará realmente.',
        ),
      },
      {
        name: 'expected_volume',
        type: 'string',
        required: false,
        description: t(
          'Free text. A rough figure saves a round trip.',
          'Свободен текст. Приблизителна цифра спестява едно връщане напред-назад.',
          'Freitext. Eine grobe Zahl spart eine Rückfrage.',
          'Texte libre. Un ordre de grandeur évite un aller-retour.',
          'Свободный текст. Примерная цифра экономит один круг переписки.',
          'Вільний текст. Приблизна цифра економить одне коло листування.',
          'Testo libero. Una cifra approssimativa evita uno scambio.',
          'Texto libre. Una cifra aproximada ahorra un ida y vuelta.',
        ),
      },
    ],
    request: `curl -X POST https://api.wyocs.org/api/v1/feature-requests \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
        "feature": "high_value_payments",
        "justification": "Our largest annual contracts invoice at around 12000 USD.",
        "intended_use": "Single invoices to established corporate customers.",
        "expected_volume": "Three or four a month"
      }'`,
    response: `{
  "id": "freq_5DL9...",
  "object": "feature_request",
  "feature": "high_value_payments",
  "status": "SUBMITTED"
}`,
  },

  {
    id: 'get-feature-request',
    errors: ['not_found'],
    method: 'GET',
    path: '/api/v1/feature-requests/{id}',
    group: 'capabilities',
    title: t('Retrieve a capability request', 'Извличане на заявка', 'Freigabeantrag abrufen', 'Récupérer une demande', 'Получить заявку', 'Отримати заявку', 'Recuperare una richiesta', 'Recuperar una solicitud'),
    summary: t(
      'One request and its thread, so you can see whether a question is waiting on you.',
      'Една заявка и нейният разговор, за да видите дали въпрос чака вас.',
      'Ein Antrag samt Verlauf, damit Sie sehen, ob eine Rückfrage auf Sie wartet.',
      "Une demande et son fil, pour voir si une question vous attend.",
      'Одна заявка и её переписка, чтобы видеть, не ждёт ли вопрос вас.',
      'Одна заявка та її листування, щоб бачити, чи не чекає питання на вас.',
      'Una richiesta e il suo thread, per vedere se una domanda attende te.',
      'Una solicitud y su hilo, para ver si hay una pregunta esperándole.',
    ),
    request: `curl https://api.wyocs.org/api/v1/feature-requests/freq_5DL9... \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "object": "feature_request",
  "id": "freq_5DL9...",
  "feature": "high_value_payments",
  "status": "INFO_REQUESTED",
  "messages": [
    {
      "id": "msg_...",
      "author_role": "ADMIN",
      "body": "How do you verify a new corporate customer before invoicing?",
      "created_at": "2026-07-21T11:03:00.220Z"
    }
  ]
}`,
  },

  {
    id: 'reply-feature-request',
    errors: ['invalid_request', 'not_found', 'request_closed'],
    method: 'POST',
    path: '/api/v1/feature-requests/{id}/reply',
    group: 'capabilities',
    title: t('Reply to a capability request', 'Отговор по заявка', 'Auf einen Antrag antworten', 'Répondre à une demande', 'Ответить по заявке', 'Відповісти за заявкою', 'Rispondere a una richiesta', 'Responder a una solicitud'),
    summary: t(
      'Answer the platform team’s question. This puts the request back in the decision queue.',
      'Отговаря на въпроса на екипа. Това връща заявката в опашката за решение.',
      'Beantwortet die Rückfrage des Teams und stellt den Antrag zurück in die Entscheidungswarteschlange.',
      "Répond à la question de l'équipe et remet la demande dans la file de décision.",
      'Отвечает на вопрос команды и возвращает заявку в очередь на решение.',
      'Відповідає на питання команди і повертає заявку в чергу на рішення.',
      'Risponde alla domanda del team e rimette la richiesta in coda per la decisione.',
      'Responde a la pregunta del equipo y devuelve la solicitud a la cola de decisión.',
    ),
    note: t(
      'A request in INFO_REQUESTED is waiting on you and nothing else; replying is what moves it. Once a decision has been made the thread is closed and a reply is refused, because there would be nobody to read it - submit a new request instead.',
      'Заявка в INFO_REQUESTED чака единствено вас; отговорът е това, което я мърда. След като има решение, разговорът е затворен и отговор се отказва, защото няма кой да го прочете - пуснете нова заявка.',
      'Ein Antrag in INFO_REQUESTED wartet allein auf Sie; erst Ihre Antwort bewegt ihn. Nach der Entscheidung ist der Verlauf geschlossen und eine Antwort wird abgelehnt - stellen Sie stattdessen einen neuen Antrag.',
      "Une demande en INFO_REQUESTED n'attend que vous ; c'est la réponse qui la fait avancer. Une fois la décision prise, le fil est clos et toute réponse est refusée : déposez une nouvelle demande.",
      'Заявка в INFO_REQUESTED ждёт только вас; сдвигает её именно ответ. После решения ветка закрыта и ответ отклоняется - подайте новую заявку.',
      'Заявка в INFO_REQUESTED чекає лише вас; зрушує її саме відповідь. Після рішення гілка закрита і відповідь відхиляється - подайте нову заявку.',
      "Una richiesta in INFO_REQUESTED attende solo te. Dopo la decisione il thread è chiuso e una risposta viene rifiutata: invia una nuova richiesta.",
      'Una solicitud en INFO_REQUESTED solo le espera a usted. Tras la decisión el hilo se cierra y la respuesta se rechaza: envíe una solicitud nueva.',
    ),
    params: [
      {
        name: 'body',
        type: 'string',
        required: true,
        description: t('1 to 4000 characters.', 'От 1 до 4000 знака.', '1 bis 4000 Zeichen.', '1 à 4000 caractères.', 'От 1 до 4000 символов.', 'Від 1 до 4000 символів.', 'Da 1 a 4000 caratteri.', 'De 1 a 4000 caracteres.'),
      },
    ],
    request: `curl -X POST https://api.wyocs.org/api/v1/feature-requests/freq_5DL9.../reply \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"body": "We run a credit check and require a signed order before the first invoice."}'`,
    response: `{ "ok": true }`,
  },

  // -------------------------------------------------------------------------
  // Support tickets
  // -------------------------------------------------------------------------

  {
    id: 'list-tickets',
    method: 'GET',
    path: '/api/v1/tickets',
    group: 'support',
    title: t('List tickets', 'Списък с тикети', 'Tickets auflisten', 'Lister les tickets', 'Список тикетов', 'Список тикетів', 'Elenca i ticket', 'Listar tickets'),
    summary: t(
      'Your support threads with the platform, plus a count of what is waiting on whom.',
      'Вашите разговори с поддръжката, плюс брой на това какво чака кого.',
      'Ihre Support-Verläufe mit der Plattform, samt Zählung, worauf wer wartet.',
      "Vos fils de support avec la plateforme, et le compte de ce qui attend qui.",
      'Ваши обращения в поддержку платформы плюс подсчёт, что кого ждёт.',
      'Ваші звернення до підтримки платформи плюс підрахунок, що кого чекає.',
      'I tuoi thread di assistenza con la piattaforma, più il conteggio di cosa attende chi.',
      'Sus hilos de soporte con la plataforma, más un recuento de qué espera a quién.',
    ),
    note: t(
      'AWAITING_ADMIN means the ball is with the platform; AWAITING_MERCHANT means it is with you. RESOLVED can still be reopened by either side, CLOSED cannot - a closed thread needs a new ticket quoting the old number. Ticket numbers look like WCS-2026-000317 and are what to quote on the phone or in an email; the tkt_ id is what to use in these calls.',
      'AWAITING_ADMIN значи, че топката е у платформата; AWAITING_MERCHANT - че е у вас. RESOLVED още може да се отвори наново от която и да е страна, CLOSED - не; затворен разговор иска нов тикет, цитиращ стария номер. Номерата изглеждат като WCS-2026-000317 и това се цитира по телефона или в имейл; tkt_ id е това, което се ползва в тези извиквания.',
      'AWAITING_ADMIN heißt, der Ball liegt bei der Plattform; AWAITING_MERCHANT bei Ihnen. RESOLVED kann jede Seite wieder öffnen, CLOSED nicht - dann braucht es ein neues Ticket unter Nennung der alten Nummer. Nummern sehen aus wie WCS-2026-000317 und werden am Telefon genannt; die tkt_-id gehört in diese Aufrufe.',
      "AWAITING_ADMIN signifie que la balle est côté plateforme ; AWAITING_MERCHANT, côté vous. RESOLVED se rouvre des deux côtés, pas CLOSED : il faut alors un nouveau ticket citant l'ancien numéro. Les numéros ressemblent à WCS-2026-000317 et se citent au téléphone ; l'id tkt_ sert dans ces appels.",
      'AWAITING_ADMIN означает, что мяч у платформы; AWAITING_MERCHANT - что у вас. RESOLVED может открыть заново любая сторона, CLOSED - нет: нужен новый тикет со ссылкой на старый номер. Номера выглядят как WCS-2026-000317 и называются по телефону; id вида tkt_ используется в этих вызовах.',
      'AWAITING_ADMIN означає, що м’яч у платформи; AWAITING_MERCHANT - що у вас. RESOLVED може відкрити знову будь-яка сторона, CLOSED - ні: потрібен новий тикет із посиланням на старий номер. Номери виглядають як WCS-2026-000317; id вигляду tkt_ використовується в цих викликах.',
      "AWAITING_ADMIN significa che la palla è alla piattaforma; AWAITING_MERCHANT che è a te. RESOLVED è riapribile da entrambi, CLOSED no: serve un nuovo ticket citando il vecchio numero.",
      'AWAITING_ADMIN significa que la pelota está en la plataforma; AWAITING_MERCHANT, en usted. RESOLVED puede reabrirse por cualquiera; CLOSED no: hace falta un ticket nuevo citando el número antiguo.',
    ),
    params: [
      {
        name: 'status',
        type: 'string',
        required: false,
        description: t(
          'OPEN, AWAITING_ADMIN, AWAITING_MERCHANT, RESOLVED or CLOSED.',
          'OPEN, AWAITING_ADMIN, AWAITING_MERCHANT, RESOLVED или CLOSED.',
          'OPEN, AWAITING_ADMIN, AWAITING_MERCHANT, RESOLVED oder CLOSED.',
          'OPEN, AWAITING_ADMIN, AWAITING_MERCHANT, RESOLVED ou CLOSED.',
          'OPEN, AWAITING_ADMIN, AWAITING_MERCHANT, RESOLVED или CLOSED.',
          'OPEN, AWAITING_ADMIN, AWAITING_MERCHANT, RESOLVED або CLOSED.',
          'OPEN, AWAITING_ADMIN, AWAITING_MERCHANT, RESOLVED o CLOSED.',
          'OPEN, AWAITING_ADMIN, AWAITING_MERCHANT, RESOLVED o CLOSED.',
        ),
      },
      {
        name: 'category',
        type: 'string',
        required: false,
        description: t(
          'general, payments, payouts, disputes, technical, account or billing.',
          'general, payments, payouts, disputes, technical, account или billing.',
          'general, payments, payouts, disputes, technical, account oder billing.',
          'general, payments, payouts, disputes, technical, account ou billing.',
          'general, payments, payouts, disputes, technical, account или billing.',
          'general, payments, payouts, disputes, technical, account або billing.',
          'general, payments, payouts, disputes, technical, account o billing.',
          'general, payments, payouts, disputes, technical, account o billing.',
        ),
      },
      {
        name: 'search',
        type: 'string',
        required: false,
        description: t(
          'Matches a ticket number or part of a subject.',
          'Съвпада с номер на тикет или част от тема.',
          'Trifft auf eine Ticketnummer oder Teile des Betreffs zu.',
          "Correspond à un numéro de ticket ou une partie de l'objet.",
          'Совпадает с номером тикета или частью темы.',
          'Збігається з номером тикета або частиною теми.',
          'Corrisponde a un numero di ticket o parte dell’oggetto.',
          'Coincide con un número de ticket o parte del asunto.',
        ),
      },
    ],
    request: `curl "https://api.wyocs.org/api/v1/tickets?status=AWAITING_MERCHANT" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "object": "list",
  "data": [
    {
      "id": "tkt_6HM3...",
      "number": "WCS-2026-000317",
      "subject": "Payout arrived short",
      "category": "payouts",
      "priority": "normal",
      "status": "AWAITING_MERCHANT",
      "messages": 4,
      "created_at": "2026-07-22T13:40:02.118Z",
      "last_message_at": "2026-07-24T09:15:41.660Z"
    }
  ],
  "counts": { "open": 1, "awaiting_merchant": 1, "awaiting_admin": 0, "resolved": 5 }
}`,
  },

  {
    id: 'create-ticket',
    errors: ['invalid_request', 'unsupported_attachment', 'attachment_too_large', 'idempotency_conflict'],
    method: 'POST',
    path: '/api/v1/tickets',
    group: 'support',
    title: t('Open a ticket', 'Отваряне на тикет', 'Ticket eröffnen', 'Ouvrir un ticket', 'Открыть тикет', 'Відкрити тикет', 'Aprire un ticket', 'Abrir un ticket'),
    summary: t(
      'Start a support thread with the platform, optionally with files attached.',
      'Започва разговор с поддръжката, по избор с прикачени файлове.',
      'Startet einen Support-Verlauf mit der Plattform, optional mit Anhängen.',
      'Ouvre un fil de support avec la plateforme, éventuellement avec des pièces jointes.',
      'Начинает обращение в поддержку, при желании с вложениями.',
      'Починає звернення до підтримки, за бажанням із вкладеннями.',
      'Avvia un thread di assistenza con la piattaforma, con eventuali allegati.',
      'Inicia un hilo de soporte con la plataforma, opcionalmente con adjuntos.',
    ),
    note: t(
      'Attachments are base64 and are judged by their own leading bytes rather than by their name or declared type: a file called invoice.png that is really an HTML document is refused, because a file served back under a type nobody verified is how an upload becomes a script running on somebody else\'s session. PNG, JPEG, GIF, WebP and PDF are accepted, up to 5 MB each and five per message. The stored filename is rebuilt from the safe characters of yours with the extension the bytes actually deserve.',
      'Прикачените файлове са base64 и се преценяват по собствените си начални байтове, а не по име или обявен тип: файл, наречен invoice.png, който всъщност е HTML документ, се отказва, защото файл, върнат под непроверен тип, е точно начинът, по който качване се превръща в скрипт, работещ в чужда сесия. Приемат се PNG, JPEG, GIF, WebP и PDF, до 5 MB всеки и по пет на съобщение. Съхраненото име се възстановява от безопасните знаци на вашето с разширението, което байтовете наистина заслужават.',
      'Anhänge sind base64 und werden nach ihren Anfangsbytes beurteilt, nicht nach Namen oder deklariertem Typ: eine "invoice.png", die in Wahrheit HTML ist, wird abgelehnt - so wird aus einem Upload sonst ein Skript in fremder Sitzung. Erlaubt sind PNG, JPEG, GIF, WebP und PDF, je 5 MB, fünf pro Nachricht. Der gespeicherte Name wird aus den sicheren Zeichen neu gebildet.',
      "Les pièces jointes sont en base64 et jugées sur leurs premiers octets, pas sur le nom ni le type déclaré : un « invoice.png » qui est en réalité du HTML est refusé, car c'est ainsi qu'un envoi devient un script s'exécutant dans la session d'autrui. PNG, JPEG, GIF, WebP et PDF sont acceptés, 5 Mo chacun, cinq par message. Le nom stocké est reconstruit.",
      'Вложения передаются в base64 и оцениваются по собственным первым байтам, а не по имени или заявленному типу: файл invoice.png, который на деле HTML, отклоняется - именно так загрузка превращается в скрипт, работающий в чужой сессии. Принимаются PNG, JPEG, GIF, WebP и PDF, до 5 МБ каждый, по пять на сообщение. Сохраняемое имя пересобирается.',
      'Вкладення передаються в base64 і оцінюються за власними першими байтами, а не за іменем чи заявленим типом: файл invoice.png, який насправді HTML, відхиляється - саме так завантаження перетворюється на скрипт у чужій сесії. Приймаються PNG, JPEG, GIF, WebP і PDF, до 5 МБ кожен, по п’ять на повідомлення. Ім’я, що зберігається, перезбирається.',
      "Gli allegati sono in base64 e giudicati dai loro primi byte, non dal nome né dal tipo dichiarato. Sono accettati PNG, JPEG, GIF, WebP e PDF, fino a 5 MB ciascuno e cinque per messaggio. Il nome memorizzato viene ricostruito.",
      'Los adjuntos van en base64 y se juzgan por sus bytes iniciales, no por el nombre ni el tipo declarado. Se aceptan PNG, JPEG, GIF, WebP y PDF, hasta 5 MB cada uno y cinco por mensaje. El nombre guardado se reconstruye.',
    ),
    params: [
      {
        name: 'subject',
        type: 'string',
        required: true,
        description: t('3 to 200 characters.', 'От 3 до 200 знака.', '3 bis 200 Zeichen.', '3 à 200 caractères.', 'От 3 до 200 символов.', 'Від 3 до 200 символів.', 'Da 3 a 200 caratteri.', 'De 3 a 200 caracteres.'),
      },
      {
        name: 'body',
        type: 'string',
        required: true,
        description: t('3 to 20000 characters.', 'От 3 до 20000 знака.', '3 bis 20000 Zeichen.', '3 à 20000 caractères.', 'От 3 до 20000 символов.', 'Від 3 до 20000 символів.', 'Da 3 a 20000 caratteri.', 'De 3 a 20000 caracteres.'),
      },
      {
        name: 'category',
        type: 'string',
        required: false,
        description: t(
          'Defaults to general. Choosing the right one routes the thread faster.',
          'По подразбиране general. Правилният избор насочва разговора по-бързо.',
          'Standard general. Die richtige Wahl leitet den Verlauf schneller weiter.',
          'Par défaut general. Le bon choix aiguille le fil plus vite.',
          'По умолчанию general. Правильный выбор быстрее направляет обращение.',
          'За замовчуванням general. Правильний вибір швидше спрямовує звернення.',
          'Predefinito general. La scelta giusta instrada il thread più in fretta.',
          'Por defecto general. Elegir bien encamina el hilo más rápido.',
        ),
      },
      {
        name: 'priority',
        type: 'string',
        required: false,
        description: t('low, normal, high or urgent.', 'low, normal, high или urgent.', 'low, normal, high oder urgent.', 'low, normal, high ou urgent.', 'low, normal, high или urgent.', 'low, normal, high або urgent.', 'low, normal, high o urgent.', 'low, normal, high o urgent.'),
      },
      {
        name: 'attachments',
        type: 'object[]',
        required: false,
        description: t(
          'Up to five, each { "filename": "...", "content": "<base64>" }.',
          'До пет, всеки { "filename": "...", "content": "<base64>" }.',
          'Bis zu fünf, jeweils { "filename": "...", "content": "<base64>" }.',
          'Jusqu’à cinq, chacune { "filename": "...", "content": "<base64>" }.',
          'До пяти, каждое { "filename": "...", "content": "<base64>" }.',
          'До п’яти, кожне { "filename": "...", "content": "<base64>" }.',
          'Fino a cinque, ciascuno { "filename": "...", "content": "<base64>" }.',
          'Hasta cinco, cada uno { "filename": "...", "content": "<base64>" }.',
        ),
      },
    ],
    request: `curl -X POST https://api.wyocs.org/api/v1/tickets \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
        "subject": "Payout arrived short",
        "body": "po_1TyH... was 128400 but 126100 reached the bank.",
        "category": "payouts",
        "priority": "high"
      }'`,
    response: `{
  "id": "tkt_6HM3...",
  "number": "WCS-2026-000317",
  "status": "OPEN"
}`,
  },

  {
    id: 'get-ticket',
    errors: ['not_found'],
    method: 'GET',
    path: '/api/v1/tickets/{id}',
    group: 'support',
    title: t('Retrieve a ticket', 'Извличане на тикет', 'Ticket abrufen', 'Récupérer un ticket', 'Получить тикет', 'Отримати тикет', 'Recuperare un ticket', 'Recuperar un ticket'),
    summary: t(
      'One thread with every message and the metadata of every attachment.',
      'Един разговор с всяко съобщение и метаданните на всеки прикачен файл.',
      'Ein Verlauf mit allen Nachrichten und den Metadaten jedes Anhangs.',
      'Un fil avec tous les messages et les métadonnées de chaque pièce jointe.',
      'Один тред со всеми сообщениями и метаданными каждого вложения.',
      'Один тред з усіма повідомленнями та метаданими кожного вкладення.',
      'Un thread con tutti i messaggi e i metadati di ogni allegato.',
      'Un hilo con todos los mensajes y los metadatos de cada adjunto.',
    ),
    note: t(
      'Internal notes written between platform staff are filtered out before the response is built, not hidden by a flag you could ignore. Attachment bytes are fetched separately from GET /api/v1/tickets/attachments/{id}; what appears here is the name, type, size and SHA-256 of each, so you can tell whether a file has changed without downloading it.',
      'Вътрешните бележки между служители на платформата се филтрират, преди отговорът да бъде съставен, а не се скриват с флаг, който бихте могли да пренебрегнете. Байтовете на прикачените файлове се теглят отделно от GET /api/v1/tickets/attachments/{id}; тук се вижда името, типът, размерът и SHA-256 на всеки, така че да разберете дали файл се е променил, без да го сваляте.',
      'Interne Notizen zwischen Plattform-Mitarbeitenden werden herausgefiltert, bevor die Antwort entsteht - nicht nur per Flag versteckt. Anhangsbytes holt man getrennt über GET /api/v1/tickets/attachments/{id}; hier stehen Name, Typ, Größe und SHA-256.',
      "Les notes internes entre membres de la plateforme sont filtrées avant la construction de la réponse, pas masquées par un indicateur. Les octets des pièces jointes se récupèrent séparément via GET /api/v1/tickets/attachments/{id} ; ici figurent nom, type, taille et SHA-256.",
      'Внутренние заметки между сотрудниками платформы отфильтровываются до сборки ответа, а не прячутся флагом. Байты вложений забираются отдельно через GET /api/v1/tickets/attachments/{id}; здесь есть имя, тип, размер и SHA-256.',
      'Внутрішні нотатки між співробітниками платформи відфільтровуються до складання відповіді, а не ховаються прапорцем. Байти вкладень забираються окремо через GET /api/v1/tickets/attachments/{id}; тут є ім’я, тип, розмір і SHA-256.',
      "Le note interne fra il personale della piattaforma vengono filtrate prima di costruire la risposta. I byte degli allegati si scaricano da GET /api/v1/tickets/attachments/{id}.",
      'Las notas internas entre el personal de la plataforma se filtran antes de construir la respuesta. Los bytes de los adjuntos se descargan en GET /api/v1/tickets/attachments/{id}.',
    ),
    request: `curl https://api.wyocs.org/api/v1/tickets/tkt_6HM3... \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "id": "tkt_6HM3...",
  "number": "WCS-2026-000317",
  "subject": "Payout arrived short",
  "status": "AWAITING_MERCHANT",
  "messages": [
    {
      "id": "msg_...",
      "author_name": "Production key (API)",
      "author_side": "MERCHANT",
      "body": "po_1TyH... was 128400 but 126100 reached the bank.",
      "created_at": "2026-07-22T13:40:02.118Z",
      "attachments": [
        {
          "id": "att_...",
          "filename": "statement.pdf",
          "mime_type": "application/pdf",
          "size": 84213,
          "sha256": "9f2c...",
          "inline": false
        }
      ]
    }
  ]
}`,
  },

  {
    id: 'reply-ticket',
    errors: ['invalid_request', 'not_found', 'ticket_closed', 'unsupported_attachment', 'attachment_too_large'],
    method: 'POST',
    path: '/api/v1/tickets/{id}/reply',
    group: 'support',
    title: t('Reply to a ticket', 'Отговор в тикет', 'Auf ein Ticket antworten', 'Répondre à un ticket', 'Ответить в тикете', 'Відповісти в тикеті', 'Rispondere a un ticket', 'Responder a un ticket'),
    summary: t(
      'Add a message to a thread. A reply moves the ticket to AWAITING_ADMIN and reopens it if it had been resolved.',
      'Добавя съобщение в разговор. Отговорът мести тикета в AWAITING_ADMIN и го отваря наново, ако е бил решен.',
      'Fügt einem Verlauf eine Nachricht hinzu. Eine Antwort setzt das Ticket auf AWAITING_ADMIN und öffnet es wieder, falls es gelöst war.',
      'Ajoute un message au fil. Une réponse place le ticket en AWAITING_ADMIN et le rouvre s’il était résolu.',
      'Добавляет сообщение в тред. Ответ переводит тикет в AWAITING_ADMIN и открывает заново, если он был решён.',
      'Додає повідомлення в тред. Відповідь переводить тикет у AWAITING_ADMIN і відкриває знову, якщо його було вирішено.',
      'Aggiunge un messaggio al thread e riporta il ticket ad AWAITING_ADMIN, riaprendolo se era risolto.',
      'Añade un mensaje al hilo y devuelve el ticket a AWAITING_ADMIN, reabriéndolo si estaba resuelto.',
    ),
    params: [
      {
        name: 'body',
        type: 'string',
        required: true,
        description: t('1 to 20000 characters.', 'От 1 до 20000 знака.', '1 bis 20000 Zeichen.', '1 à 20000 caractères.', 'От 1 до 20000 символов.', 'Від 1 до 20000 символів.', 'Da 1 a 20000 caratteri.', 'De 1 a 20000 caracteres.'),
      },
      {
        name: 'attachments',
        type: 'object[]',
        required: false,
        description: t(
          'Same rules as opening a ticket.',
          'Същите правила като при отваряне на тикет.',
          'Dieselben Regeln wie beim Eröffnen.',
          "Mêmes règles qu'à l'ouverture.",
          'Те же правила, что при открытии тикета.',
          'Ті самі правила, що при відкритті тикета.',
          "Stesse regole dell'apertura.",
          'Las mismas reglas que al abrir.',
        ),
      },
    ],
    request: `curl -X POST https://api.wyocs.org/api/v1/tickets/tkt_6HM3.../reply \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"body": "The bank statement is attached; the shortfall is 2300."}'`,
    response: `{
  "id": "msg_...",
  "status": "AWAITING_ADMIN"
}`,
  },

  {
    id: 'ticket-status',
    errors: ['invalid_request', 'not_found', 'ticket_closed'],
    method: 'POST',
    path: '/api/v1/tickets/{id}/status',
    group: 'support',
    title: t('Resolve or reopen a ticket', 'Решаване или отваряне наново', 'Ticket lösen oder wieder öffnen', 'Résoudre ou rouvrir un ticket', 'Решить или открыть тикет заново', 'Вирішити або відкрити тикет знову', 'Risolvere o riaprire un ticket', 'Resolver o reabrir un ticket'),
    summary: t(
      'Mark a thread resolved when the answer worked, or reopen it when it did not.',
      'Отбелязва разговор като решен, когато отговорът е свършил работа, или го отваря наново, когато не е.',
      'Markiert einen Verlauf als gelöst, wenn die Antwort geholfen hat, oder öffnet ihn wieder.',
      "Marque un fil comme résolu si la réponse a fonctionné, ou le rouvre sinon.",
      'Помечает тред решённым, если ответ помог, или открывает заново, если нет.',
      'Позначає тред вирішеним, якщо відповідь допомогла, або відкриває знову, якщо ні.',
      'Segna il thread come risolto se la risposta ha funzionato, altrimenti lo riapre.',
      'Marca el hilo como resuelto si la respuesta funcionó, o lo reabre si no.',
    ),
    note: t(
      'RESOLVED and OPEN are the two values a merchant may set, and that is deliberate: you are the one who knows whether the answer worked. Only the platform can CLOSED a thread, because closing is what stops further replies, and a merchant closing their own ticket by mistake would have no way back into it.',
      'RESOLVED и OPEN са двете стойности, които търговец може да зададе, и това е нарочно: вие сте този, който знае дали отговорът е свършил работа. Само платформата може да сложи CLOSED, защото затварянето е това, което спира по-нататъшни отговори, а търговец, затворил тикета си по грешка, не би имал път обратно в него.',
      'RESOLVED und OPEN sind die beiden Werte, die ein Händler setzen darf - Sie wissen, ob die Antwort geholfen hat. Nur die Plattform kann CLOSED setzen, denn Schließen beendet weitere Antworten, und ein versehentlich geschlossenes Ticket wäre für den Händler nicht mehr erreichbar.',
      "RESOLVED et OPEN sont les deux valeurs qu'un marchand peut poser : c'est vous qui savez si la réponse a marché. Seule la plateforme peut passer à CLOSED, car la clôture arrête les réponses et un ticket fermé par erreur serait sans retour.",
      'RESOLVED и OPEN - два значения, которые может выставить продавец: именно вы знаете, помог ли ответ. Только платформа может поставить CLOSED, потому что закрытие прекращает ответы, а закрытый по ошибке тикет был бы уже недоступен.',
      'RESOLVED і OPEN - два значення, які може виставити продавець: саме ви знаєте, чи допомогла відповідь. Лише платформа може поставити CLOSED, бо закриття припиняє відповіді, а закритий помилково тикет був би вже недоступний.',
      "RESOLVED e OPEN sono i due valori che un esercente può impostare. Solo la piattaforma può passare a CLOSED, perché la chiusura ferma ogni risposta successiva.",
      'RESOLVED y OPEN son los dos valores que un comercio puede fijar. Solo la plataforma puede pasar a CLOSED, porque cerrar detiene toda respuesta posterior.',
    ),
    params: [
      {
        name: 'status',
        type: 'string',
        required: true,
        description: t(
          'RESOLVED or OPEN. CLOSED is refused - only the platform can close.',
          'RESOLVED или OPEN. CLOSED се отказва - само платформата може да затваря.',
          'RESOLVED oder OPEN. CLOSED wird abgelehnt - schließen kann nur die Plattform.',
          'RESOLVED ou OPEN. CLOSED est refusé : seule la plateforme peut clore.',
          'RESOLVED или OPEN. CLOSED отклоняется - закрывать может только платформа.',
          'RESOLVED або OPEN. CLOSED відхиляється - закривати може лише платформа.',
          'RESOLVED o OPEN. CLOSED è rifiutato: solo la piattaforma può chiudere.',
          'RESOLVED u OPEN. CLOSED se rechaza: solo la plataforma puede cerrar.',
        ),
      },
      {
        name: 'note',
        type: 'string',
        required: false,
        description: t(
          'Written into the thread, so the reason a ticket stopped is in the ticket.',
          'Записва се в разговора, така че причината тикетът да спре е в самия тикет.',
          'Wird in den Verlauf geschrieben, damit der Grund im Ticket selbst steht.',
          'Écrit dans le fil, pour que la raison de l’arrêt figure dans le ticket.',
          'Записывается в тред, чтобы причина остановки была в самом тикете.',
          'Записується в тред, щоб причина зупинки була в самому тикеті.',
          'Scritto nel thread, così il motivo resta nel ticket.',
          'Se escribe en el hilo, para que el motivo quede en el ticket.',
        ),
      },
    ],
    request: `curl -X POST https://api.wyocs.org/api/v1/tickets/tkt_6HM3.../status \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"status": "RESOLVED", "note": "The adjustment arrived on the next payout."}'`,
    response: `{
  "id": "tkt_6HM3...",
  "status": "RESOLVED"
}`,
  },

  {
    id: 'ticket-attachment',
    errors: ['not_found'],
    method: 'GET',
    path: '/api/v1/tickets/attachments/{id}',
    group: 'support',
    title: t('Download an attachment', 'Сваляне на прикачен файл', 'Anhang herunterladen', 'Télécharger une pièce jointe', 'Скачать вложение', 'Завантажити вкладення', 'Scaricare un allegato', 'Descargar un adjunto'),
    summary: t(
      'The bytes of one attachment, with the media type that was determined when it was uploaded.',
      'Байтовете на един прикачен файл, с медийния тип, определен при качването.',
      'Die Bytes eines Anhangs, mit dem beim Hochladen ermittelten Medientyp.',
      "Les octets d'une pièce jointe, avec le type média déterminé au téléversement.",
      'Байты одного вложения с медиатипом, определённым при загрузке.',
      'Байти одного вкладення з медіатипом, визначеним при завантаженні.',
      "I byte di un allegato, con il tipo determinato al caricamento.",
      'Los bytes de un adjunto, con el tipo determinado al subirlo.',
    ),
    note: t(
      'The response is the file itself, not JSON. Content-Type is the type decided from the file’s own leading bytes when it was uploaded and never the type the uploader claimed, X-Content-Type-Options is nosniff so the browser cannot second-guess it, and a restrictive Content-Security-Policy is set on top so that nothing in the file could run or reach the network even if it were somehow interpreted. An attachment on a platform-internal note is not reachable and answers as though it did not exist.',
      'Отговорът е самият файл, не JSON. Content-Type е типът, определен от собствените начални байтове при качването, а никога обявеният от качващия; X-Content-Type-Options е nosniff, за да не гадае браузърът, и отгоре е поставена ограничителна Content-Security-Policy, така че нищо във файла да не може да се изпълни или да достигне мрежата, дори ако бъде интерпретиран. Прикачен файл към вътрешна бележка е недостижим и отговаря, все едно не съществува.',
      'Die Antwort ist die Datei selbst, kein JSON. Content-Type stammt aus den Anfangsbytes beim Hochladen und nie aus der Angabe des Absenders, X-Content-Type-Options ist nosniff, und eine restriktive Content-Security-Policy verhindert jede Ausführung. Ein Anhang an einer internen Notiz ist nicht erreichbar und antwortet, als gäbe es ihn nicht.',
      "La réponse est le fichier lui-même, pas du JSON. Content-Type vient des premiers octets constatés au téléversement, jamais du type annoncé ; X-Content-Type-Options vaut nosniff et une Content-Security-Policy restrictive empêche toute exécution. Une pièce jointe d'une note interne est inaccessible et répond comme si elle n'existait pas.",
      'В ответе сам файл, а не JSON. Content-Type взят из первых байтов при загрузке, а не из заявленного типа; X-Content-Type-Options - nosniff, а сверху выставлена ограничительная Content-Security-Policy, чтобы ничто в файле не выполнилось и не вышло в сеть. Вложение к внутренней заметке недоступно и отвечает так, будто его нет.',
      'У відповіді сам файл, а не JSON. Content-Type узятий із перших байтів при завантаженні, а не із заявленого типу; X-Content-Type-Options - nosniff, а зверху виставлено обмежувальну Content-Security-Policy. Вкладення до внутрішньої нотатки недоступне і відповідає так, ніби його немає.',
      "La risposta è il file stesso, non JSON. Il Content-Type deriva dai primi byte al caricamento, mai dal tipo dichiarato; nosniff e una Content-Security-Policy restrittiva impediscono ogni esecuzione.",
      'La respuesta es el archivo, no JSON. El Content-Type procede de los bytes iniciales al subirlo, nunca del tipo declarado; nosniff y una Content-Security-Policy restrictiva impiden cualquier ejecución.',
    ),
    request: `curl https://api.wyocs.org/api/v1/tickets/attachments/att_... \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -o statement.pdf`,
    response: `Content-Type: application/pdf
X-Content-Type-Options: nosniff
Content-Security-Policy: default-src 'none'; sandbox; frame-ancestors 'none'
Content-Disposition: attachment; filename*=UTF-8''statement.pdf

<binary>`,
  },

  // -------------------------------------------------------------------------
  // Branding
  // -------------------------------------------------------------------------

  {
    id: 'get-logo',
    method: 'GET',
    path: '/api/v1/logo',
    group: 'branding',
    title: t('Logo status', 'Състояние на логото', 'Logo-Status', 'État du logo', 'Состояние логотипа', 'Стан логотипа', 'Stato del logo', 'Estado del logotipo'),
    summary: t(
      'Whether a logo is set, what type and size it is, and the hash of the bytes currently stored.',
      'Дали има зададено лого, какъв е типът и размерът му и хешът на съхранените в момента байтове.',
      'Ob ein Logo gesetzt ist, Typ und Größe, sowie der Hash der aktuell gespeicherten Bytes.',
      "Si un logo est défini, son type et sa taille, et le hachage des octets actuellement stockés.",
      'Установлен ли логотип, его тип и размер, и хеш хранящихся сейчас байтов.',
      'Чи встановлено логотип, його тип і розмір, і хеш байтів, що зберігаються зараз.',
      'Se un logo è impostato, tipo e dimensione, e l’hash dei byte attualmente memorizzati.',
      'Si hay logotipo, su tipo y tamaño, y el hash de los bytes guardados.',
    ),
    note: t(
      'The logo appears beside your name on the payment page, and it is entirely optional - an account that never sets one has a page that looks exactly as it did before this existed, with no placeholder and no empty box. The sha256 is there so that "is the file on the page the one I uploaded" is a question with an answer.',
      'Логото се появява до името ви на страницата за плащане и е напълно по избор - профил, който никога не зададе такова, има страница, изглеждаща точно както преди това да съществува, без запълващо изображение и без празна кутия. sha256 е там, за да има отговор на въпроса „файлът на страницата ли е този, който качих".',
      'Das Logo erscheint neben Ihrem Namen auf der Zahlungsseite und ist völlig optional - ein Konto ohne Logo hat eine Seite wie zuvor, ohne Platzhalter und ohne leeren Kasten. Der sha256 macht die Frage "ist die Datei auf der Seite die, die ich hochgeladen habe" beantwortbar.',
      "Le logo apparaît à côté de votre nom sur la page de paiement, et il est entièrement facultatif : sans logo, la page est identique à ce qu'elle était, sans espace réservé ni cadre vide. Le sha256 permet de répondre à « le fichier affiché est-il celui que j'ai envoyé ».",
      'Логотип появляется рядом с вашим именем на странице оплаты и полностью необязателен: аккаунт без логотипа имеет страницу ровно такую, какой она была раньше, без заглушки и пустой рамки. sha256 нужен, чтобы у вопроса «на странице тот файл, который я загрузил» был ответ.',
      'Логотип з’являється поруч із вашим іменем на сторінці оплати і повністю необов’язковий: обліковий запис без логотипа має сторінку рівно таку, якою вона була раніше. sha256 потрібен, щоб питання «на сторінці той файл, який я завантажив» мало відповідь.',
      "Il logo appare accanto al tuo nome sulla pagina di pagamento ed è del tutto facoltativo. Il sha256 serve a rispondere a \"il file mostrato è quello che ho caricato\".",
      'El logotipo aparece junto a su nombre en la página de pago y es totalmente opcional. El sha256 sirve para responder a «el archivo mostrado es el que subí».',
    ),
    request: `curl https://api.wyocs.org/api/v1/logo \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "present": true,
  "mime_type": "image/png",
  "size": 21488,
  "sha256": "4c1e...",
  "updated_at": "2026-07-11T16:20:03.771Z",
  "prompted": true
}`,
  },

  {
    id: 'set-logo',
    errors: ['invalid_request', 'unsupported_logo', 'logo_too_large'],
    method: 'POST',
    path: '/api/v1/logo',
    group: 'branding',
    title: t('Upload a logo', 'Качване на лого', 'Logo hochladen', 'Téléverser un logo', 'Загрузить логотип', 'Завантажити логотип', 'Caricare un logo', 'Subir un logotipo'),
    summary: t(
      'Set the image shown beside your name on the payment page. Base64, and it replaces whatever was there.',
      'Задава изображението до името ви на страницата за плащане. Base64 и замества каквото е било там.',
      'Setzt das Bild neben Ihrem Namen auf der Zahlungsseite. Base64, ersetzt das bisherige.',
      'Définit l’image affichée à côté de votre nom sur la page de paiement. En base64, elle remplace la précédente.',
      'Задаёт изображение рядом с вашим именем на странице оплаты. Base64, заменяет прежнее.',
      'Задає зображення поруч із вашим іменем на сторінці оплати. Base64, замінює попереднє.',
      'Imposta l’immagine accanto al tuo nome sulla pagina di pagamento. Base64, sostituisce la precedente.',
      'Fija la imagen junto a su nombre en la página de pago. Base64, sustituye a la anterior.',
    ),
    note: t(
      'PNG, JPEG or WebP, up to 128 KB, and the type is decided by the file’s own leading bytes rather than by what you call it. SVG is refused and will stay refused: an SVG is a document rather than a picture - it can carry script and event handlers, and it is the one image format that executes. Served from an origin that holds credentials it would run with that origin’s privileges, which is precisely the "susceptible to attacks from scripts" condition that removes SAQ A eligibility for the payment page. If your designer produced only an SVG, export a PNG. The cap is small because this image arrives with the payment itself, so the customer’s first paint already shows who they are paying rather than the header shifting under them a moment later.',
      'PNG, JPEG или WebP, до 128 KB, като типът се решава от собствените начални байтове на файла, а не от това как го наричате. SVG се отказва и ще си остане отказан: SVG е документ, а не картина - може да носи скрипт и обработчици на събития и е единственият формат за изображение, който се изпълнява. Сервиран от произход, който държи идентификационни данни, той би се изпълнил с привилегиите на този произход, а това е точно условието „податлив на атаки от скриптове", което отнема годността по SAQ A на страницата за плащане. Ако дизайнерът ви е направил само SVG, изнесете PNG. Таванът е малък, защото това изображение пристига заедно с плащането.',
      'PNG, JPEG oder WebP, bis 128 KB; der Typ ergibt sich aus den Anfangsbytes, nicht aus dem Namen. SVG wird abgelehnt und bleibt abgelehnt: ein SVG ist ein Dokument, kein Bild - es kann Skript und Event-Handler tragen und ist das einzige Bildformat, das ausgeführt wird. Von einer Herkunft mit Zugangsdaten ausgeliefert liefe es mit deren Rechten - genau die Bedingung "anfällig für Skript-Angriffe", die der Zahlungsseite die SAQ-A-Eignung nimmt. Exportieren Sie stattdessen ein PNG.',
      "PNG, JPEG ou WebP, jusqu'à 128 Ko ; le type vient des premiers octets, pas du nom. Le SVG est refusé et le restera : un SVG est un document, pas une image - il peut porter du script et s'exécute. Servi depuis une origine qui détient des identifiants, il tournerait avec les privilèges de celle-ci, soit exactement la condition « vulnérable aux attaques par script » qui fait perdre l'éligibilité SAQ A à la page de paiement. Exportez un PNG.",
      'PNG, JPEG или WebP, до 128 КБ; тип определяется первыми байтами файла, а не его именем. SVG отклоняется и останется отклонённым: SVG - документ, а не картинка, он может нести скрипт и обработчики событий и является единственным форматом изображения, который выполняется. Отданный с источника, хранящего учётные данные, он выполнился бы с его привилегиями - ровно то условие «уязвим для атак сценариями», которое лишает страницу оплаты пригодности по SAQ A. Экспортируйте PNG.',
      'PNG, JPEG або WebP, до 128 КБ; тип визначається першими байтами файлу, а не його іменем. SVG відхиляється і залишиться відхиленим: SVG - документ, а не картинка, він може нести скрипт і обробники подій і є єдиним форматом зображення, який виконується. Відданий із джерела, що зберігає облікові дані, він виконався б з його привілеями - саме та умова, яка позбавляє сторінку оплати придатності за SAQ A. Експортуйте PNG.',
      "PNG, JPEG o WebP, fino a 128 KB; il tipo deriva dai primi byte del file. L'SVG è rifiutato e resterà tale: è un documento, non un'immagine, può contenere script ed è l'unico formato che esegue. Servito da un'origine con credenziali girerebbe con i suoi privilegi, la condizione esatta che toglie l'idoneità SAQ A alla pagina di pagamento.",
      'PNG, JPEG o WebP, hasta 128 KB; el tipo lo deciden los bytes iniciales del archivo. El SVG se rechaza y seguirá rechazado: es un documento, no una imagen, puede llevar script y es el único formato de imagen que se ejecuta. Servido desde un origen con credenciales correría con sus privilegios, justo la condición que quita la elegibilidad SAQ A a la página de pago.',
    ),
    params: [
      {
        name: 'image',
        type: 'string',
        required: true,
        description: t(
          'Base64 of the file, without a data: prefix. Up to about 180000 characters, which covers the 128 KB binary cap.',
          'Base64 на файла, без префикс data:. До около 180000 знака, което покрива тавана от 128 KB.',
          'Base64 der Datei, ohne data:-Präfix. Bis etwa 180000 Zeichen, was die 128-KB-Grenze abdeckt.',
          'Base64 du fichier, sans préfixe data:. Jusqu’à environ 180000 caractères.',
          'Base64 файла, без префикса data:. До примерно 180000 символов.',
          'Base64 файлу, без префікса data:. До приблизно 180000 символів.',
          'Base64 del file, senza prefisso data:. Fino a circa 180000 caratteri.',
          'Base64 del archivo, sin prefijo data:. Hasta unos 180000 caracteres.',
        ),
      },
    ],
    request: `curl -X POST https://api.wyocs.org/api/v1/logo \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d "{\\"image\\": \\"$(base64 -i logo.png)\\"}"`,
    response: `{
  "present": true,
  "mime_type": "image/png",
  "size": 21488,
  "sha256": "4c1e...",
  "updated_at": "2026-07-29T12:04:55.019Z",
  "prompted": true
}`,
  },

  {
    id: 'logo-image',
    method: 'GET',
    path: '/api/v1/logo/image',
    group: 'branding',
    title: t('Download the logo', 'Сваляне на логото', 'Logo herunterladen', 'Télécharger le logo', 'Скачать логотип', 'Завантажити логотип', 'Scaricare il logo', 'Descargar el logotipo'),
    summary: t(
      'The stored image itself, for checking what customers actually see.',
      'Самото съхранено изображение, за проверка какво реално виждат клиентите.',
      'Das gespeicherte Bild selbst, zur Kontrolle dessen, was Kunden sehen.',
      "L'image stockée elle-même, pour vérifier ce que voient les clients.",
      'Само сохранённое изображение - чтобы проверить, что видят клиенты.',
      'Саме збережене зображення - щоб перевірити, що бачать клієнти.',
      "L'immagine memorizzata, per verificare cosa vedono i clienti.",
      'La imagen guardada, para comprobar lo que ven los clientes.',
    ),
    note: t(
      'Binary, not JSON, and served with the media type determined from the bytes at upload. On the payment page itself the image does not travel this way at all: it arrives inline as a data: URI over the API call the page already makes, so the page’s Content-Security-Policy does not have to be widened by one character to accommodate a logo.',
      'Двоичен отговор, не JSON, сервиран с медийния тип, определен от байтовете при качването. На самата страница за плащане изображението изобщо не пътува така: пристига вградено като data: URI през API извикването, което страницата и без това прави, за да не се разширява Content-Security-Policy на страницата дори с един знак заради лого.',
      'Binär, kein JSON, ausgeliefert mit dem beim Hochladen ermittelten Medientyp. Auf der Zahlungsseite selbst reist das Bild gar nicht so: es kommt als data:-URI über den ohnehin erfolgenden API-Aufruf, damit die Content-Security-Policy der Seite für ein Logo um kein Zeichen aufgeweicht werden muss.',
      "Binaire, pas du JSON, servi avec le type média déterminé au téléversement. Sur la page de paiement, l'image ne transite pas ainsi : elle arrive en data: URI via l'appel API déjà effectué, pour ne pas élargir d'un caractère la Content-Security-Policy de la page.",
      'Двоичный ответ, а не JSON, с медиатипом, определённым при загрузке. На самой странице оплаты изображение так не передаётся: оно приходит встроенным как data: URI через тот же вызов API, чтобы не расширять Content-Security-Policy страницы ни на символ ради логотипа.',
      'Двійкова відповідь, а не JSON, з медіатипом, визначеним при завантаженні. На самій сторінці оплати зображення так не передається: воно приходить вбудованим як data: URI через той самий виклик API, щоб не розширювати Content-Security-Policy сторінки ані на символ заради логотипа.',
      "Binario, non JSON, con il tipo determinato al caricamento. Sulla pagina di pagamento l'immagine non viaggia così: arriva come data: URI tramite la chiamata API già effettuata.",
      'Binario, no JSON, con el tipo determinado al subirlo. En la página de pago la imagen no viaja así: llega como data: URI en la llamada de API que la página ya hace.',
    ),
    request: `curl https://api.wyocs.org/api/v1/logo/image \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -o logo.png`,
    response: `Content-Type: image/png
X-Content-Type-Options: nosniff
Content-Security-Policy: default-src 'none'; sandbox; frame-ancestors 'none'

<binary>`,
  },

  {
    id: 'delete-logo',
    method: 'DELETE',
    path: '/api/v1/logo',
    group: 'branding',
    title: t('Remove the logo', 'Премахване на логото', 'Logo entfernen', 'Retirer le logo', 'Удалить логотип', 'Видалити логотип', 'Rimuovere il logo', 'Quitar el logotipo'),
    summary: t(
      'Take the image off the payment page. Your name stays; nothing takes the logo’s place.',
      'Маха изображението от страницата за плащане. Името ви остава; нищо не заема мястото на логото.',
      'Nimmt das Bild von der Zahlungsseite. Ihr Name bleibt; nichts tritt an die Stelle des Logos.',
      'Retire l’image de la page de paiement. Votre nom reste ; rien ne prend la place du logo.',
      'Убирает изображение со страницы оплаты. Имя остаётся; место логотипа ничем не занимается.',
      'Прибирає зображення зі сторінки оплати. Ім’я лишається; місце логотипа нічим не займається.',
      'Toglie l’immagine dalla pagina di pagamento. Il nome resta; nulla prende il posto del logo.',
      'Quita la imagen de la página de pago. Su nombre permanece; nada ocupa el lugar del logotipo.',
    ),
    request: `curl -X DELETE https://api.wyocs.org/api/v1/logo \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "present": false,
  "mime_type": null,
  "size": null,
  "sha256": null,
  "updated_at": null,
  "prompted": true
}`,
  },

  // -------------------------------------------------------------------------
  // Account, continued
  // -------------------------------------------------------------------------

  {
    id: 'summary',
    method: 'GET',
    path: '/api/v1/summary',
    group: 'account',
    title: t('Account summary', 'Обобщение на профила', 'Kontoübersicht', 'Résumé du compte', 'Сводка по аккаунту', 'Зведення по обліковому запису', 'Riepilogo account', 'Resumen de la cuenta'),
    summary: t(
      'Thirty days of volume, counts and success rate, plus the ten most recent payments - the same figures the panel dashboard shows.',
      'Трийсет дни оборот, брой и успеваемост, плюс десетте най-скорошни плащания - същите числа, които показва таблото в панела.',
      'Dreißig Tage Volumen, Anzahl und Erfolgsquote, plus die zehn jüngsten Zahlungen - dieselben Zahlen wie im Panel-Dashboard.',
      'Trente jours de volume, de comptes et de taux de réussite, plus les dix derniers paiements - les chiffres du tableau de bord.',
      'Тридцать дней объёма, количества и доли успеха, плюс десять последних платежей - те же цифры, что и на панели.',
      'Тридцять днів обсягу, кількості та частки успіху, плюс десять останніх платежів - ті самі цифри, що й на панелі.',
      'Trenta giorni di volume, conteggi e tasso di successo, più i dieci pagamenti più recenti.',
      'Treinta días de volumen, recuentos y tasa de éxito, más los diez pagos más recientes.',
    ),
    note: t(
      'Computed from the platform’s own records rather than from Stripe, so it agrees with what the payment endpoints return and costs no Stripe call. A partially refunded payment still counts as a success, because it was one: excluding it would make the rate read worse than the business actually performed. This is not a general reporting endpoint - anything more specific is a filtered GET /api/v1/payments.',
      'Изчислено от собствените записи на платформата, а не от Stripe, така че съвпада с това, което връщат адресите за плащания, и не струва извикване към Stripe. Частично рефундирано плащане пак се брои за успех, защото е било такова: изключването му би направило процента по-лош от реалното представяне. Това не е общ отчетен адрес - всичко по-конкретно е филтриран GET /api/v1/payments.',
      'Aus den eigenen Aufzeichnungen der Plattform berechnet, nicht aus Stripe - deckt sich also mit den Zahlungs-Endpunkten und kostet keinen Stripe-Aufruf. Eine teilerstattete Zahlung zählt weiterhin als Erfolg, denn sie war einer. Für alles Speziellere ein gefiltertes GET /api/v1/payments.',
      "Calculé sur les registres de la plateforme, pas sur Stripe : cohérent avec les endpoints de paiement et sans appel Stripe. Un paiement partiellement remboursé compte toujours comme réussi, car il l'a été. Pour tout besoin plus précis, un GET /api/v1/payments filtré.",
      'Считается по собственным записям платформы, а не по Stripe: согласуется с платёжными эндпоинтами и не стоит вызова к Stripe. Частично возвращённый платёж по-прежнему считается успешным, потому что он им был. Для чего-то более точного - отфильтрованный GET /api/v1/payments.',
      'Рахується за власними записами платформи, а не за Stripe: узгоджується з платіжними ендпоінтами і не коштує виклику до Stripe. Частково повернений платіж і далі вважається успішним, бо він ним був. Для чогось точнішого - відфільтрований GET /api/v1/payments.',
      "Calcolato sui registri della piattaforma, non su Stripe. Un pagamento parzialmente rimborsato conta comunque come riuscito, perché lo è stato. Per esigenze più specifiche, un GET /api/v1/payments filtrato.",
      'Se calcula con los registros de la plataforma, no con Stripe. Un pago parcialmente reembolsado sigue contando como exitoso, porque lo fue. Para algo más concreto, un GET /api/v1/payments filtrado.',
    ),
    request: `curl https://api.wyocs.org/api/v1/summary \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "object": "summary",
  "currency": "usd",
  "window_days": 30,
  "volume_30d": 1284900,
  "payments_30d": 312,
  "succeeded_30d": 297,
  "success_rate": 95.2,
  "by_state": { "SUCCEEDED": 291, "PARTIALLY_REFUNDED": 6, "FAILED": 11, "CREATED": 4 },
  "recent": []
}`,
  },

  {
    id: 'connect-refresh',
    errors: ['not_onboarded', 'processor_error', 'not_configured'],
    method: 'POST',
    path: '/api/v1/connect/refresh',
    group: 'account',
    title: t('Refresh account state', 'Опресняване на състоянието', 'Kontostatus aktualisieren', 'Rafraîchir l’état du compte', 'Обновить состояние аккаунта', 'Оновити стан облікового запису', 'Aggiornare lo stato account', 'Actualizar el estado de la cuenta'),
    summary: t(
      'Ask Stripe directly whether charges and payouts are enabled, and take the answer.',
      'Пита Stripe директно дали плащанията и изплащанията са разрешени, и приема отговора.',
      'Fragt Stripe direkt, ob Zahlungen und Auszahlungen freigegeben sind, und übernimmt die Antwort.',
      "Demande directement à Stripe si les encaissements et virements sont actifs, et retient la réponse.",
      'Спрашивает Stripe напрямую, включены ли приём платежей и выплаты, и принимает ответ.',
      'Питає Stripe напряму, чи ввімкнені прийом платежів і виплати, і приймає відповідь.',
      'Chiede direttamente a Stripe se incassi e bonifici sono abilitati e ne recepisce la risposta.',
      'Pregunta a Stripe directamente si los cobros y las transferencias están activos, y toma la respuesta.',
    ),
    note: t(
      'The account.updated webhook is the normal path and stays authoritative; this exists for when a delivery is dropped or you simply want to know now. It is a read against Stripe followed by a write of exactly what Stripe said, so it cannot invent a verification that did not happen. If verification has just completed, the capabilities that unlock on verification are granted as part of the same call and come back in unlocked. Limited to six calls a minute per key - nothing is gained by asking more often, and every call spends the platform’s Stripe rate limit on everybody’s behalf.',
      'Уебхукът account.updated е нормалният път и остава меродавен; това съществува за случаите, когато доставка е изпусната или просто искате да знаете сега. Това е четене срещу Stripe, последвано от запис на точно каквото Stripe е казал, така че не може да измисли верификация, която не се е случила. Ако верификацията току-що е приключила, възможностите, които се отключват при нея, се дават в същото извикване и се връщат в unlocked. Ограничено до шест извиквания в минута на ключ.',
      'Der account.updated-Webhook ist der Normalweg und bleibt maßgeblich; das hier ist für verlorene Zustellungen oder wenn Sie es jetzt wissen wollen. Es liest bei Stripe und schreibt genau das, was Stripe sagte - eine nicht erfolgte Verifizierung kann so nicht erfunden werden. Ist die Verifizierung eben abgeschlossen, werden die dadurch freien Funktionen im selben Aufruf erteilt und in unlocked zurückgegeben. Sechs Aufrufe pro Minute je Schlüssel.',
      "Le webhook account.updated est la voie normale et fait foi ; ceci sert quand une livraison est perdue ou que vous voulez savoir tout de suite. C'est une lecture chez Stripe suivie de l'écriture exacte de sa réponse : impossible d'inventer une vérification. Si elle vient d'aboutir, les capacités correspondantes sont accordées dans le même appel et renvoyées dans unlocked. Six appels par minute et par clé.",
      'Вебхук account.updated - нормальный путь и остаётся главным; это на случай потерянной доставки или когда нужно узнать прямо сейчас. Это чтение у Stripe и запись ровно того, что Stripe сказал, - выдумать непроизошедшую верификацию невозможно. Если верификация только что завершилась, открывающиеся при ней возможности выдаются в том же вызове и возвращаются в unlocked. Шесть вызовов в минуту на ключ.',
      'Вебхук account.updated - нормальний шлях і залишається головним; це на випадок втраченої доставки або коли треба дізнатися просто зараз. Це читання у Stripe і запис рівно того, що Stripe сказав. Якщо верифікація щойно завершилася, можливості, що відкриваються при ній, видаються в тому ж виклику і повертаються в unlocked. Шість викликів за хвилину на ключ.',
      "Il webhook account.updated è la via normale e resta autorevole; questo serve quando una consegna si perde. È una lettura su Stripe seguita dalla scrittura esatta della sua risposta. Se la verifica è appena conclusa, le capacità che si sbloccano vengono concesse nella stessa chiamata. Sei chiamate al minuto per chiave.",
      'El webhook account.updated es la vía normal y sigue siendo la autoridad; esto sirve cuando se pierde una entrega. Es una lectura en Stripe seguida de la escritura exacta de su respuesta. Si la verificación acaba de completarse, las capacidades que se desbloquean se conceden en la misma llamada. Seis llamadas por minuto y clave.',
    ),
    request: `curl -X POST https://api.wyocs.org/api/v1/connect/refresh \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "charges_enabled": true,
  "payouts_enabled": true,
  "details_submitted": true,
  "requirements_due": [],
  "disabled_reason": null,
  "unlocked": ["payment_links", "refunds", "payouts"]
}`,
  },

  {
    id: 'update-payment-methods',
    errors: ['invalid_request', 'no_changes', 'card_required', 'not_onboarded', 'processor_error', 'not_configured'],
    method: 'POST',
    path: '/api/v1/payment-methods',
    group: 'account',
    title: t('Change payment methods', 'Промяна на методите за плащане', 'Zahlungsmethoden ändern', 'Modifier les moyens de paiement', 'Изменить способы оплаты', 'Змінити способи оплати', 'Modificare i metodi di pagamento', 'Cambiar métodos de pago'),
    summary: t(
      'Turn payment methods on and off. Only the keys you send are touched.',
      'Включва и изключва методи за плащане. Засягат се само подадените ключове.',
      'Schaltet Zahlungsmethoden ein und aus. Nur die gesendeten Schlüssel werden berührt.',
      "Active et désactive des moyens de paiement. Seules les clés envoyées sont touchées.",
      'Включает и выключает способы оплаты. Затрагиваются только переданные ключи.',
      'Вмикає і вимикає способи оплати. Зачіпаються лише передані ключі.',
      'Attiva e disattiva metodi di pagamento. Solo le chiavi inviate vengono toccate.',
      'Activa y desactiva métodos de pago. Solo se tocan las claves enviadas.',
    ),
    note: t(
      'This writes a payment method configuration on your connected account and takes effect on the next payment created - links already sent keep the selection they were created with. Cards cannot be turned off. A method being switched on does not guarantee it appears at checkout either: the amount, the currency and the customer’s country all narrow what Stripe will actually show, and GET /api/v1/payment-methods reports Stripe’s own verdict per method in available and unavailableReason.',
      'Това записва конфигурация на методите за плащане във вашия свързан акаунт и влиза в сила при следващото създадено плащане - вече изпратени линкове запазват избора, с който са създадени. Картите не могат да се изключат. Включен метод също не гарантира, че ще се появи на касата: сумата, валутата и държавата на клиента стесняват какво Stripe реално ще покаже, а GET /api/v1/payment-methods съобщава присъдата на самия Stripe за всеки метод в available и unavailableReason.',
      'Das schreibt eine Zahlungsmethoden-Konfiguration auf Ihr verbundenes Konto und wirkt ab der nächsten erstellten Zahlung - bereits versandte Links behalten ihre Auswahl. Karten lassen sich nicht abschalten. Aktiv heißt nicht sichtbar: Betrag, Währung und Kundenland schränken ein; GET /api/v1/payment-methods nennt Stripes Urteil je Methode.',
      "Cela écrit une configuration de moyens de paiement sur votre compte connecté et s'applique au prochain paiement créé ; les liens déjà envoyés conservent leur sélection. Les cartes ne peuvent être désactivées. Activé ne signifie pas visible : montant, devise et pays du client restreignent ce que Stripe affiche ; GET /api/v1/payment-methods donne le verdict de Stripe par méthode.",
      'Это записывает конфигурацию способов оплаты в ваш подключённый аккаунт и действует со следующего созданного платежа - уже отправленные ссылки сохраняют свой набор. Карты отключить нельзя. Включённый способ не гарантирует появления на оплате: сумма, валюта и страна клиента сужают то, что покажет Stripe; GET /api/v1/payment-methods сообщает вердикт Stripe по каждому способу.',
      'Це записує конфігурацію способів оплати у ваш підключений акаунт і діє з наступного створеного платежу - вже надіслані посилання зберігають свій набір. Картки вимкнути не можна. Увімкнений спосіб не гарантує появи на оплаті: сума, валюта і країна клієнта звужують те, що покаже Stripe.',
      "Scrive una configurazione dei metodi di pagamento sul tuo account connesso e vale dal prossimo pagamento creato; i link già inviati mantengono la selezione. Le carte non si disattivano. Attivo non significa visibile: importo, valuta e paese del cliente restringono ciò che Stripe mostra.",
      'Escribe una configuración de métodos de pago en su cuenta conectada y rige desde el siguiente pago creado; los enlaces ya enviados mantienen su selección. Las tarjetas no se pueden desactivar. Activo no significa visible: importe, moneda y país del cliente limitan lo que Stripe muestra.',
    ),
    params: [
      {
        name: 'changes',
        type: 'object',
        required: true,
        description: t(
          'Method key to true or false, for example { "klarna": true, "ideal": false }. Keys you leave out are unchanged.',
          'Ключ на метод към true или false, например { "klarna": true, "ideal": false }. Пропуснатите ключове остават непроменени.',
          'Methodenschlüssel auf true oder false, z. B. { "klarna": true, "ideal": false }. Weggelassene bleiben unverändert.',
          'Clé de méthode à true ou false, par exemple { "klarna": true, "ideal": false }. Les clés omises restent inchangées.',
          'Ключ метода в true или false, например { "klarna": true, "ideal": false }. Пропущенные ключи не меняются.',
          'Ключ методу в true або false, наприклад { "klarna": true, "ideal": false }. Пропущені ключі не змінюються.',
          'Chiave del metodo a true o false, ad esempio { "klarna": true, "ideal": false }.',
          'Clave de método a true o false, por ejemplo { "klarna": true, "ideal": false }.',
        ),
      },
    ],
    request: `curl -X POST https://api.wyocs.org/api/v1/payment-methods \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"changes": {"klarna": true, "ideal": true, "alipay": false}}'`,
    response: `{
  "configuration_id": "pmc_..."
}`,
  },

  {
    id: 'list-api-keys',
    method: 'GET',
    path: '/api/v1/api-keys',
    group: 'account',
    title: t('List API keys', 'Списък с API ключове', 'API-Schlüssel auflisten', 'Lister les clés API', 'Список API-ключей', 'Список API-ключів', 'Elenca le chiavi API', 'Listar claves de API'),
    summary: t(
      'Your keys as metadata: name, prefix, mode, when each was created, when each was last used and whether it has been revoked.',
      'Вашите ключове като метаданни: име, префикс, режим, кога е създаден всеки, кога е използван последно и дали е отнет.',
      'Ihre Schlüssel als Metadaten: Name, Präfix, Modus, Erstellung, letzte Nutzung und Widerruf.',
      "Vos clés en métadonnées : nom, préfixe, mode, création, dernière utilisation et révocation éventuelle.",
      'Ваши ключи как метаданные: имя, префикс, режим, дата создания, дата последнего использования и признак отзыва.',
      'Ваші ключі як метадані: ім’я, префікс, режим, дата створення, дата останнього використання та ознака відкликання.',
      'Le tue chiavi come metadati: nome, prefisso, modalità, creazione, ultimo uso ed eventuale revoca.',
      'Sus claves como metadatos: nombre, prefijo, modo, creación, último uso y si está revocada.',
    ),
    note: t(
      'No key material, because there is none to return: only a SHA-256 of each key is stored, which is what makes the table useless to anyone who reads it. last_used_at is written at most once a minute, so it is a good enough signal for "is this key still in use" and not a request log. There is deliberately no endpoint here for creating a key: a key that can mint keys makes revocation meaningless, because whoever holds a stolen one would simply issue themselves a second and keep it after the first is killed. New keys are issued from the panel, by a person who signed in.',
      'Няма материал на ключа, защото няма какво да се върне: пази се само SHA-256 на всеки ключ, което прави таблицата безполезна за всеки, който я прочете. last_used_at се записва най-много веднъж в минута, така че е достатъчен сигнал за „още ли се ползва този ключ", а не журнал на заявките. Нарочно няма адрес за създаване на ключ: ключ, който може да ражда ключове, обезсмисля отнемането, защото който държи откраднат, просто би си издал втори и би го запазил след убиването на първия. Новите ключове се издават от панела, от човек, който е влязъл.',
      'Kein Schlüsselmaterial, weil es keines gibt: gespeichert wird nur ein SHA-256 je Schlüssel, was die Tabelle für Lesende wertlos macht. last_used_at wird höchstens minütlich geschrieben - ein Signal für "wird der Schlüssel noch genutzt", kein Anfrageprotokoll. Bewusst kein Endpunkt zum Erzeugen: ein Schlüssel, der Schlüssel erzeugen kann, entwertet jeden Widerruf. Neue Schlüssel entstehen im Panel.',
      "Aucun matériel de clé, car il n'y en a pas : seul un SHA-256 est conservé, ce qui rend la table sans valeur pour qui la lit. last_used_at est écrit au plus une fois par minute : un signal « cette clé sert-elle encore », pas un journal. Aucun endpoint de création ici, délibérément : une clé capable d'en créer d'autres vide la révocation de son sens. Les nouvelles clés se créent dans le panneau.",
      'Материала ключа нет, потому что его нет вовсе: хранится только SHA-256 каждого ключа, что делает таблицу бесполезной для того, кто её прочтёт. last_used_at пишется не чаще раза в минуту - это сигнал «используется ли ключ», а не журнал запросов. Эндпоинта создания здесь нет намеренно: ключ, способный создавать ключи, обесценивает отзыв. Новые ключи выпускаются в панели.',
      'Матеріалу ключа немає, бо його немає взагалі: зберігається лише SHA-256 кожного ключа. last_used_at пишеться не частіше разу на хвилину - це сигнал «чи використовується ключ», а не журнал запитів. Ендпоінта створення тут немає навмисно: ключ, здатний створювати ключі, знецінює відкликання. Нові ключі випускаються в панелі.',
      "Nessun materiale di chiave, perché non ce n'è: si conserva solo uno SHA-256 per chiave. last_used_at è scritto al più una volta al minuto. Non c'è un endpoint di creazione, deliberatamente: una chiave che può creare chiavi svuota di senso la revoca.",
      'No hay material de clave, porque no lo hay: solo se guarda un SHA-256 de cada clave. last_used_at se escribe como mucho una vez por minuto. No hay endpoint de creación, a propósito: una clave capaz de crear claves vacía de sentido la revocación.',
    ),
    request: `curl https://api.wyocs.org/api/v1/api-keys \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "object": "list",
  "data": [
    {
      "id": "ak_3BX7...",
      "name": "Production",
      "prefix": "sk_live_A1B2C3",
      "livemode": true,
      "createdAt": "2026-05-04T10:00:00.000Z",
      "lastUsedAt": "2026-07-29T12:11:40.000Z",
      "revokedAt": null
    }
  ]
}`,
  },

  {
    id: 'revoke-api-key',
    errors: ['not_found'],
    method: 'DELETE',
    path: '/api/v1/api-keys/{id}',
    group: 'account',
    title: t('Revoke an API key', 'Отнемане на API ключ', 'API-Schlüssel widerrufen', 'Révoquer une clé API', 'Отозвать API-ключ', 'Відкликати API-ключ', 'Revocare una chiave API', 'Revocar una clave de API'),
    summary: t(
      'Kill a key immediately. The next request carrying it is refused.',
      'Убива ключ незабавно. Следващата заявка с него се отказва.',
      'Setzt einen Schlüssel sofort außer Kraft. Die nächste Anfrage damit wird abgelehnt.',
      'Neutralise une clé immédiatement. La requête suivante qui la porte est refusée.',
      'Немедленно убивает ключ. Следующий запрос с ним отклоняется.',
      'Негайно вбиває ключ. Наступний запит із ним відхиляється.',
      'Disattiva subito una chiave. La richiesta successiva che la porta viene rifiutata.',
      'Anula una clave de inmediato. La siguiente petición que la lleve se rechaza.',
    ),
    note: t(
      'Revocation is the half of key management that has to be automatable. The moment your own tooling spots a key in a log file, a support ticket or a public repository, the useful thing is to kill it in that same second rather than wait for somebody to sign in - which is why this endpoint exists while its opposite deliberately does not. A key may revoke itself; the response says so with self: true, and every request after that one fails with 401. Issue the replacement from the panel first if you need continuity.',
      'Отнемането е онази половина от управлението на ключове, която трябва да е автоматизируема. В мига, в който вашият инструментариум забележи ключ в лог файл, в тикет или в публично хранилище, полезното е да го убиете в същата секунда, а не да чакате някой да влезе - затова този адрес съществува, а обратният му нарочно не. Ключ може да отнеме сам себе си; отговорът го казва със self: true, а всяка следваща заявка се проваля с 401. Ако ви трябва непрекъснатост, издайте заместника от панела първо.',
      'Widerruf ist die Hälfte der Schlüsselverwaltung, die automatisierbar sein muss. Sobald Ihre Werkzeuge einen Schlüssel in einer Logdatei oder einem öffentlichen Repository entdecken, gehört er in derselben Sekunde außer Kraft gesetzt - deshalb gibt es diesen Endpunkt, während sein Gegenstück bewusst fehlt. Ein Schlüssel darf sich selbst widerrufen; die Antwort sagt das mit self: true. Ersatz vorher im Panel erzeugen, wenn Sie Kontinuität brauchen.',
      "La révocation est la moitié de la gestion des clés qui doit être automatisable. Dès que votre outillage repère une clé dans un journal ou un dépôt public, il faut la tuer dans la seconde plutôt qu'attendre une connexion humaine - d'où cet endpoint, quand son opposé manque délibérément. Une clé peut se révoquer elle-même ; la réponse l'indique par self: true. Créez le remplacement dans le panneau d'abord si vous voulez la continuité.",
      'Отзыв - та половина управления ключами, которая обязана быть автоматизируемой. Как только ваши инструменты замечают ключ в логе или публичном репозитории, правильно убить его в ту же секунду, а не ждать, пока кто-то войдёт, - потому этот эндпоинт есть, а обратного намеренно нет. Ключ может отозвать сам себя; ответ скажет об этом через self: true. Если нужна непрерывность, сначала выпустите замену в панели.',
      'Відкликання - та половина управління ключами, яка мусить бути автоматизованою. Щойно ваші інструменти помічають ключ у лозі чи публічному репозиторії, правильно вбити його в ту саму секунду, а не чекати, поки хтось увійде. Ключ може відкликати сам себе; відповідь скаже про це через self: true. Якщо потрібна безперервність, спершу випустіть заміну в панелі.',
      "La revoca è la metà della gestione delle chiavi che deve essere automatizzabile. Appena i tuoi strumenti trovano una chiave in un log o in un repository pubblico, va disattivata nello stesso istante. Una chiave può revocare sé stessa; la risposta lo dice con self: true.",
      'La revocación es la mitad de la gestión de claves que debe poder automatizarse. En cuanto sus herramientas detecten una clave en un registro o repositorio público, hay que anularla en ese mismo segundo. Una clave puede revocarse a sí misma; la respuesta lo indica con self: true.',
    ),
    request: `curl -X DELETE https://api.wyocs.org/api/v1/api-keys/ak_3BX7... \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "revoked": true,
  "id": "ak_3BX7...",
  "self": false
}`,
  },

  {
    id: 'activity',
    errors: ['invalid_request'],
    method: 'GET',
    path: '/api/v1/activity',
    group: 'account',
    title: t('Account activity', 'Активност по профила', 'Kontoaktivität', 'Activité du compte', 'Активность аккаунта', 'Активність облікового запису', 'Attività dell’account', 'Actividad de la cuenta'),
    summary: t(
      'Everything done to this account and to its payments, newest first: who did it, when, and with what.',
      'Всичко направено по този профил и по неговите плащания, най-новото отпред: кой, кога и с какво.',
      'Alles, was mit diesem Konto und seinen Zahlungen geschah, neueste zuerst: wer, wann und womit.',
      "Tout ce qui a été fait sur ce compte et ses paiements, du plus récent : qui, quand, et avec quoi.",
      'Всё, что делалось с этим аккаунтом и его платежами, новое сверху: кто, когда и чем.',
      'Усе, що робилося з цим обліковим записом і його платежами, нове згори: хто, коли і чим.',
      "Tutto ciò che è stato fatto su questo account e sui suoi pagamenti, dal più recente: chi, quando e con cosa.",
      'Todo lo hecho en esta cuenta y sus pagos, lo más reciente primero: quién, cuándo y con qué.',
    ),
    note: t(
      'The actor tells you which credential did it: api_key:ak_... for something one of your keys did, a usr_ id for a person in the panel, stripe_webhook for a state change that came from a verified Stripe event, and reconciler for one the platform’s own reconciliation applied. It reads from the hash-chained audit log, so an entry cannot be altered afterwards without the chain failing verification. This is the most expensive read on the API - it gathers your recent payment ids before it can filter - so it carries a tighter limit of twenty calls a minute.',
      'Полето actor казва кой е направил нещото: api_key:ak_... за нещо, направено от ваш ключ, usr_ id за човек в панела, stripe_webhook за промяна на състояние, дошла от проверено Stripe събитие, и reconciler за приложена от собственото равнение на платформата. Чете се от хеш-верижния одитен журнал, така че запис не може да бъде променен после, без веригата да се провали при проверка. Това е най-скъпото четене в API - събира вашите скорошни id-та на плащания, преди да може да филтрира - затова носи по-стегнат лимит от двайсет извиквания в минута.',
      'actor nennt die handelnde Berechtigung: api_key:ak_... für einen Ihrer Schlüssel, eine usr_-id für eine Person im Panel, stripe_webhook für eine Statusänderung aus einem verifizierten Stripe-Ereignis und reconciler für den plattformeigenen Abgleich. Gelesen wird aus dem hash-verketteten Audit-Log. Es ist die teuerste Abfrage der API und daher auf zwanzig Aufrufe pro Minute begrenzt.',
      "Le champ actor dit quelle identité a agi : api_key:ak_... pour une de vos clés, un id usr_ pour une personne dans le panneau, stripe_webhook pour un changement issu d'un événement Stripe vérifié, reconciler pour le rapprochement interne. Lecture depuis le journal d'audit chaîné par hachage. C'est la lecture la plus coûteuse de l'API, d'où une limite resserrée à vingt appels par minute.",
      'Поле actor говорит, чьи это действия: api_key:ak_... для одного из ваших ключей, id вида usr_ для человека в панели, stripe_webhook для смены состояния из проверенного события Stripe и reconciler для собственной сверки платформы. Читается из хеш-цепочечного журнала аудита. Это самое дорогое чтение в API, поэтому лимит здесь строже - двадцать вызовов в минуту.',
      'Поле actor каже, чиї це дії: api_key:ak_... для одного з ваших ключів, id вигляду usr_ для людини в панелі, stripe_webhook для зміни стану з перевіреної події Stripe і reconciler для власної звірки платформи. Читається з хеш-ланцюгового журналу аудиту. Це найдорожче читання в API, тому ліміт тут суворіший - двадцять викликів на хвилину.',
      "Il campo actor dice quale credenziale ha agito: api_key:ak_... per una tua chiave, un id usr_ per una persona nel pannello, stripe_webhook per un cambio di stato da evento Stripe verificato, reconciler per la riconciliazione interna. È la lettura più costosa dell'API: venti chiamate al minuto.",
      'El campo actor dice qué credencial actuó: api_key:ak_... para una de sus claves, un id usr_ para una persona en el panel, stripe_webhook para un cambio de estado de un evento verificado de Stripe, y reconciler para la conciliación interna. Es la lectura más costosa de la API: veinte llamadas por minuto.',
    ),
    params: [
      {
        name: 'limit',
        type: 'integer',
        required: false,
        description: t('1 to 100. Defaults to 50.', 'От 1 до 100. По подразбиране 50.', '1 bis 100. Standard 50.', '1 à 100. Par défaut 50.', 'От 1 до 100. По умолчанию 50.', 'Від 1 до 100. За замовчуванням 50.', 'Da 1 a 100. Predefinito 50.', 'De 1 a 100. Por defecto 50.'),
      },
    ],
    request: `curl "https://api.wyocs.org/api/v1/activity?limit=3" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "object": "list",
  "data": [
    {
      "at": "2026-07-29T12:11:40.220Z",
      "action": "api_key.revoked",
      "actor": "api_key:ak_3BX7...",
      "subject": "ak_9CD1...",
      "data": { "prefix": "sk_live_Z9Y8X7" }
    },
    {
      "at": "2026-07-29T10:22:07.551Z",
      "action": "payment.state_changed",
      "actor": "stripe_webhook",
      "subject": "pay_...",
      "data": { "from": "PROCESSING", "to": "SUCCEEDED" }
    }
  ]
}`,
  },
];

export const ERRORS: Record<Lang, { heading: string; rows: Array<[string, string]> }> = {
  en: {
    heading: 'Errors',
    rows: [
      ['400', 'The request did not validate. The body names the field.'],
      ['401', 'Missing or invalid API key.'],
      ['403', 'The key is valid but not permitted to do this.'],
      ['404', 'No such object, or it belongs to another merchant. The two are deliberately indistinguishable.'],
      ['409', 'Idempotency key reused with a different body.'],
      ['422', 'Understood but not possible - refunding an unsettled payment, for instance.'],
      ['429', 'Rate limited. Retry after the interval in the response.'],
      ['503', 'Stripe is not reachable. Nothing was charged; retry.'],
    ],
  },
  bg: {
    heading: 'Грешки',
    rows: [
      ['400', 'Заявката не премина валидация. Тялото казва кое поле.'],
      ['401', 'Липсващ или невалиден API ключ.'],
      ['403', 'Ключът е валиден, но няма право за това действие.'],
      ['404', 'Няма такъв обект или принадлежи на друг търговец. Двете нарочно не се различават.'],
      ['409', 'Idempotency ключ, използван повторно с различно тяло.'],
      ['422', 'Разбрано, но невъзможно - например рефунд на неуредено плащане.'],
      ['429', 'Твърде много заявки. Опитайте след посочения интервал.'],
      ['503', 'Stripe не е достъпен. Нищо не е таксувано; опитайте пак.'],
    ],
  },
  de: {
    heading: 'Fehler',
    rows: [
      ['400', 'Die Anfrage war ungültig. Der Body nennt das Feld.'],
      ['401', 'Fehlender oder ungültiger API-Schlüssel.'],
      ['403', 'Schlüssel gültig, aber nicht berechtigt.'],
      ['404', 'Kein solches Objekt, oder es gehört einem anderen Händler.'],
      ['409', 'Idempotency-Key mit abweichendem Body wiederverwendet.'],
      ['422', 'Verstanden, aber nicht möglich.'],
      ['429', 'Zu viele Anfragen.'],
      ['503', 'Stripe nicht erreichbar. Nichts belastet; erneut versuchen.'],
    ],
  },
  fr: {
    heading: 'Erreurs',
    rows: [
      ['400', "La requête n'a pas été validée. Le corps indique le champ."],
      ['401', 'Clé API manquante ou invalide.'],
      ['403', "Clé valide mais non autorisée."],
      ['404', "Objet inexistant, ou appartenant à un autre marchand."],
      ['409', "Clé d'idempotence réutilisée avec un corps différent."],
      ['422', 'Compris mais impossible.'],
      ['429', 'Trop de requêtes.'],
      ['503', 'Stripe injoignable. Rien n’a été débité ; réessayez.'],
    ],
  },
  ru: {
    heading: 'Ошибки',
    rows: [
      ['400', 'Запрос не прошёл валидацию. Тело указывает поле.'],
      ['401', 'Отсутствует или неверен API-ключ.'],
      ['403', 'Ключ верен, но действие не разрешено.'],
      ['404', 'Объекта нет либо он принадлежит другому продавцу.'],
      ['409', 'Idempotency-ключ повторно использован с другим телом.'],
      ['422', 'Понято, но невозможно.'],
      ['429', 'Слишком много запросов.'],
      ['503', 'Stripe недоступен. Ничего не списано; повторите.'],
    ],
  },
  uk: {
    heading: 'Помилки',
    rows: [
      ['400', 'Запит не пройшов валідацію. Тіло вказує поле.'],
      ['401', 'Відсутній або невірний API-ключ.'],
      ['403', 'Ключ дійсний, але дія не дозволена.'],
      ['404', "Об'єкта немає або він належить іншому продавцю."],
      ['409', 'Idempotency-ключ повторно використано з іншим тілом.'],
      ['422', 'Зрозуміло, але неможливо.'],
      ['429', 'Забагато запитів.'],
      ['503', 'Stripe недоступний. Нічого не списано; повторіть.'],
    ],
  },
  it: {
    heading: 'Errori',
    rows: [
      ['400', 'La richiesta non ha superato la validazione.'],
      ['401', 'Chiave API mancante o non valida.'],
      ['403', 'Chiave valida ma non autorizzata.'],
      ['404', "Oggetto inesistente, o appartenente a un altro esercente."],
      ['409', 'Idempotency key riusata con un corpo diverso.'],
      ['422', 'Compreso ma non possibile.'],
      ['429', 'Troppe richieste.'],
      ['503', 'Stripe non raggiungibile. Nessun addebito; riprova.'],
    ],
  },
  es: {
    heading: 'Errores',
    rows: [
      ['400', 'La petición no validó. El cuerpo indica el campo.'],
      ['401', 'Clave de API ausente o inválida.'],
      ['403', 'Clave válida pero sin permiso.'],
      ['404', 'No existe el objeto, o pertenece a otro comercio.'],
      ['409', 'Clave de idempotencia reutilizada con otro cuerpo.'],
      ['422', 'Entendido pero no posible.'],
      ['429', 'Demasiadas peticiones.'],
      ['503', 'Stripe inaccesible. No se cobró nada; reintente.'],
    ],
  },
};

export const LANGUAGES: Array<{ code: Lang; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'bg', label: 'Български' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
  { code: 'ru', label: 'Русский' },
  { code: 'uk', label: 'Українська' },
];
